import { Temporal } from "@js-temporal/polyfill";
import { useFocusEffect } from "@react-navigation/native";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Animated,
  Dimensions,
  Easing,
  Image,
  Linking,
  Platform,
  Pressable,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  Activity,
  ArrowUpFromDot,
  Baby,
  Brain,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDot,
  CircleSlash2,
  Dna,
  DnaOff,
  Droplet,
  Droplets,
  Egg,
  FilePlus2,
  FileText,
  FlaskConical,
  Flower2,
  HeartHandshake,
  HeartPulse,
  History,
  Hourglass,
  Leaf,
  Minus,
  PackageCheck,
  Pill,
  Phone,
  Plus,
  Ribbon,
  RouteOff,
  ShieldAlert,
  ShieldCheck,
  Snowflake,
  Sparkles,
  Truck,
  Undo2,
  Utensils,
  Waves,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import Svg, { Circle } from "react-native-svg";
import {
  Collapsible,
  ActionRowCard as SharedActionRowCard,
  CloseButton,
  Dialog,
  DialogBodyText,
  DialogHeader,
  MetaText,
  MetricValueText,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  Tag,
  TextActionButton,
  CardSubtitleText,
  CardTitleText,
  EmptyStateSubtitleText,
  EmptyStateTitleText,
  SupportingSubtitleText,
  GlassButton,
  IconButton,
  SectionActionLink,
  SectionTitleRow,
  ClinicalDataStatus,
  CompactActionButton,
  FloatingActionDock,
  FloatingActionDockButton,
  useToast,
  ActionBanner,
  FieldMessage,
  MoonFilterChip,
  MoonSegmentedControl,
  CardSurface,
  cardSurface,
  CARD_RADIUS,
  FEATURE_CARD_RADIUS,
  PANEL_ON_DARK_PRESSED_FILL,
  LoadingState,
  DialogIcon,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import {
  EMBER,
  SCREEN_PADDING_TOP,
  gradients,
  semanticColors,
  withAlpha,
} from "@louise/moon-ui/theme/tokens";
import {
  activeEggFreezingSections,
  ConceptSectionView,
  eggLabUpdateCards,
  LabUpdateCarousel,
  TodayList,
  type MedicationLogReadState,
  type HomepageMedicationFactSheetPayload,
  type HomepageProcedureFactSheetPayload,
  type HomepageTestFactSheetPayload,
  type TodayRow,
} from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { OutcomeEstimateFunnelSection } from "@src/packages/homepage/plan/components/EggFreezingBiologyDeepCarouselCard";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { HorizontalSnapCarousel } from "@src/packages/homepage/plan/components/HorizontalSnapCarousel";
import { DismissibleSpotlightStack } from "@src/packages/homepage/plan/components/DismissibleSpotlightStack";
import { CollapsibleChevronButton } from "@src/packages/homepage/plan/components/CollapsibleChevronButton";
import { useDismissible } from "@src/packages/homepage/utils/useDismissible";
import {
  MedicationRow,
  type MedicationForm,
} from "@src/packages/homepage/plan/components/MedicationRow";
import {
  Hero,
  type HeroMenuAction,
} from "@src/packages/homepage/plan/components/hero/Hero";
import type { PlanAction } from "@src/packages/homepage/plan/plan.types";
import {
  getMedicationByBrandName,
  getProcedureByName,
} from "@src/packages/content";
import {
  useCreateSymptoms,
  useDeleteSymptom,
  useSymptoms,
} from "@src/packages/care";
import {
  useAdjustMedicationStock,
  useCreateMedicationStockLot,
  useMedicationStock,
} from "@src/packages/care/hooks/useMedicationStock";
import type { MedicationStockBalance } from "@src/packages/care/hooks/useMedicationStock";
import {
  useAppointments,
  appointmentKeys,
  type Appointment,
} from "@src/packages/care/hooks/useAppointments";
import { useApiClient } from "@src/packages/api/louise-api";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import {
  buildMarkersByDate,
  buildTodayRowsByDate,
  type ConceptDayMarker,
} from "./conceptCalendarData";
import {
  calendarCheckInColors,
  calendarMarkerColors,
  calendarViewColors,
} from "./calendarTokens";
import type { FertileWindowPrediction } from "@src/packages/care/prediction/fertileWindow.types";
import { buildFertileWindowCalendarData } from "@src/packages/care/prediction/predictionToCareCalendarMarkers";
import { useCreateTreatmentSnapshot } from "@src/packages/cycles/hooks/usePastCycleDetail";
import { hasConfirmedPregnancyProcedure } from "@src/packages/cycles/ivfFlowGuards";
import {
  useActiveCycleWithPhases,
  useCyclePhases,
  useCycles,
  useCycleTypeLabels,
  type Cycle,
  type CycleOutcome,
  type CycleType,
  type Phase,
  type PhaseType,
} from "@src/packages/cycles/hooks/useCycles";
import { resolvePostProcedureOutcomeClinicalState } from "@src/packages/cycles/presenters/postProcedureOutcomeState";
import {
  isCycleEndingRecent,
  latestClosedCycle,
  readCycleEnding,
} from "@src/packages/homepage/experience/selectHomeCycleEnding";
import { useCycleHeaderState } from "@src/packages/cycles/hooks/useCycleHeaderState";
import { presentCycleHeaderForCare } from "@src/packages/cycles/presenters/cycleHeaderCare";
import {
  useCreatePatientNote,
  useDeletePatientNote,
  usePatientNotes,
  useUpdatePatientNote,
  patientNoteKeys,
} from "@src/packages/care/hooks/usePatientNotes";
import { isPregnancyDetailsNote } from "@src/packages/homepage/pregnancy/pregnancyDetailsNote";
import {
  useCareTeam,
  type CareTeamMember as ApiCareTeamMember,
} from "@src/packages/care/hooks/useCareTeam";
import {
  useCareConnections,
  type CareConnection,
} from "@src/packages/patients/hooks/useCareConnections";
import { useCycleClinic } from "@src/packages/cycles/hooks/useCycleClinic";
import {
  useOocyteCohorts,
  useSpecimens,
  type OocyteCohort,
  type Specimen,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  CryoVaultOverviewCard,
  buildCryoSpecimenSnapshotsFromApiSpecimens,
  type CryoSpecimenSnapshot,
} from "@src/packages/care/cryovault/CryoVaultOverviewCard";
import {
  CryoSpecimenDetailModal,
  buildCryoSpecimenLabel,
  mapApiSpecimensToCryoDetails,
} from "../../../components/CryoSpecimenDetailModal";
import { getCryoCycleRole } from "@src/packages/care/cryovault/cycleCryoState";
import {
  useTestResults,
  useCreateTestResult,
  useUpdateTestResult,
  useDeleteTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import { groupCanonicalTestResults } from "@src/packages/test-results/canonical-results";
import {
  createCycleMonitoringRequestId,
  useCreateCycleMonitoringEncounter,
  useCycleMonitoringEncounters,
  useUpdateCycleMonitoringEncounter,
} from "@src/packages/care/hooks/useCycleMonitoring";
import {
  usePatient,
  type UpdatePatientInput,
} from "@src/packages/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import { startClinicalWriteAcceptance } from "@src/packages/patients/sync/clinicalWriteLatch";
import type {
  Patient,
  DiseaseType,
} from "@src/packages/patients/patients.types";
import { useUserPersona } from "@src/packages/persona/useUserPersona";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import {
  useTranslations,
  useLocale,
  type TranslationFunction,
} from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import {
  getMedicalHistoryProgressSummary,
  getVisibleMedicalHistoryGroups,
} from "@src/packages/medical-history/sectionRegistry";
import ScreenHeader from "../../../components/ScreenHeader";
import StatusBadge, {
  type StatusBadgeTone,
} from "../../../components/StatusBadge";
import RecordsSection, {
  RECORDS_CATEGORY_TO_DOC_CATEGORY,
  type RecordsCategory,
} from "../sections/RecordsSection";
import {
  useDocuments,
  type Document,
} from "@src/packages/documents/hooks/useDocuments";
import { DocumentRow } from "../../documents/DocumentRow";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "@src/packages/homepage/plan/components/OutcomeDotFunnelRows";
import { DOCUMENT_SLOTS } from "../../DocumentsScreen";
import CycleClinicTeamCard, {
  type CycleTeamMember,
} from "../sections/CycleClinicTeamCard";
import MedicationFactsheetSheet from "../../../components/MedicationFactsheetSheet";
import ProcedureDetailSheet from "../../../components/ProcedureDetailSheet";
import TestDetailSheet from "../../../components/TestDetailSheet";
import { useDocumentUploadController } from "../../../DocumentUploadController";
import EndCycleModal from "../../YouScreen/modals/EndCycleModal";
import type {
  CareCalendarRouteParams,
  CareStackParamList,
  MainTabParamList,
} from "../../../types";
import { AddAppointmentModal } from "../modals/AddAppointmentModal";
import { AppointmentDetailSheet } from "../modals/AppointmentDetailSheet";
import CycleCareTeamModal from "../modals/CycleCareTeamModal";
import type { AppointmentDetailItem } from "../schedule.types";
import {
  calendarKeys,
  usePeriodLogs,
} from "@src/packages/care/useCalendarData";
import {
  BLEEDING_SYMPTOM_LABELS,
  periodDaysFromBleedingSymptoms,
} from "@src/packages/care/prediction/menstrualBleeding";
import { createBatchRetryLedger } from "@src/packages/care/batch-create";
import {
  AddMedicationModal,
  type EditMedicationData,
  type AddMedicationInventoryEntry,
} from "../modals/AddMedicationModal";
import { AddSpecimenModal } from "../modals/AddSpecimenModal";
import { type CycleType as StartTreatmentCycleType } from "../modals/MoonStartTreatmentModal";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useTabBarOverride } from "../../../TabBarOverrideContext";
import { useCycleNavigation } from "../../../hooks/useCycleNavigation";
import { type NavigationProp } from "@react-navigation/native";
import type { RootStackParamList } from "../../../../App";
import { useStartTreatmentController } from "../../../StartTreatmentController";
import {
  getDevClinicalDataState,
  isDevClinicalDataLoading,
} from "@src/packages/care/dev-clinical-data-state";
import {
  MonitoringEntryModal,
  type MonitoringEntryDraft,
  type MonitoringUploadRequest,
} from "./MonitoringEntryModal";
import {
  buildMonitoringDraftFromSources,
  buildPreviousMonitoringFromSources,
  findMonitoringEncounterForDate,
  persistMonitoringDraft,
  persistMonitoringEncounter,
  type MonitoringWriteReceipt,
} from "../sections/stimMonitoringEntry";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type CareReviewConceptVariant = "egg-freezing-active";

type Props = {
  variant: CareReviewConceptVariant;
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  initialAppointmentId?: string;
  initialFocus?: "appointment" | "medication";
  initialCalendarState?: CareCalendarRouteParams;
  onCalendarStateChange?: (state: CareCalendarRouteParams) => void;
  showFixtureData?: boolean;
};

function appointmentDateForEdit(
  value: string | null | undefined,
  fallbackDate: string,
): string {
  const date = value?.split("T")[0];
  return date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : fallbackDate;
}

function optionalAppointmentDateForEdit(
  value: string | null | undefined,
): string | undefined {
  const date = value?.split("T")[0];
  return date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined;
}

function appointmentTimeForEdit(
  value: string | null | undefined,
): string | undefined {
  if (!value) return undefined;

  const isoTime = /T(\d{2}):(\d{2})/.exec(value);
  if (isoTime) return `${isoTime[1]}:${isoTime[2]}`;

  const wallTime = /^(\d{1,2}):(\d{2})(?:\s*([AP]M))?/i.exec(value.trim());
  if (!wallTime) return undefined;

  let hours = Number(wallTime[1]);
  const minutes = wallTime[2];
  const meridiem = wallTime[3]?.toUpperCase();
  if (meridiem === "AM" && hours === 12) hours = 0;
  if (meridiem === "PM" && hours < 12) hours += 12;
  if (hours > 23) return undefined;

  return `${String(hours).padStart(2, "0")}:${minutes}`;
}

function buildAppointmentDetailItem({
  row,
  appointment,
  fallbackDate,
}: {
  row: TodayRow;
  appointment?: Appointment;
  fallbackDate: string;
}): AppointmentDetailItem {
  const procedure = getProcedureByName(row.procedureName ?? row.title);
  const location = appointment?.meetingRoom ?? null;

  return {
    type: "appointment",
    id: row.id,
    appointmentId: appointment?.id ?? row.appointmentId,
    title: appointment?.title ?? row.title,
    subtitle: location ?? row.detail,
    date: appointmentDateForEdit(appointment?.startDate, fallbackDate),
    time:
      appointmentTimeForEdit(
        appointment?.startTimeLocal ?? appointment?.startTime,
      ) ?? appointmentTimeForEdit(row.time),
    endDate: optionalAppointmentDateForEdit(appointment?.endDate),
    endTime: appointmentTimeForEdit(
      appointment?.endTimeLocal ?? appointment?.endTime,
    ),
    timeZone: appointment?.timeZone,
    icon: CalendarClock,
    factsheetId: procedure?.id,
    consultationType: appointment?.consultationType ?? null,
    location,
    isOnline: appointment?.isOnline ?? null,
    meetingLink: appointment?.meetingLink ?? null,
    notes: appointment?.description ?? row.notes ?? null,
    ownership: appointment?.ownership,
    ownerId: appointment?.ownerId,
    providerId: appointment?.providerId,
    source: appointment?.source,
  };
}

type MedicalHistoryFlowRoute = Extract<
  keyof CareStackParamList,
  `${string}Flow`
>;

type MedicalHistoryProgressItem = {
  sectionKey: string;
  title: string;
  percent: number;
  route: MedicalHistoryFlowRoute;
  icon: LucideIcon;
};

type MedicalHistoryProgressCategory = {
  key: string;
  title: string;
  icon: LucideIcon;
  items: MedicalHistoryProgressItem[];
};

type CalendarDay = {
  label: string;
  date: string;
  isoDate?: string;
  isoPrefix?: string;
  monthName?: string;
  state: "past" | "today" | "upcoming";
  markers: ("med" | "visit" | "period" | "lab" | "fertile")[];
};

type CalendarWeekPage = {
  label: string;
  monthName: string;
  isoPrefix: string;
  defaultDate: string;
  days: CalendarDay[];
};

type CalendarMonthPage = {
  label: string;
  monthName: string;
  isoPrefix: string;
  defaultDate: string;
  cells: (CalendarDay | null)[];
};

type CalendarMode = "view" | "check-in";
type CalendarDataState = "loading" | "unavailable" | "stale" | null;
type SelectedCheckInDataState = "loading" | "unavailable" | null;
type MonitoringDataState = "loading" | "unavailable" | null;
type ScheduleDataState = "loading" | "unavailable" | "stale" | null;

type CheckInBleedingLevel =
  "none" | "spotting" | "light" | "medium" | "heavy" | "clots";

type DailyCheckInDraft = {
  bleeding: CheckInBleedingLevel[];
  symptoms: string[];
  notes: string;
};

type DateLogTab = "symptoms" | "monitoring" | "schedule";

export type CalendarSymptomOverlayState = {
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
};

function getDateLogTabs(t: ReturnType<typeof useTranslations>): {
  id: DateLogTab;
  label: string;
  icon: LucideIcon;
}[] {
  return [
    {
      id: "symptoms",
      label: t("care.overview.calendar.log_tabs.symptoms"),
      icon: Activity,
    },
    {
      id: "monitoring",
      label: t("care.overview.calendar.log_tabs.monitoring"),
      icon: FlaskConical,
    },
    {
      id: "schedule",
      label: t("care.overview.calendar.log_tabs.schedule"),
      icon: CalendarClock,
    },
  ];
}

type CheckInOption = {
  label: string;
  labelKey: string;
  emoji: string;
};

type CheckInCategory = {
  id: string;
  label: string;
  icon: LucideIcon;
  options: CheckInOption[];
};

type PastCycleSnapshot = {
  id: string;
  title: string;
  subtitle: string;
  statusBadge?: string;
  statusTone?: StatusBadgeTone;
  organicIcon: OrganicMiniIconKind;
  metrics: { label: string; value: string }[];
};

export type PharmacyFulfillmentTone =
  "delivery" | "pickup" | "attention" | "done";

export type PharmacyFulfillmentStep = {
  label: string;
  dateLabel?: string;
  icon: LucideIcon;
};

export type PharmacyShipmentMedication = {
  name: string;
  dosage: string;
  route: string;
  form: MedicationForm;
};

type PharmacyInventoryMedication = PharmacyShipmentMedication & {
  id: string;
  quantity: number;
  unitLabel: string;
  lotId?: string;
  catalogItemId?: string | null;
  unitSystem?: string;
  unitCode?: string;
};

type AddMedicationModalMode = "schedule" | "inventory";

const PATIENT_INVENTORY_UNIT_SYSTEM = "urn:louise:medication-units";

function inventoryUnitCode(unitLabel: string) {
  return unitLabel
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
}

function inventoryFormFromUnit(unitLabel: string): MedicationForm {
  const unit = unitLabel.toLowerCase();
  if (unit.includes("pen")) return "pen";
  if (unit.includes("vial")) return "vial";
  if (
    unit.includes("pill") ||
    unit.includes("tablet") ||
    unit.includes("capsule")
  ) {
    return "pill";
  }
  if (unit.includes("suppository")) return "suppository";
  return "supplies";
}

function mapMedicationStockBalance(
  balance: MedicationStockBalance,
): PharmacyInventoryMedication {
  const { lot } = balance;
  return {
    id: lot.id,
    lotId: lot.id,
    catalogItemId: lot.medicationId,
    name: lot.rawProductName,
    dosage: lot.containerDescription ?? "",
    route: "",
    form: inventoryFormFromUnit(lot.quantityUnit),
    quantity: balance.quantity,
    unitLabel: lot.quantityUnit,
    unitSystem: lot.quantityUnitSystem,
    unitCode: lot.quantityUnitCode,
  };
}

export type PharmacyFulfillmentCardData = {
  id: string;
  kind?: PharmacyCardKind;
  timeSensitive?: boolean;
  title: string;
  pharmacy: string;
  statusLabel: string;
  timing: string;
  estimatedTime: string;
  trackingNumber?: string;
  trackingUrl?: string;
  pharmacyPhone: string;
  tone: PharmacyFulfillmentTone;
  currentStep: number;
  confirmationLabel?: string;
  steps: PharmacyFulfillmentStep[];
  medications: PharmacyShipmentMedication[];
  detail: string;
};

type PharmacyCardKind = "fulfillment" | "refill-action" | "pickup-action";

type LabReportSnapshot = {
  id: string;
  title: string;
  subtitle: string;
  status: string;
  organicIcon: OrganicMiniIconKind;
  enabled?: boolean;
};

type ConditionDiagnosisSnapshot = {
  id: string;
  title: string;
  status: string;
  code?: string;
  source: string;
  summary: string;
  careImpact: string;
  fertilityImpact: string;
  insuranceImpact: string;
  records: string[];
  watchItems: string[];
};

const silkBrown =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;
const CONDITION_DETAIL_ILLUSTRATION =
  require("@src/apps/moon/assets/images/stethoscope-copper.png") as number;
const careHeroGlowGradient =
  require("@src/apps/moon/assets/images/care-hero-glow-gradient.png") as number;
const pillCapsuleImage =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("@src/apps/moon/assets/images/injection-pen.png") as number;
const parcelBoxIcon =
  require("@src/apps/moon/assets/images/parcel-box-icon.png") as number;

const TEXT = EMBER.colors.text;
const TEXT_MID = EMBER.colors.textMid;
const TEXT_LIGHT = EMBER.colors.textLight;
const ACCENT = semanticColors.accent.default;
const CARE_SECTION_TOP_MARGIN = 28;
const CALENDAR_MARKER_DOT_SIZE = 6;
/**
 * The fertile window is drawn as a leaf rather than a dot, and a leaf needs
 * more room than a disc to read as a leaf at all. Sized so it still sits on
 * the same marker row without pushing the other markers around.
 */
const CALENDAR_MARKER_LEAF_SIZE = 11;

const emptyDailyCheckIn: DailyCheckInDraft = {
  bleeding: ["none"],
  symptoms: [],
  notes: "",
};

const bleedingOptions: (CheckInOption & { value: CheckInBleedingLevel })[] = [
  {
    value: "none",
    label: "None",
    labelKey: "care.overview.symptoms.bleeding.none",
    emoji: "\u2713",
  },
  {
    value: "spotting",
    label: BLEEDING_SYMPTOM_LABELS.spotting,
    labelKey: "care.overview.symptoms.bleeding.spotting",
    emoji: "\u{1FA78}",
  },
  {
    value: "light",
    label: BLEEDING_SYMPTOM_LABELS.light,
    labelKey: "care.overview.symptoms.bleeding.light_bleeding",
    emoji: "\u{1FA78}",
  },
  {
    value: "medium",
    label: BLEEDING_SYMPTOM_LABELS.medium,
    labelKey: "care.overview.symptoms.bleeding.medium_bleeding",
    emoji: "\u{1FA78}",
  },
  {
    value: "heavy",
    label: BLEEDING_SYMPTOM_LABELS.heavy,
    labelKey: "care.overview.symptoms.bleeding.heavy_bleeding",
    emoji: "\u{1FA78}",
  },
  {
    value: "clots",
    label: "Clots",
    labelKey: "care.overview.symptoms.bleeding.clots",
    emoji: "\u{1F7E4}",
  },
];

// NOTE: option labels are persisted as identifiers in the API (sent as
// symptoms: string[] to useCreateSymptoms). They cannot be safely translated
// from the FE without breaking round-tripping. Category labels are display-
// only and ARE translated via getCheckInSymptomCategories(t).
const checkInSymptomCategories: CheckInCategory[] = [
  {
    id: "worth-flagging",
    label: "Worth flagging",
    icon: Activity,
    options: [
      {
        label: "Injection site sore",
        labelKey: "care.overview.symptoms.item.injection_site_sore",
        emoji: "\u{1FA79}",
      },
      {
        label: "Headache",
        labelKey: "care.overview.symptoms.item.headache",
        emoji: "\u{1F915}",
      },
      {
        label: "Cramping",
        labelKey: "care.overview.symptoms.item.cramping",
        emoji: "\u{1F915}",
      },
      {
        label: "Dizziness",
        labelKey: "care.overview.symptoms.item.dizziness",
        emoji: "\u{1F4AB}",
      },
      {
        label: "Palpitations",
        labelKey: "care.overview.symptoms.item.palpitations",
        emoji: "\u{1F493}",
      },
      {
        label: "Shortness of breath",
        labelKey: "care.overview.symptoms.item.shortness_of_breath",
        emoji: "\u{1F62E}\u200D\u{1F4A8}",
      },
      {
        label: "Chest tightness",
        labelKey: "care.overview.symptoms.item.chest_tightness",
        emoji: "\u{1FAC0}",
      },
      {
        label: "Fever",
        labelKey: "care.overview.symptoms.item.fever",
        emoji: "\u{1F912}",
      },
      {
        label: "Chills",
        labelKey: "care.overview.symptoms.item.chills",
        emoji: "\u{1F976}",
      },
      {
        label: "Vomiting",
        labelKey: "care.overview.symptoms.item.vomiting",
        emoji: "\u{1F92E}",
      },
    ],
  },
  {
    id: "digestion",
    label: "Digestion",
    icon: Utensils,
    options: [
      {
        label: "Bloated",
        labelKey: "care.overview.symptoms.item.bloated",
        emoji: "\u{1F4A8}",
      },
      {
        label: "Nauseous",
        labelKey: "care.overview.symptoms.item.nauseous",
        emoji: "\u{1F922}",
      },
      {
        label: "Constipation",
        labelKey: "care.overview.symptoms.item.constipation",
        emoji: "\u{1F6BD}",
      },
      {
        label: "Diarrhea",
        labelKey: "care.overview.symptoms.item.diarrhea",
        emoji: "\u{1F6BD}",
      },
      {
        label: "Cravings",
        labelKey: "care.overview.symptoms.item.cravings",
        emoji: "\u{1F36B}",
      },
      {
        label: "Indigestion",
        labelKey: "care.overview.symptoms.item.indigestion",
        emoji: "\u{1F616}",
      },
      {
        label: "Appetite changes",
        labelKey: "care.overview.symptoms.item.appetite_changes",
        emoji: "\u{1F37D}\uFE0F",
      },
    ],
  },
  {
    id: "sexual",
    label: "Sexual & Reproductive",
    icon: HeartHandshake,
    options: [
      {
        label: "High libido",
        labelKey: "care.overview.symptoms.item.high_libido",
        emoji: "\u{1F525}",
      },
      {
        label: "Low libido",
        labelKey: "care.overview.symptoms.item.low_libido",
        emoji: "\u{1F636}",
      },
      {
        label: "Ovulation pain",
        labelKey: "care.overview.symptoms.item.ovulation_pain",
        emoji: "\u{1F915}",
      },
      {
        label: "Breast tenderness",
        labelKey: "care.overview.symptoms.item.breast_tenderness",
        emoji: "\u{1F610}",
      },
    ],
  },
  {
    id: "urogenital",
    label: "Urogenital",
    icon: Droplets,
    options: [
      {
        label: "Frequent urination",
        labelKey: "care.overview.symptoms.item.frequent_urination",
        emoji: "\u{1F6BB}",
      },
      {
        label: "Burning",
        labelKey: "care.overview.symptoms.item.burning",
        emoji: "\u{1F525}",
      },
      {
        label: "Incontinence",
        labelKey: "care.overview.symptoms.item.incontinence",
        emoji: "\u{1F4A7}",
      },
    ],
  },
  {
    id: "psychological",
    label: "Psychological",
    icon: Brain,
    options: [
      {
        label: "Moody",
        labelKey: "care.overview.symptoms.item.moody",
        emoji: "\u{1F614}",
      },
      {
        label: "Anxious",
        labelKey: "care.overview.symptoms.item.anxious",
        emoji: "\u{1F630}",
      },
      {
        label: "Irritable",
        labelKey: "care.overview.symptoms.item.irritable",
        emoji: "\u{1F624}",
      },
      {
        label: "Tired",
        labelKey: "care.overview.symptoms.item.tired",
        emoji: "\u{1F634}",
      },
      {
        label: "Insomnia",
        labelKey: "care.overview.symptoms.item.insomnia",
        emoji: "\u{1F319}",
      },
      {
        label: "Good energy",
        labelKey: "care.overview.symptoms.item.good_energy",
        emoji: "\u26A1",
      },
      {
        label: "Feeling okay",
        labelKey: "care.overview.symptoms.item.feeling_okay",
        emoji: "\u{1F44D}",
      },
    ],
  },
  {
    id: "body-skin",
    label: "Body & skin",
    icon: Activity,
    options: [
      {
        label: "Fatigue",
        labelKey: "care.overview.symptoms.item.fatigue",
        emoji: "\u{1F634}",
      },
      {
        label: "Body aches",
        labelKey: "care.overview.symptoms.item.body_aches",
        emoji: "\u{1F9CD}",
      },
      {
        label: "Hot flashes",
        labelKey: "care.overview.symptoms.item.hot_flashes",
        emoji: "\u{1F525}",
      },
      {
        label: "Acne",
        labelKey: "care.overview.symptoms.item.acne",
        emoji: "\u{1FA9E}",
      },
      {
        label: "Dry skin",
        labelKey: "care.overview.symptoms.item.dry_skin",
        emoji: "\u{1F3DC}\uFE0F",
      },
      {
        label: "Hair loss",
        labelKey: "care.overview.symptoms.item.hair_loss",
        emoji: "\u{1F487}",
      },
    ],
  },
];

const SYMPTOM_CATEGORY_LABEL_KEYS: Record<string, string> = {
  "worth-flagging": "care.overview.symptoms.category.worth_flagging",
  digestion: "care.overview.symptoms.category.digestion",
  sexual: "care.overview.symptoms.category.sexual_reproductive",
  urogenital: "care.overview.symptoms.category.urogenital",
  psychological: "care.overview.symptoms.category.psychological",
  "body-skin": "care.overview.symptoms.category.body_skin",
};

function getCheckInSymptomCategories(
  t: ReturnType<typeof useTranslations>,
): CheckInCategory[] {
  return checkInSymptomCategories.map((category) => ({
    ...category,
    label: t(SYMPTOM_CATEGORY_LABEL_KEYS[category.id] ?? category.label),
  }));
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const WEEKDAY_LABEL_KEY: Record<string, string> = {
  Mon: "care.weekday_short.mon",
  Tue: "care.weekday_short.tue",
  Wed: "care.weekday_short.wed",
  Thu: "care.weekday_short.thu",
  Fri: "care.weekday_short.fri",
  Sat: "care.weekday_short.sat",
  Sun: "care.weekday_short.sun",
};

const WEEKDAY_LONG_KEY: Record<string, string> = {
  Mon: "care.weekday_long.mon",
  Tue: "care.weekday_long.tue",
  Wed: "care.weekday_long.wed",
  Thu: "care.weekday_long.thu",
  Fri: "care.weekday_long.fri",
  Sat: "care.weekday_long.sat",
  Sun: "care.weekday_long.sun",
};

const MONTH_NAME_KEY: Record<string, string> = {
  January: "care.calendar_full_month.january",
  February: "care.calendar_full_month.february",
  March: "care.calendar_full_month.march",
  April: "care.calendar_full_month.april",
  May: "care.calendar_full_month.may",
  June: "care.calendar_full_month.june",
  July: "care.calendar_full_month.july",
  August: "care.calendar_full_month.august",
  September: "care.calendar_full_month.september",
  October: "care.calendar_full_month.october",
  November: "care.calendar_full_month.november",
  December: "care.calendar_full_month.december",
};

function translateWeekday(t: TranslationFunction, label: string): string {
  const key = WEEKDAY_LABEL_KEY[label];
  return key ? t(key) : label;
}

// Full weekday name for accessibility labels — screen readers should hear
// "Friday July 10", not the abbreviated "FRI" used in the compact visual grid.
function translateWeekdayLong(t: TranslationFunction, label: string): string {
  const key = WEEKDAY_LONG_KEY[label];
  return key ? t(key) : label;
}

function translateMonthName(t: TranslationFunction, name: string): string {
  const key = MONTH_NAME_KEY[name];
  return key ? t(key) : name;
}

function translateCalendarLabel(t: TranslationFunction, label: string): string {
  return Object.entries(MONTH_NAME_KEY).reduce(
    (acc, [en, key]) => acc.replace(en, t(key)),
    label,
  );
}

function getMonthName(date: Temporal.PlainDate) {
  return MONTH_NAMES[date.month - 1] ?? "May";
}

function getIsoMonthPrefix(date: Temporal.PlainDate) {
  return `${date.year}-${String(date.month).padStart(2, "0")}`;
}

function getCalendarDateKey(isoMonthPrefix: string, date: string) {
  return `${isoMonthPrefix}-${date.padStart(2, "0")}`;
}

function isCalendarRouteDate(value: string | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  try {
    Temporal.PlainDate.from(value);
    return true;
  } catch {
    return false;
  }
}

function getCalendarDayIsoDate(day: CalendarDay, fallbackIsoPrefix: string) {
  return (
    day.isoDate ??
    getCalendarDateKey(day.isoPrefix ?? fallbackIsoPrefix, day.date)
  );
}

function getCalendarIsoDate(isoMonthPrefix: string, date: string) {
  return getCalendarDateKey(isoMonthPrefix, date);
}

function getCalendarDayAccessibilityLabel(
  t: TranslationFunction,
  day: CalendarDay,
  fallbackMonthName: string,
  fallbackIsoPrefix: string,
) {
  const monthName = day.monthName ?? fallbackMonthName;
  const isoDate =
    day.isoDate ??
    getCalendarIsoDate(day.isoPrefix ?? fallbackIsoPrefix, day.date);
  const year = isoDate.slice(0, 4);

  return `${translateWeekdayLong(t, day.label)} ${translateMonthName(t, monthName)} ${day.date}${year ? ` ${year}` : ""}`;
}

function getCalendarDayState(
  date: Temporal.PlainDate,
  today: Temporal.PlainDate,
): CalendarDay["state"] {
  const comparison = Temporal.PlainDate.compare(date, today);
  if (comparison < 0) return "past";
  if (comparison > 0) return "upcoming";
  return "today";
}

function getCalendarDay(date: Temporal.PlainDate, today: Temporal.PlainDate) {
  const monthName = getMonthName(date);
  const isoPrefix = getIsoMonthPrefix(date);
  return {
    label: WEEKDAY_LABELS[date.dayOfWeek - 1] ?? "",
    date: String(date.day),
    isoDate: date.toString(),
    isoPrefix,
    monthName,
    state: getCalendarDayState(date, today),
    markers: [],
  } satisfies CalendarDay;
}

function startOfWeek(date: Temporal.PlainDate) {
  return date.subtract({ days: date.dayOfWeek - 1 });
}

// The year used to be noise: week paging was capped at two weeks back, so
// "March" could only ever mean this March. The calendar now reaches a whole
// history, where a bare month name is genuinely ambiguous.
function getCalendarWeekLabel(weekStart: Temporal.PlainDate) {
  const weekEnd = weekStart.add({ days: 6 });
  if (weekStart.month === weekEnd.month && weekStart.year === weekEnd.year) {
    return `${getMonthName(weekStart)} ${weekStart.year}`;
  }
  if (weekStart.year === weekEnd.year) {
    return `${getMonthName(weekStart)} - ${getMonthName(weekEnd)} ${weekEnd.year}`;
  }
  return `${getMonthName(weekStart)} ${weekStart.year} - ${getMonthName(weekEnd)} ${weekEnd.year}`;
}

// The first week that actually sits inside a month. Anchoring on the week
// containing the 1st can land on a week that is mostly the month before —
// jumping to August and being shown five days of July.
function firstWholeWeekOfMonth(monthStart: Temporal.PlainDate) {
  const containingWeek = startOfWeek(monthStart);
  return containingWeek.equals(monthStart)
    ? containingWeek
    : containingWeek.add({ days: 7 });
}

/**
 * The calendar used to hold a fixed array of pages — twelve months, `-2`
 * through `+9` around today. That array was two things at once: the floor
 * the previous arrow went dead against two months back, and the reason
 * every page the screen might ever show had to be built before it could
 * show one. A page is now built from the anchor the patient is actually
 * looking at, so any month of her life is reachable and only the visible
 * page is ever built.
 */
function buildRuntimeWeekPage(
  weekStart: Temporal.PlainDate,
  today: Temporal.PlainDate,
): CalendarWeekPage {
  const days = Array.from({ length: 7 }, (_, index) =>
    getCalendarDay(weekStart.add({ days: index }), today),
  );
  const holdsToday = startOfWeek(today).equals(weekStart);

  return {
    label: getCalendarWeekLabel(weekStart),
    monthName: getMonthName(weekStart),
    isoPrefix: getIsoMonthPrefix(weekStart),
    defaultDate: holdsToday ? String(today.day) : String(days[0]?.date ?? "1"),
    days,
  };
}

function buildRuntimeMonthCells(
  monthDate: Temporal.PlainDate,
  today: Temporal.PlainDate,
): (CalendarDay | null)[] {
  const firstOfMonth = monthDate.with({ day: 1 });
  const cells: (CalendarDay | null)[] = Array.from(
    { length: firstOfMonth.dayOfWeek - 1 },
    () => null,
  );

  for (let day = 1; day <= firstOfMonth.daysInMonth; day += 1) {
    cells.push(getCalendarDay(firstOfMonth.with({ day }), today));
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
}

function buildRuntimeMonthPage(
  monthDate: Temporal.PlainDate,
  today: Temporal.PlainDate,
): CalendarMonthPage {
  const firstOfMonth = monthDate.with({ day: 1 });
  const firstOfThisMonth = today.with({ day: 1 });
  const monthName = getMonthName(firstOfMonth);
  const comparedToThisMonth = Temporal.PlainDate.compare(
    firstOfMonth,
    firstOfThisMonth,
  );

  return {
    label: `${monthName} ${firstOfMonth.year}`,
    monthName,
    isoPrefix: getIsoMonthPrefix(firstOfMonth),
    defaultDate:
      comparedToThisMonth === 0
        ? String(today.day)
        : comparedToThisMonth < 0
          ? String(firstOfMonth.daysInMonth)
          : "1",
    cells: buildRuntimeMonthCells(firstOfMonth, today),
  };
}

// A page is addressed by the ISO date it starts on: the Monday for a week,
// the 1st for a month. Runtime pages are built from that anchor; the
// fixture pages are looked up by it, so nothing downstream has to know
// which of the two it is holding.
function weekPageAnchor(page: CalendarWeekPage) {
  const firstDay = page.days[0];
  return firstDay
    ? getCalendarDayIsoDate(firstDay, page.isoPrefix)
    : `${page.isoPrefix}-01`;
}

function monthPageAnchor(page: CalendarMonthPage) {
  return `${page.isoPrefix}-01`;
}

function findCalendarPageIndex<T>(
  pages: T[],
  containsToday: (page: T) => boolean,
  fallbackIndex: number,
) {
  if (pages.length === 0) return 0;
  const index = pages.findIndex(containsToday);
  return index >= 0 ? index : Math.min(fallbackIndex, pages.length - 1);
}

const CHECK_IN_SYMPTOM_LABELS = new Set([
  ...bleedingOptions
    .filter((option) => option.value !== "none")
    .map((option) => option.label),
  ...checkInSymptomCategories.flatMap((category) =>
    category.options.map((option) => option.label),
  ),
]);

type PersistedSymptomRecord = {
  id: string;
  symptom?: string | null;
  notes?: string | null;
};

function getDraftSymptomLabels(draft: DailyCheckInDraft) {
  return [
    ...bleedingOptions
      .filter(
        (option) =>
          option.value !== "none" && draft.bleeding.includes(option.value),
      )
      .map((option) => option.label),
    ...draft.symptoms,
  ];
}

function buildDailyCheckInFromRecords(
  records: PersistedSymptomRecord[] | undefined,
  noteText: string | null | undefined,
): DailyCheckInDraft | undefined {
  const labels = new Set(
    (records ?? [])
      .map((record) => record.symptom)
      .filter((symptom): symptom is string => Boolean(symptom)),
  );
  const bleeding = bleedingOptions
    .filter((option) => option.value !== "none" && labels.has(option.label))
    .map((option) => option.value);
  const symptomLabels = checkInSymptomCategories
    .flatMap((category) => category.options.map((option) => option.label))
    .filter((label) => labels.has(label));
  const notes =
    noteText ??
    records?.find((record) => record.notes && record.notes.trim().length > 0)
      ?.notes ??
    "";

  if (bleeding.length === 0 && symptomLabels.length === 0 && !notes.trim()) {
    return undefined;
  }

  return {
    bleeding: bleeding.length > 0 ? bleeding : ["none"],
    symptoms: symptomLabels,
    notes,
  };
}

function toggleListValue(values: string[], value: string) {
  return values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];
}

function hasBleedingSelection(bleeding: CheckInBleedingLevel[]) {
  return bleeding.some((value) => value !== "none");
}

function toggleBleedingSelection(
  current: CheckInBleedingLevel[],
  value: CheckInBleedingLevel,
): CheckInBleedingLevel[] {
  if (value === "none") return ["none"];

  const withoutNone = current.filter((item) => item !== "none");

  if (value === "clots") {
    const next: CheckInBleedingLevel[] = withoutNone.includes("clots")
      ? withoutNone.filter((item) => item !== "clots")
      : [...withoutNone, "clots"];
    return next.length > 0 ? next : ["none"];
  }

  const flowValues: CheckInBleedingLevel[] = [
    "spotting",
    "light",
    "medium",
    "heavy",
  ];
  const hasSameFlow = withoutNone.includes(value);
  const preservedModifiers = withoutNone.filter(
    (item) => !flowValues.includes(item),
  );
  const next = hasSameFlow
    ? preservedModifiers
    : [value, ...preservedModifiers];
  return next.length > 0 ? next : ["none"];
}

const markerByMayDate: Record<string, CalendarDay["markers"]> = {
  "3": ["period"],
  "4": ["period"],
  "5": ["period"],
  "6": ["period"],
  "12": ["lab"],
  "13": ["med"],
  "14": ["med"],
  "15": ["med"],
  "16": ["visit"],
  "17": ["med"],
  "18": ["med"],
  "19": ["med", "visit"],
  "20": ["visit", "lab"],
  "21": ["med"],
  "22": ["med"],
  "23": ["med"],
  "24": ["visit"],
};

const weekDays: CalendarDay[] = [
  { label: "Mon", date: "18", state: "past", markers: ["med"] },
  { label: "Tue", date: "19", state: "today", markers: ["med", "visit"] },
  { label: "Wed", date: "20", state: "upcoming", markers: ["visit", "lab"] },
  { label: "Thu", date: "21", state: "upcoming", markers: ["med"] },
  { label: "Fri", date: "22", state: "upcoming", markers: ["med"] },
  { label: "Sat", date: "23", state: "upcoming", markers: ["med"] },
  { label: "Sun", date: "24", state: "upcoming", markers: ["visit"] },
];

const calendarWeekPages: CalendarWeekPage[] = [
  {
    label: "May",
    monthName: "May",
    isoPrefix: "2026-05",
    defaultDate: "16",
    days: [
      { label: "Mon", date: "11", state: "past", markers: [] },
      { label: "Tue", date: "12", state: "past", markers: ["lab"] },
      { label: "Wed", date: "13", state: "past", markers: ["med"] },
      { label: "Thu", date: "14", state: "past", markers: ["med"] },
      { label: "Fri", date: "15", state: "past", markers: ["med"] },
      { label: "Sat", date: "16", state: "past", markers: ["visit"] },
      { label: "Sun", date: "17", state: "past", markers: ["med"] },
    ] satisfies CalendarDay[],
  },
  {
    label: "May",
    monthName: "May",
    isoPrefix: "2026-05",
    defaultDate: "19",
    days: weekDays,
  },
  {
    label: "May",
    monthName: "May",
    isoPrefix: "2026-05",
    defaultDate: "25",
    days: [
      { label: "Mon", date: "25", state: "upcoming", markers: [] },
      { label: "Tue", date: "26", state: "upcoming", markers: [] },
      { label: "Wed", date: "27", state: "upcoming", markers: [] },
      { label: "Thu", date: "28", state: "upcoming", markers: [] },
      { label: "Fri", date: "29", state: "upcoming", markers: [] },
      { label: "Sat", date: "30", state: "upcoming", markers: [] },
      { label: "Sun", date: "31", state: "upcoming", markers: [] },
    ] satisfies CalendarDay[],
  },
  {
    label: "June - July",
    monthName: "June",
    isoPrefix: "2026-06",
    defaultDate: "29",
    days: [
      "2026-06-29",
      "2026-06-30",
      "2026-07-01",
      "2026-07-02",
      "2026-07-03",
      "2026-07-04",
      "2026-07-05",
    ].map((isoDate) => {
      const date = Temporal.PlainDate.from(isoDate);
      return {
        ...getCalendarDay(date, Temporal.PlainDate.from("2026-05-19")),
        markers: [],
      };
    }),
  },
  {
    label: "December 2026 - January 2027",
    monthName: "December",
    isoPrefix: "2026-12",
    defaultDate: "28",
    days: [
      "2026-12-28",
      "2026-12-29",
      "2026-12-30",
      "2026-12-31",
      "2027-01-01",
      "2027-01-02",
      "2027-01-03",
    ].map((isoDate) => {
      const date = Temporal.PlainDate.from(isoDate);
      return {
        ...getCalendarDay(date, Temporal.PlainDate.from("2026-05-19")),
        markers: [],
      };
    }),
  },
];

function buildMonthCells({
  daysInMonth,
  leadingBlanks,
  monthState,
  isMay = false,
}: {
  daysInMonth: number;
  leadingBlanks: number;
  monthState: "past" | "current" | "upcoming";
  isMay?: boolean;
}): (CalendarDay | null)[] {
  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const cells: (CalendarDay | null)[] = Array.from(
    { length: leadingBlanks },
    () => null,
  );

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = String(day);
    const state: CalendarDay["state"] =
      monthState === "current"
        ? day < 19
          ? "past"
          : day === 19
            ? "today"
            : "upcoming"
        : monthState;
    cells.push({
      label: labels[(leadingBlanks + day - 1) % labels.length]!,
      date,
      state,
      markers: isMay ? (markerByMayDate[date] ?? []) : [],
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
}

const calendarMonthPages: CalendarMonthPage[] = [
  {
    label: "April 2026",
    monthName: "April",
    isoPrefix: "2026-04",
    defaultDate: "30",
    cells: buildMonthCells({
      daysInMonth: 30,
      leadingBlanks: 2,
      monthState: "past",
    }),
  },
  {
    label: "May 2026",
    monthName: "May",
    isoPrefix: "2026-05",
    defaultDate: "19",
    cells: buildMonthCells({
      daysInMonth: 31,
      leadingBlanks: 4,
      monthState: "current",
      isMay: true,
    }),
  },
  {
    label: "June 2026",
    monthName: "June",
    isoPrefix: "2026-06",
    defaultDate: "1",
    cells: buildMonthCells({
      daysInMonth: 30,
      leadingBlanks: 0,
      monthState: "upcoming",
    }),
  },
];

const calendarRowsByDate: Record<string, TodayRow[]> = {
  "18": [
    {
      id: "calendar-18-gonal-f",
      time: "7:00 AM",
      title: "Gonal-F 225 IU",
      detail: "Completed stimulation dose from yesterday.",
      icon: "hormones",
      status: "med",
      kind: "medication",
      date: "2026-05-18",
      medicationName: "Gonal-F",
      medicationId: "15640000-0000-4000-8000-000000000001",
      medicationDose: "225 IU",
      scheduledFor: "2026-05-18T07:00:00",
      medicationFormat: "Subcutaneous pen",
      medicationForm: "pen",
      medicationAvailabilityStatus: "on-hand",
    },
    {
      id: "calendar-18-cetrotide",
      time: "9:00 PM",
      title: "Cetrotide",
      detail: "Antagonist dose for the previous cycle day.",
      icon: "hormones",
      status: "med",
      kind: "medication",
      date: "2026-05-18",
      medicationName: "Cetrotide",
      medicationId: "15640000-0000-4000-8000-000000000002",
      medicationDose: "0.25 mg",
      scheduledFor: "2026-05-18T21:00:00",
      medicationFormat: "Subcutaneous vial",
      medicationForm: "vial",
      medicationAvailabilityStatus: "low-stock",
    },
  ],
  "19": [
    {
      id: "calendar-19-gonal-f",
      time: "7:00 AM",
      title: "Gonal-F 225 IU",
      detail: "Gives follicles the FSH signal to keep growing together.",
      icon: "hormones",
      status: "med",
      kind: "medication",
      date: "2026-05-19",
      medicationName: "Gonal-F",
      medicationId: "15640000-0000-4000-8000-000000000001",
      medicationDose: "225 IU",
      scheduledFor: "2026-05-19T07:00:00",
      medicationFormat: "Subcutaneous pen",
      medicationForm: "pen",
      medicationAvailabilityStatus: "on-hand",
    },
    {
      id: "calendar-19-menopur-partner",
      time: "7:05 AM",
      title: "Menopur 75 IU",
      detail: "Adds LH activity alongside the morning stimulation dose.",
      partnerName: "Maya",
      icon: "hormones",
      status: "med",
      kind: "medication",
      date: "2026-05-19",
      medicationName: "Menopur",
      medicationId: "15640000-0000-4000-8000-000000000003",
      medicationDose: "75 IU",
      scheduledFor: "2026-05-19T07:05:00",
      medicationFormat: "Subcutaneous vial",
      medicationForm: "vial",
      medicationAvailabilityStatus: "on-hand",
    },
    {
      id: "calendar-19-monitoring",
      time: "8:20 AM",
      title: "Monitoring visit",
      detail: "Ultrasound + estradiol. Dose timing may update after this.",
      icon: "afc",
      status: "appointment",
      kind: "monitoring",
      procedureName: "Transvaginal ultrasound",
      appointmentResultType: "monitoring",
    },
    {
      id: "calendar-19-appointment",
      appointmentId: "fixture-appointment-19",
      time: "2:00 PM",
      title: "Follow-up consultation",
      detail: "Louise clinic room",
      notes: "Bring your questions for the clinic team.",
      icon: "start",
      status: "appointment",
      kind: "appointment",
      date: "2026-05-19",
      completable: true,
    },
    {
      id: "calendar-19-cetrotide",
      time: "9:00 PM",
      title: "Cetrotide",
      detail: "Prevents early ovulation while follicles finish growing.",
      icon: "hormones",
      status: "med",
      kind: "medication",
      date: "2026-05-19",
      medicationName: "Cetrotide",
      medicationId: "15640000-0000-4000-8000-000000000002",
      medicationDose: "0.25 mg",
      scheduledFor: "2026-05-19T21:00:00",
      medicationFormat: "Subcutaneous vial",
      medicationForm: "vial",
      medicationAvailabilityStatus: "low-stock",
    },
  ],
  "20": [
    {
      id: "calendar-20-monitoring",
      time: "8:20 AM",
      title: "Monitoring visit",
      detail: "Ultrasound + estradiol. Retrieval estimate may update.",
      icon: "afc",
      status: "appointment",
      kind: "monitoring",
      procedureName: "Transvaginal ultrasound",
      appointmentResultType: "monitoring",
    },
    {
      id: "calendar-20-gonal-f",
      time: "7:00 PM",
      title: "Gonal-F 225 IU",
      detail: "Keep scheduled unless the clinic changes the dose.",
      icon: "hormones",
      status: "med",
      kind: "medication",
      date: "2026-05-20",
      medicationName: "Gonal-F",
      medicationId: "15640000-0000-4000-8000-000000000001",
      medicationDose: "225 IU",
      scheduledFor: "2026-05-20T19:00:00",
      medicationFormat: "Subcutaneous pen",
      medicationForm: "pen",
      medicationAvailabilityStatus: "on-hand",
    },
  ],
  "21": [
    {
      id: "calendar-21-cetrotide",
      time: "9:00 PM",
      title: "Cetrotide",
      detail: "Antagonist dose if the clinic keeps the current plan.",
      icon: "hormones",
      status: "med",
      kind: "medication",
      date: "2026-05-21",
      medicationName: "Cetrotide",
      medicationId: "15640000-0000-4000-8000-000000000002",
      medicationDose: "0.25 mg",
      scheduledFor: "2026-05-21T21:00:00",
      medicationFormat: "Subcutaneous vial",
      medicationForm: "vial",
      medicationAvailabilityStatus: "in-transit",
    },
  ],
  "22": [
    {
      id: "calendar-22-supply",
      time: "10:00 AM",
      title: "Medication supply check",
      detail: "Make sure trigger and antagonist meds are physically on hand.",
      icon: "hormones",
      status: "support",
      kind: "task",
    },
  ],
  "23": [
    {
      id: "calendar-23-trigger-window",
      time: "Evening",
      title: "Possible trigger window",
      detail: "Estimated only. The clinic confirms after monitoring.",
      icon: "hormones",
      status: "scheduled",
      kind: "task",
    },
  ],
  "24": [
    {
      id: "calendar-24-retrieval-window",
      time: "Morning",
      title: "Possible retrieval window",
      detail: "Only appears if trigger timing is confirmed.",
      icon: "cryo",
      status: "scheduled",
      kind: "appointment",
      procedureName: "Egg retrieval",
      appointmentResultType: "collection",
    },
  ],
};

export const pharmacyFulfillmentCards: PharmacyFulfillmentCardData[] = [
  {
    id: "alto-delivery",
    title: "Medication delivery",
    pharmacy: "Alto Pharmacy",
    statusLabel: "Shipped",
    timing: "Arrives tomorrow, 2-6 PM",
    estimatedTime: "Estimated arrival by 3:00 PM",
    trackingNumber: "7489 2847 3910",
    trackingUrl: "https://alto.com",
    pharmacyPhone: "+18008746210",
    tone: "delivery",
    currentStep: 2,
    confirmationLabel: "I got them",
    steps: [
      { label: "Placed", dateLabel: "May 18", icon: CheckCircle2 },
      { label: "Preparing", dateLabel: "May 19", icon: FlaskConical },
      { label: "Shipped", dateLabel: "Today", icon: PackageCheck },
      { label: "In transit", dateLabel: "Tomorrow", icon: Truck },
      { label: "Delivered", dateLabel: "May 21", icon: CheckCircle2 },
    ],
    medications: [
      {
        name: "Gonal-F",
        dosage: "900 IU",
        route: "Subcutaneous",
        form: "pen",
      },
      {
        name: "Cetrotide",
        dosage: "0.25 mg",
        route: "Subcutaneous",
        form: "vial",
      },
      {
        name: "Ovidrel",
        dosage: "250 mcg",
        route: "Subcutaneous",
        form: "pen",
      },
      {
        name: "Endometrin",
        dosage: "100 mg",
        route: "Vaginal",
        form: "suppository",
      },
      {
        name: "Dexamethasone",
        dosage: "0.5 mg",
        route: "Oral",
        form: "pill",
      },
    ],
    detail:
      "Cold-chain shipment. Bring it inside when delivered and move refrigerated meds to the fridge.",
  },
  {
    id: "local-pickup",
    kind: "pickup-action",
    timeSensitive: true,
    title: "Local pharmacy pickup",
    pharmacy: "CVS Specialty",
    statusLabel: "Ready today",
    timing: "Pickup by 7 PM",
    estimatedTime: "Pickup window closes at 7:00 PM",
    pharmacyPhone: "+18007472878",
    tone: "pickup",
    currentStep: 2,
    confirmationLabel: "I've picked them up",
    steps: [
      { label: "Requested", dateLabel: "Today", icon: CheckCircle2 },
      { label: "Pharmacy review", dateLabel: "Today", icon: FlaskConical },
      { label: "Ready", dateLabel: "By 7 PM", icon: PackageCheck },
      { label: "On hand", dateLabel: "Tonight", icon: CheckCircle2 },
    ],
    medications: [
      {
        name: "Menopur",
        dosage: "75 IU",
        route: "Subcutaneous",
        form: "vial",
      },
      {
        name: "Alcohol swabs",
        dosage: "1 box",
        route: "Supplies",
        form: "pill",
      },
    ],
    detail:
      "Pickup is time-sensitive because the clinic may adjust dosing after your monitoring call.",
  },
  {
    id: "refill-needed",
    kind: "refill-action",
    timeSensitive: true,
    title: "Refill needed",
    pharmacy: "Clinic pharmacy",
    statusLabel: "Action needed",
    timing: "Cetrotide runs out tomorrow",
    estimatedTime: "Request before tomorrow's monitoring call",
    pharmacyPhone: "+18005550121",
    tone: "attention",
    currentStep: 0,
    steps: [
      { label: "Request refill", dateLabel: "Today", icon: Pill },
      { label: "Clinic approval", dateLabel: "Next", icon: FlaskConical },
      {
        label: "Pharmacy fill",
        dateLabel: "After approval",
        icon: PackageCheck,
      },
      { label: "On hand", dateLabel: "Before tomorrow", icon: CheckCircle2 },
    ],
    medications: [
      {
        name: "Cetrotide",
        dosage: "0.25 mg",
        route: "Subcutaneous",
        form: "vial",
      },
    ],
    detail:
      "You have one antagonist dose left after tonight. Requesting now avoids a scramble if stimulation extends.",
  },
  {
    id: "delivered",
    title: "Delivery complete",
    pharmacy: "Alto Pharmacy",
    statusLabel: "On hand",
    timing: "Delivered yesterday",
    estimatedTime: "Delivered yesterday at 4:12 PM",
    trackingNumber: "5281 6381 9402",
    trackingUrl: "https://alto.com",
    pharmacyPhone: "+18008746210",
    tone: "done",
    currentStep: 4,
    steps: [
      { label: "Placed", dateLabel: "May 16", icon: CheckCircle2 },
      { label: "Preparing", dateLabel: "May 16", icon: FlaskConical },
      { label: "Shipped", dateLabel: "May 17", icon: PackageCheck },
      { label: "In transit", dateLabel: "May 18", icon: Truck },
      { label: "Delivered", dateLabel: "Yesterday", icon: CheckCircle2 },
    ],
    medications: [
      {
        name: "Ovidrel",
        dosage: "250 mcg",
        route: "Subcutaneous",
        form: "pen",
      },
    ],
    detail:
      "Marked on hand. Keep the trigger medication exactly where you can find it when the clinic calls.",
  },
];

// Optional visual fixture only; the product path hydrates from medication stock.
const pharmacyInventoryMedications: PharmacyInventoryMedication[] = [
  {
    id: "inventory-gonal-f",
    name: "Gonal-F",
    dosage: "225 IU",
    route: "Subcutaneous",
    form: "pen",
    quantity: 1,
    unitLabel: "pen",
  },
  {
    id: "inventory-cetrotide",
    name: "Cetrotide",
    dosage: "0.25 mg",
    route: "Subcutaneous",
    form: "vial",
    quantity: 1,
    unitLabel: "vial",
  },
  {
    id: "inventory-ovidrel",
    name: "Ovidrel",
    dosage: "250 mcg",
    route: "Subcutaneous",
    form: "pen",
    quantity: 1,
    unitLabel: "pen",
  },
  {
    id: "inventory-alcohol-swabs",
    name: "Alcohol swabs",
    dosage: "1 box",
    route: "Supplies",
    form: "pill",
    quantity: 1,
    unitLabel: "box",
  },
];

const cycleTeamMembers: CycleTeamMember[] = [
  {
    id: "provider-maya-chen",
    kind: "person",
    label: "Provider",
    name: "Dr. Maya Chen",
    detail: "REI overseeing stimulation and trigger timing.",
    organicIcon: "concierge",
  },
  {
    id: "coordinator-elena",
    kind: "person",
    label: "Coordinator",
    name: "Elena R.",
    detail: "Nurse line, lab instructions, and same-day updates.",
    organicIcon: "partner",
  },
  {
    id: "pharmacy-alto",
    kind: "organization",
    label: "Pharmacy",
    name: "Alto Pharmacy",
    detail: "Delivery, refills, cold-chain notes, and trigger kit.",
    organicIcon: "hormones",
  },
];

const conditionDiagnosisSnapshots: ConditionDiagnosisSnapshot[] = [
  {
    id: "pcos",
    title: "PMOS (ex PCOS)",
    status: "Confirmed",
    code: "E28.2",
    source: "Medical history + prior clinic note",
    summary:
      "May change ovarian response, OHSS risk, trigger planning, and how closely stimulation is monitored.",
    careImpact:
      "Louise can keep AMH, AFC, follicle growth, symptoms, trigger plan, and medication changes connected to this context.",
    fertilityImpact:
      "PMOS (ex PCOS) can mean a stronger ovarian response during stimulation, but egg quantity and egg quality still need to be understood separately.",
    insuranceImpact:
      "Diagnosis codes and clinic notes may support prior authorization, medication coverage, and cycle documentation.",
    records: [
      "Clinic note confirming PMOS (ex PCOS)",
      "Most recent AMH/AFC",
      "Prior stimulation response, if any",
    ],
    watchItems: [
      "OHSS symptoms",
      "Fast follicle growth",
      "Trigger medication timing",
    ],
  },
  {
    id: "pelvic-surgery",
    title: "Prior pelvic surgery",
    status: "Self-reported",
    source: "Medical history",
    summary:
      "Can matter for anesthesia planning, retrieval approach, adhesions, and what records the clinic may request.",
    careImpact:
      "Louise can keep surgical reports, anesthesia notes, and clinic questions attached to this cycle.",
    fertilityImpact:
      "Prior surgery may or may not affect retrieval access or ovarian reserve depending on what was done and where.",
    insuranceImpact:
      "Operative reports can help explain medical necessity or clarify whether a procedure is fertility-related.",
    records: [
      "Operative report",
      "Pathology report, if relevant",
      "Surgeon or hospital summary",
    ],
    watchItems: [
      "Anesthesia notes",
      "Retrieval access questions",
      "Pain or recovery history",
    ],
  },
  {
    id: "thyroid",
    title: "Thyroid history",
    status: "Needs review",
    source: "Medication list + missing recent lab",
    summary:
      "Thyroid status can affect cycle planning, pregnancy planning, and medication safety checks.",
    careImpact:
      "Louise can flag when recent TSH or medication updates are missing before a cycle decision.",
    fertilityImpact:
      "Thyroid levels are usually interpreted with the treatment goal in mind, especially if pregnancy is part of the plan.",
    insuranceImpact:
      "Recent labs and active prescriptions can support coverage review and clinic clearance.",
    records: [
      "Recent TSH",
      "Active thyroid medication",
      "Endocrinology note, if available",
    ],
    watchItems: [
      "TSH timing",
      "Medication changes",
      "Pregnancy-planning thresholds",
    ],
  },
];

const labReportSnapshots: LabReportSnapshot[] = [
  {
    id: "embryo-testing",
    title: "Embryo testing report",
    subtitle:
      "PGT-A, PGT-M, biopsy status, euploidy, and report files when embryos are tested.",
    status: "Coming soon",
    organicIcon: "genetics",
  },
  {
    id: "egg-assessment",
    title: "Egg assessment report",
    subtitle:
      "Clinic or lab analysis of frozen eggs when imaging or quality reports are available.",
    status: "Coming soon",
    organicIcon: "eggs",
  },
];

type CurrentCycleContext = {
  eyebrow: string;
  title: string;
  summary: string;
  hasActiveCycle: boolean;
};

function getStartTreatmentPresetCycleType({
  activeCycleType,
  treatmentGoal,
}: {
  activeCycleType: CycleType | null | undefined;
  treatmentGoal: string | null | undefined;
}): StartTreatmentCycleType | null {
  if (activeCycleType) return activeCycleType as StartTreatmentCycleType;

  switch (treatmentGoal) {
    case "egg-freezing":
    case "egg-preserve":
      return "egg_freezing";
    case "embryo-freezing":
    case "embryo-preserve":
      return "embryo_freezing";
    case "sperm-freezing":
    case "sperm-preserve":
      return "sperm_freezing";
    default:
      return null;
  }
}

function formatEnumLabel(value: string | null | undefined) {
  if (!value) return "";
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getCycleOrganicIcon(cycleType: CycleType): OrganicMiniIconKind {
  if (cycleType === "egg_freezing") return "eggs";
  if (cycleType === "sperm_freezing") return "sperm";
  if (
    cycleType === "ivf" ||
    cycleType === "icsi" ||
    cycleType === "fet" ||
    cycleType === "embryo_freezing"
  ) {
    return "genetics";
  }
  return "hormones";
}

function formatShortDate(date: string | null | undefined, locale: Locale) {
  if (!date) return "";
  const parsed = new Date(`${date.split("T")[0]}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return formatDate(parsed, locale, {
    month: "short",
    day: "numeric",
  });
}

function formatCycleMonthYear(date: string | null | undefined, locale: Locale) {
  if (!date) return "";
  const parsed = new Date(`${date.split("T")[0]}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return formatDate(parsed, locale, {
    month: "short",
    year: "numeric",
  });
}

type CycleStatusKey =
  "active" | "positive" | "closed" | "cancelled" | "completed" | "other";

function getCycleStatusKey(cycle: Cycle): CycleStatusKey {
  const outcome = (
    cycle as {
      outcome?: { type?: string | null } | null;
    }
  ).outcome;
  if (cycle.status === "open") return "active";
  if (outcome?.type === "pregnancy") return "positive";
  if (outcome?.type === "no_pregnancy" || outcome?.type === "miscarriage") {
    return "closed";
  }
  if (outcome?.type === "cancelled") return "cancelled";
  return cycle.status === "closed" ? "completed" : "other";
}

function translateCycleStatusLabel(
  t: TranslationFunction,
  cycle: Cycle,
): string {
  const key = getCycleStatusKey(cycle);
  switch (key) {
    case "active":
      return t("care.cycle_history.status_active");
    case "positive":
      return t("care.cycle_history.outcome_positive");
    case "closed":
      return t("care.cycle_history.outcome_closed");
    case "cancelled":
      return t("care.cycle_history.outcome_cancelled");
    case "completed":
      return t("care.cycle_history.outcome_completed");
    default:
      return formatEnumLabel(cycle.status);
  }
}

function buildPastCycleSnapshots({
  t,
  cycles,
  activeCycleId,
  labels,
  locale,
}: {
  t: TranslationFunction;
  cycles: Cycle[] | undefined;
  activeCycleId: string | null | undefined;
  labels: Partial<Record<CycleType, string>>;
  locale: Locale;
}): PastCycleSnapshot[] {
  if (!cycles) return [];
  // Past cycles are sorted by when they *ended*, not when they started.
  // A cycle can be planned with a future startDate and then closed early
  // (cancelled), which would otherwise float ahead of cycles that actually
  // ran to term more recently. Fall back to startDate/createdAt so cycles
  // missing an endDate still get a stable position.
  const closed = cycles
    .filter((cycle) => cycle.id !== activeCycleId && cycle.status === "closed")
    .sort((a, b) => {
      const aTime = new Date(
        a.endDate ?? a.startDate ?? a.createdAt ?? 0,
      ).getTime();
      const bTime = new Date(
        b.endDate ?? b.startDate ?? b.createdAt ?? 0,
      ).getTime();
      return bTime - aTime;
    });

  // Number cycles by when they started (oldest start = #1) so "Cycle FIV #1"
  // matches the patient's mental model of "my first IVF." Independent of the
  // display sort, which is by endDate.
  const chronologicalIndex = new Map<string, number>();
  const chronoCounters: Partial<Record<CycleType, number>> = {};
  const byStartAsc = [...closed].sort((a, b) => {
    const aTime = new Date(a.startDate ?? a.createdAt ?? 0).getTime();
    const bTime = new Date(b.startDate ?? b.createdAt ?? 0).getTime();
    return aTime - bTime;
  });
  for (const cycle of byStartAsc) {
    const next = (chronoCounters[cycle.cycleType] ?? 0) + 1;
    chronoCounters[cycle.cycleType] = next;
    chronologicalIndex.set(cycle.id, next);
  }

  return closed.map((cycle) => {
    const count = chronologicalIndex.get(cycle.id) ?? 1;
    const title = `${labels[cycle.cycleType] ?? formatEnumLabel(cycle.cycleType)} #${count}`;
    const dateLabel = formatCycleMonthYear(cycle.startDate, locale);
    const subtitle = [cycle.provider, dateLabel].filter(Boolean).join(", ");
    const statusKey = getCycleStatusKey(cycle);
    const statusBadge =
      statusKey === "completed"
        ? undefined
        : translateCycleStatusLabel(t, cycle);
    const statusTone: StatusBadgeTone | undefined =
      statusKey === "positive" ? "green" : undefined;
    const metrics = [
      cycle.startDate
        ? {
            label: t("care.cycle_history.metric_started"),
            value: formatShortDate(cycle.startDate, locale),
          }
        : null,
      cycle.endDate
        ? {
            label: t("care.cycle_history.metric_ended"),
            value: formatShortDate(cycle.endDate, locale),
          }
        : null,
    ].filter((metric): metric is { label: string; value: string } =>
      Boolean(metric?.value),
    );

    return {
      id: cycle.id,
      title,
      subtitle: subtitle || t("care.cycle_history.subtitle_default"),
      statusBadge,
      statusTone,
      organicIcon: getCycleOrganicIcon(cycle.cycleType),
      metrics,
    };
  });
}

function buildRealCycleTeamMembers({
  t,
  members,
  clinicName,
  recordedConnections = [],
}: {
  t: ReturnType<typeof useTranslations>;
  members: ApiCareTeamMember[] | undefined;
  clinicName: string | null | undefined;
  recordedConnections?: CareConnection[];
}): CycleTeamMember[] {
  const people = (members ?? []).map((member) => ({
    id: member.id,
    kind: "person" as const,
    label: member.role || t("care.overview.team.role_provider"),
    name: member.name,
    detail: [member.role, member.email, member.phoneNumber, clinicName]
      .filter(Boolean)
      .join(" · "),
    organicIcon: "concierge" as const,
  }));

  const recordedMembers = recordedConnections
    .filter((connection) => connection.kind === "provider")
    .map((connection) => ({
      id: `recorded-${connection.id}`,
      kind: "person" as const,
      label: connection.role || t("care.overview.team.role_provider"),
      name: connection.name,
      detail: [
        connection.role,
        connection.specialty,
        connection.practiceName,
        connection.contactPhone,
        connection.contactEmail,
      ]
        .filter(Boolean)
        .join(" · "),
      organicIcon: "concierge" as const,
    }));
  const allPeople = [...people, ...recordedMembers];

  if (!clinicName) return allPeople;

  return [
    ...allPeople,
    {
      id: `clinic-${clinicName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      kind: "organization" as const,
      label: t("care.overview.team.label_clinic"),
      name: clinicName,
      detail:
        allPeople.length > 0
          ? t("care.overview.team.clinic_connected")
          : t("care.overview.team.clinic_add_details"),
      organicIcon: "clinics" as const,
    },
  ];
}

function getCycleGuidanceContent(
  t: ReturnType<typeof useTranslations>,
  cycleType: CycleType | null | undefined,
  hasRecordedPregnancy: boolean,
) {
  // A recorded pregnancy moves the patient past the cycle guide. Keyed on the
  // result rather than the cycle type because the cycle stays open once a result
  // is recorded, so the type alone would keep offering stimulation and transfer
  // planning to someone whose transfer already worked.
  if (hasRecordedPregnancy) {
    return {
      title: t("care.overview.guides.early_pregnancy_title"),
      subtitle: t("care.overview.guides.early_pregnancy_subtitle"),
      organicIcon: "start" as const,
      // No early-pregnancy article exists in the library yet, so this opens
      // Learn rather than pointing at an id that resolves to nothing — the same
      // fallback the transfer and IUI rows already use.
      articleId: null,
    };
  }
  if (cycleType === "egg_freezing") {
    return {
      title: t("care.overview.guides.egg_freezing_title"),
      subtitle: t("care.overview.guides.egg_freezing_subtitle"),
      organicIcon: "eggs" as const,
      articleId: "moon-article-egg-freezing-timeline",
    };
  }
  if (cycleType === "ivf" || cycleType === "icsi") {
    return {
      title: t("care.overview.guides.ivf_title"),
      subtitle: t("care.overview.guides.ivf_subtitle"),
      organicIcon: "genetics" as const,
      articleId: "moon-article-ivf-protocols",
    };
  }
  if (cycleType === "embryo_freezing") {
    return {
      title: t("care.overview.guides.embryo_freezing_title"),
      subtitle: t("care.overview.guides.embryo_freezing_subtitle"),
      organicIcon: "genetics" as const,
      articleId: "moon-article-embryo-freezing",
    };
  }
  if (cycleType === "fet") {
    return {
      title: t("care.overview.guides.transfer_title"),
      subtitle: t("care.overview.guides.transfer_subtitle"),
      organicIcon: "process" as const,
      articleId: null,
    };
  }
  if (cycleType === "iui") {
    return {
      title: t("care.overview.guides.iui_title"),
      subtitle: t("care.overview.guides.iui_subtitle"),
      organicIcon: "hormones" as const,
      articleId: null,
    };
  }
  if (cycleType === "sperm_freezing") {
    return {
      title: t("care.overview.guides.sperm_freezing_title"),
      subtitle: t("care.overview.guides.sperm_freezing_subtitle"),
      organicIcon: "sperm" as const,
      articleId: "new-article-sperm-freezing-guide",
    };
  }
  return {
    title: t("care.overview.guides.fallback_title"),
    subtitle: t("care.overview.guides.fallback_subtitle"),
    organicIcon: "process" as const,
    articleId: null,
  };
}

function getCareOutcomeFunnelVariant(
  cycleType: CycleType | null | undefined,
  hasRecordedResult: boolean,
) {
  // The funnel estimates what a cycle might yield. Once the cycle has a recorded
  // result there is nothing left to estimate, and projecting a live-birth chance
  // at someone who already has their answer — in either direction — is the kind
  // of interpretation this product does not do.
  if (hasRecordedResult) return null;
  if (cycleType === "egg_freezing") return "egg-freezing" as const;
  if (
    cycleType === "embryo_freezing" ||
    cycleType === "ivf" ||
    cycleType === "icsi"
  ) {
    return "embryo-freezing" as const;
  }
  return null;
}

export function getCareReviewConceptVariant(
  raw: string | null | undefined,
): CareReviewConceptVariant | null {
  if (raw === "egg-freezing-active") return raw;
  return null;
}

export function getDevSuggestedCareSurface(): string | null {
  if (!isDevEvidenceRouteEnabled()) return null;
  try {
    const params = getDevEvidenceSearchParams();
    if (!params) return null;
    return (
      params.get("dev_care_suggested") ??
      params.get("dev_home_suggested") ??
      params.get("dev_home_suggested_surface")
    );
  } catch {
    return null;
  }
}

function getDevAppointmentDetailMode(): "empty" | "note" | null {
  try {
    const params = getDevEvidenceSearchParams();
    if (!params) return null;
    const mode = params.get("dev_care_appointment_detail");
    if (!isDevEvidenceRouteEnabled() && params.get("test") !== "1") {
      return null;
    }
    return mode === "empty" || mode === "note" ? mode : null;
  } catch {
    return null;
  }
}

function isDevEvidenceRouteEnabled() {
  const params = getDevEvidenceSearchParams();
  if (!params || typeof window === "undefined") return false;
  if (__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") return true;
  return (
    params.get("test") === "1" &&
    (params.has("dev_care_suggested") ||
      params.has("dev_care_appointment_detail"))
  );
}

function getDevEvidenceSearchParams() {
  if (typeof window === "undefined") return null;
  const current = new URLSearchParams(window.location.search);
  try {
    const stored = window.sessionStorage?.getItem("__pr_visual_route_search");
    if (!stored) return current;
    for (const [key, value] of new URLSearchParams(stored)) {
      if (!current.has(key)) current.set(key, value);
    }
    return current;
  } catch {
    return current;
  }
}

export function ActionRowCard({
  icon,
  organicIcon,
  title,
  subtitle,
  onPress,
  trailing,
}: {
  icon?: ReactNode;
  organicIcon?: OrganicMiniIconKind;
  title: string;
  subtitle: string;
  onPress?: () => void;
  trailing?: ReactNode;
}) {
  return (
    <SharedActionRowCard
      onPress={onPress}
      title={title}
      subtitle={subtitle}
      leading={
        organicIcon ? <OrganicMiniIcon kind={organicIcon} size={36} /> : icon
      }
      trailing={trailing}
    />
  );
}

function QuickAddBar({
  onAddMedication,
  onAddAppointment,
  onUploadDocument,
  topSpacing = 14,
}: {
  onAddMedication: () => void;
  onAddAppointment: () => void;
  onUploadDocument: () => void;
  topSpacing?: number;
}) {
  const t = useTranslations();
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 8,
        marginTop: topSpacing,
      }}
    >
      <QuickAddButton
        icon={
          <Pill
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        }
        label={t("care.overview.quick_add.medication")}
        onPress={onAddMedication}
      />
      <QuickAddButton
        icon={
          <CalendarPlus
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        }
        label={t("care.overview.quick_add.appointment")}
        onPress={onAddAppointment}
      />
      <QuickAddButton
        icon={
          <FilePlus2
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        }
        label={t("care.overview.quick_add.document")}
        onPress={onUploadDocument}
      />
    </View>
  );
}

function PreCycleQuickAddBar({
  onStartCycle,
  onAddMedication,
  onAddAppointment,
  onUploadDocument,
}: {
  onStartCycle: () => void;
  onAddMedication: () => void;
  onAddAppointment: () => void;
  onUploadDocument: () => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ flexDirection: "row", gap: 8, marginTop: 14 }}>
      <QuickAddButton
        icon={
          <Activity
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        }
        label={t("care.overview.quick_add.start_cycle")}
        onPress={onStartCycle}
      />
      <QuickAddButton
        icon={
          <Pill
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        }
        label={t("care.overview.quick_add.medication")}
        onPress={onAddMedication}
        iconOnly
      />
      <QuickAddButton
        icon={
          <CalendarPlus
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        }
        label={t("care.overview.quick_add.appointment")}
        onPress={onAddAppointment}
        iconOnly
      />
      <QuickAddButton
        icon={
          <FilePlus2
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        }
        label={t("care.overview.quick_add.document")}
        onPress={onUploadDocument}
        iconOnly
      />
    </View>
  );
}

function QuickAddButton({
  icon,
  label,
  onPress,
  iconOnly = false,
}: {
  icon: ReactNode;
  label: string;
  onPress: () => void;
  iconOnly?: boolean;
}) {
  return (
    <GlassButton
      onPress={onPress}
      label={label}
      fill={!iconOnly}
      iconOnly={iconOnly}
      width={iconOnly ? 46 : undefined}
      leading={
        <Plus
          size={11}
          color={semanticColors.onDark.text.primary}
          strokeWidth={2.2}
        />
      }
      icon={icon}
    />
  );
}

function CurrentCycleContextCard({
  context,
  isTryingNaturally,
  onStartCycle,
  onCycleDetail,
  onViewPastCycles,
  onViewDocuments,
  onEndCycle,
  onAddMedication,
  onAddAppointment,
  onUploadDocument,
}: {
  context: CurrentCycleContext;
  isTryingNaturally: boolean;
  onStartCycle: () => void;
  onCycleDetail: () => void;
  onViewPastCycles: () => void;
  onViewDocuments: () => void;
  onEndCycle: () => void;
  onAddMedication: () => void;
  onAddAppointment: () => void;
  onUploadDocument: () => void;
}) {
  const t = useTranslations();
  const menuActions: HeroMenuAction[] = context.hasActiveCycle
    ? [
        {
          label: t("care.overview.menu.view_cycle_details"),
          action: "view_cycle_details",
          icon: Activity,
          onPress: onCycleDetail,
        },
        {
          label: t("care.overview.menu.view_past_cycles"),
          action: "view_past_cycles",
          icon: History,
          onPress: onViewPastCycles,
        },
        {
          label: t("care.overview.menu.cycle_documents"),
          action: "view_cycle_documents",
          icon: FileText,
          onPress: onViewDocuments,
        },
        {
          label: t("care.overview.menu.end_cycle"),
          action: "end_cycle",
          icon: CheckCircle2,
          onPress: onEndCycle,
          tone: "destructive",
        },
      ]
    : [
        {
          label: t("care.overview.menu.start_cycle"),
          action: "start_cycle",
          icon: Activity,
          onPress: onStartCycle,
        },
        {
          label: t("care.overview.menu.view_past_cycles"),
          action: "view_past_cycles",
          icon: History,
          onPress: onViewPastCycles,
        },
        {
          label: t("care.overview.menu.documents"),
          action: "view_documents",
          icon: FileText,
          onPress: onViewDocuments,
        },
      ];

  return (
    <Hero
      backgroundSource={careHeroGlowGradient}
      style={{ position: "relative", zIndex: 2 }}
      eyebrow={{ label: context.eyebrow, icon: Activity }}
      title={context.title}
      summary={context.summary}
      menuActions={menuActions}
      onPress={context.hasActiveCycle ? onCycleDetail : undefined}
      onPressAccessibilityLabel={
        context.hasActiveCycle
          ? t("care.overview.menu.view_cycle_details")
          : undefined
      }
      bottomContent={
        context.hasActiveCycle || isTryingNaturally ? (
          <QuickAddBar
            onAddMedication={onAddMedication}
            onAddAppointment={onAddAppointment}
            onUploadDocument={onUploadDocument}
          />
        ) : (
          <PreCycleQuickAddBar
            onStartCycle={onStartCycle}
            onAddMedication={onAddMedication}
            onAddAppointment={onAddAppointment}
            onUploadDocument={onUploadDocument}
          />
        )
      }
    />
  );
}

function CalendarMarker({
  marker,
}: {
  marker: CalendarDay["markers"][number];
}) {
  const color = calendarMarkerColors[marker] ?? calendarMarkerColors.fallback;

  /**
   * The fertile window is the one marker that names a stretch of the body's own
   * cycle rather than something scheduled, and it is the only estimate on the
   * row. A leaf says that where a dot could not: the other markers stay discs,
   * so the difference is visible before the legend is read. It keeps the green
   * it already had, which is what ties it to its legend entry.
   */
  if (marker === "fertile") {
    return (
      <View
        style={{
          width: CALENDAR_MARKER_LEAF_SIZE,
          height: CALENDAR_MARKER_LEAF_SIZE,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Leaf size={CALENDAR_MARKER_LEAF_SIZE} color={color} fill={color} />
      </View>
    );
  }

  return (
    <View
      style={{
        width: CALENDAR_MARKER_DOT_SIZE,
        height: CALENDAR_MARKER_DOT_SIZE,
        minWidth: CALENDAR_MARKER_DOT_SIZE,
        maxWidth: CALENDAR_MARKER_DOT_SIZE,
        borderRadius: CALENDAR_MARKER_DOT_SIZE / 2,
        backgroundColor: color,
        flexShrink: 0,
        overflow: "hidden",
      }}
    />
  );
}

function FertileWindowPredictionNotice({
  prediction,
  onDark,
}: {
  prediction: FertileWindowPrediction;
  onDark: boolean;
}) {
  const t = useTranslations();
  const { isDismissed: isNoticeDismissed, dismiss: dismissNotice } =
    useDismissible("care.calendar.fertile_window.insufficient");

  if (prediction.status === "not_applicable") return null;

  const hasEstimate = prediction.fertileDays.length > 0;

  // The educational "unavailable" state is dismissible; once dismissed it stays
  // hidden until there's enough logged history to show a real estimate.
  if (!hasEstimate && isNoticeDismissed) return null;

  const title = hasEstimate
    ? t("care.overview.calendar.prediction.title")
    : t("care.overview.calendar.prediction.insufficient_title");
  const body = hasEstimate
    ? t("care.overview.calendar.prediction.confidence", {
        params: {
          level: t(
            `care.overview.calendar.prediction.confidence_levels.${prediction.confidence}`,
          ),
        },
      })
    : t("care.overview.calendar.prediction.insufficient_body");

  return (
    <ActionBanner
      testID="care-fertile-window-prediction"
      variant="info"
      title={title}
      message={[
        body,
        ...prediction.warnings.map((warning) =>
          t(`care.overview.calendar.prediction.warnings.${warning}`),
        ),
        hasEstimate ? t("care.overview.calendar.prediction.disclaimer") : null,
      ]
        .filter(Boolean)
        .join("\n")}
      onDismiss={hasEstimate ? undefined : dismissNotice}
      dismissLabel={t("care.overview.calendar.prediction.dismiss_a11y")}
      dismissAction="care.overview.calendar.prediction.dismiss"
      onDark={onDark}
    />
  );
}

function CalendarDayCell({
  day,
  monthName,
  isoPrefix,
  selected,
  mode,
  onPress,
}: {
  day: CalendarDay;
  monthName: string;
  isoPrefix: string;
  selected: boolean;
  mode: CalendarMode;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("CalendarDayCell");
  const t = useTranslations();
  const isToday = day.state === "today";
  const isCheckInMode = mode === "check-in";
  const selectedColor = isCheckInMode
    ? calendarCheckInColors.foreground
    : ACCENT;
  const labelColor = isCheckInMode
    ? calendarCheckInColors.dayLabel
    : TEXT_LIGHT;
  const dateColor = isCheckInMode ? calendarCheckInColors.weekDate : TEXT;
  const dayLabel = translateWeekday(t, day.label);
  const defaultBg = isCheckInMode
    ? calendarCheckInColors.cellBackground
    : calendarViewColors.weekCellBackground;
  const selectedBg = isCheckInMode
    ? calendarCheckInColors.selectedCellBackground
    : calendarViewColors.selectedCellBackground;
  const selectedBorder = isCheckInMode
    ? calendarCheckInColors.selectedCellBorder
    : calendarViewColors.selectedCellBorder;
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            getCalendarDayAccessibilityLabel(t, day, monthName, isoPrefix),
          ),
          "calendar_day_cell",
        );
        onPress();
      }}
      testID={`care-calendar-day-${getCalendarDayIsoDate(day, isoPrefix)}${selected ? "-selected" : ""}`}
      accessibilityRole="button"
      accessibilityLabel={getCalendarDayAccessibilityLabel(
        t,
        day,
        monthName,
        isoPrefix,
      )}
      accessibilityState={{ selected }}
      style={{
        flex: 1,
        minHeight: 48,
        borderRadius: 9,
        borderWidth: selected ? 1.5 : 1,
        borderColor: selected ? selectedBorder : "transparent",
        backgroundColor: selected ? selectedBg : defaultBg,
        paddingVertical: 7,
        alignItems: "center",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: selected && isCheckInMode ? 5 : 0 },
        shadowOpacity: selected && isCheckInMode ? 0.14 : 0,
        shadowRadius: selected && isCheckInMode ? 10 : 0,
        opacity: day.state === "past" ? 0.66 : 1,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          lineHeight: 12,
          color: selected ? selectedColor : labelColor,
          fontWeight: selected || isToday ? "900" : "800",
        }}
      >
        {dayLabel}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 18,
          color: selected ? selectedColor : dateColor,
          fontWeight: "900",
          marginTop: 2,
        }}
      >
        {day.date}
      </Text>
      <View style={{ flexDirection: "row", gap: 3, marginTop: 4 }}>
        {day.markers.slice(0, 3).map((marker, index) => (
          <CalendarMarker
            key={`${day.date}-${marker}-${index}`}
            marker={marker}
          />
        ))}
      </View>
    </Pressable>
  );
}

function CalendarMonthCell({
  day,
  monthName,
  isoPrefix,
  selected,
  mode,
  onPress,
}: {
  day: CalendarDay | null;
  monthName: string;
  isoPrefix: string;
  selected: boolean;
  mode: CalendarMode;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("CalendarMonthCell");
  const t = useTranslations();
  if (!day) {
    return <View style={{ width: "13.4%", minHeight: 44 }} />;
  }

  const isToday = day.state === "today";
  const isCheckInMode = mode === "check-in";
  const selectedColor = isCheckInMode
    ? calendarCheckInColors.foreground
    : ACCENT;
  const dateColor = isCheckInMode ? calendarCheckInColors.monthDate : TEXT_MID;
  const defaultBg = isCheckInMode
    ? calendarCheckInColors.cellBackground
    : calendarViewColors.monthCellBackground;
  const selectedBg = isCheckInMode
    ? calendarCheckInColors.selectedCellBackground
    : calendarViewColors.selectedCellBackground;
  const selectedBorder = isCheckInMode
    ? calendarCheckInColors.selectedCellBorder
    : calendarViewColors.selectedCellBorder;
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            getCalendarDayAccessibilityLabel(t, day, monthName, isoPrefix),
          ),
          "calendar_month_cell",
        );
        onPress();
      }}
      testID={`care-calendar-day-${getCalendarDayIsoDate(day, isoPrefix)}${selected ? "-selected" : ""}`}
      accessibilityRole="button"
      accessibilityLabel={getCalendarDayAccessibilityLabel(
        t,
        day,
        monthName,
        isoPrefix,
      )}
      accessibilityState={{ selected }}
      style={{
        width: "13.4%",
        minHeight: 44,
        borderRadius: 8,
        borderWidth: selected ? 1.5 : 1,
        borderColor: selected ? selectedBorder : "transparent",
        backgroundColor: selected ? selectedBg : defaultBg,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: selected && isCheckInMode ? 4 : 0 },
        shadowOpacity: selected && isCheckInMode ? 0.12 : 0,
        shadowRadius: selected && isCheckInMode ? 8 : 0,
        opacity: day.state === "past" ? 0.62 : 1,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 15,
          color: selected ? selectedColor : dateColor,
          fontWeight: selected || isToday ? "900" : "700",
        }}
      >
        {day.date}
      </Text>
      <View style={{ flexDirection: "row", gap: 2.5, marginTop: 4 }}>
        {day.markers.slice(0, 3).map((marker, index) => (
          <CalendarMarker
            key={`${day.date}-${marker}-${index}`}
            marker={marker}
          />
        ))}
      </View>
    </Pressable>
  );
}

/**
 * The header label is a button, and this is what it opens. The arrows walk
 * a month at a time, which is fine for the month either side of today and
 * hopeless for a cycle two years back; this jumps straight there.
 */
function CalendarMonthPicker({
  visible,
  onClose,
  anchorISO,
  floor,
  ceiling,
  today,
  onSelect,
}: {
  visible: boolean;
  onClose: () => void;
  anchorISO: string;
  floor: Temporal.PlainDate;
  ceiling: Temporal.PlainDate;
  today: Temporal.PlainDate;
  onSelect: (year: number, month: number) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CalendarMonthPicker");
  const [year, setYear] = useState(
    () => Temporal.PlainDate.from(anchorISO).year,
  );

  // Reopening the picker should show the month the calendar is on now, not
  // the year the last visit wandered off to.
  useEffect(() => {
    if (visible) setYear(Temporal.PlainDate.from(anchorISO).year);
  }, [anchorISO, visible]);

  const firstYear = floor.year;
  const lastYear = ceiling.year;
  const canStepBack = year > firstYear;
  const canStepForward = year < lastYear;

  const isMonthReachable = (month: number) => {
    const candidate = Temporal.PlainDate.from({ year, month, day: 1 });
    if (Temporal.PlainDate.compare(candidate, floor) < 0) return false;
    return Temporal.PlainDate.compare(candidate, ceiling) <= 0;
  };

  const stepYear = (direction: -1 | 1) => {
    setYear((current) =>
      Math.min(lastYear, Math.max(firstYear, current + direction)),
    );
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      testID="care-calendar-month-picker"
    >
      <DialogHeader
        title={t("care.overview.calendar.jump.choose_a_month")}
        style={{ marginBottom: 16 }}
      />

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <CalendarNavButton
          label={t("care.overview.calendar.jump.previous_year_a11y")}
          disabled={!canStepBack}
          onPress={() => stepYear(-1)}
          icon={<ChevronLeft size={14} color={TEXT_LIGHT} strokeWidth={2} />}
        />
        <MetricValueText>{String(year)}</MetricValueText>
        <CalendarNavButton
          label={t("care.overview.calendar.jump.next_year_a11y")}
          disabled={!canStepForward}
          onPress={() => stepYear(1)}
          icon={<ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={2} />}
        />
      </View>

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "space-between",
          rowGap: 8,
        }}
      >
        {MONTH_NAMES.map((monthName, index) => {
          const month = index + 1;
          const reachable = isMonthReachable(month);
          // The picker should show where the calendar is standing. Today
          // gets a quieter mark so it stays findable after a long jump.
          const viewedAnchor = Temporal.PlainDate.from(anchorISO);
          const isViewed =
            year === viewedAnchor.year && month === viewedAnchor.month;
          const isThisMonth = year === today.year && month === today.month;
          const label = translateMonthName(t, monthName);
          return (
            <Pressable
              key={monthName}
              disabled={!reachable}
              accessibilityRole="button"
              accessibilityState={{ disabled: !reachable }}
              accessibilityLabel={`${label} ${year}`}
              onPress={() => {
                capturePress(
                  pressLabel(`${label} ${year}`),
                  "calendar_month_jump",
                );
                onSelect(year, month);
              }}
              style={{
                width: "31%",
                borderRadius: 9,
                paddingVertical: 10,
                alignItems: "center",
                opacity: reachable ? 1 : 0.35,
                backgroundColor: isViewed
                  ? withAlpha(ACCENT, 0.16)
                  : isThisMonth
                    ? semanticColors.accent.tint
                    : semanticColors.surface.muted,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 17,
                  color: isViewed || isThisMonth ? ACCENT : TEXT,
                  fontWeight: isViewed ? "900" : "700",
                }}
              >
                {label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <TextActionButton
        label={t("care.overview.calendar.jump.this_month")}
        action="calendar_month_jump_today"
        size="sm"
        centered
        onPress={() => onSelect(today.year, today.month)}
        style={{ marginTop: 12 }}
      />
    </Dialog>
  );
}

export function CalendarOverviewCard({
  cycleId,
  onOpenMedicationFactSheet,
  onOpenAppointment,
  onOpenProcedureFactSheet,
  onRequestDocumentAttachment,
  onUploadMonitoringReport,
  onLoggingOverlayChange,
  onAddMedication,
  onAddAppointment,
  initialAppointmentId,
  initialCalendarState,
  onCalendarStateChange,
  showStickyBackdrop = false,
  showFixtureData = false,
}: {
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenAppointment: (appointment: AppointmentDetailItem) => void;
  onOpenProcedureFactSheet: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  onUploadMonitoringReport?: MonitoringUploadRequest;
  cycleId?: string | null;
  onLoggingOverlayChange?: (state: CalendarSymptomOverlayState | null) => void;
  onAddMedication: (dateISO?: string) => void;
  onAddAppointment: (dateISO?: string) => void;
  initialAppointmentId?: string;
  initialCalendarState?: CareCalendarRouteParams;
  onCalendarStateChange?: (state: CareCalendarRouteParams) => void;
  showStickyBackdrop?: boolean;
  showFixtureData?: boolean;
}) {
  const capturePress = usePressCapture("CalendarOverviewCard");
  const t = useTranslations();
  const { showToast } = useToast();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const { tenantId, patientId } = useApiClient();
  const today = useMemo(() => Temporal.Now.plainDateISO(), []);
  const { data: patient } = usePatient();

  // ---- Where the calendar is standing ------------------------------------
  // Two anchors place it: the Monday the visible week starts on, and the
  // 1st of the visible month. They are resolved here, above the clinical
  // queries, because the queries follow them — the screen asks the API for
  // the month being looked at rather than for a fixed span around today.

  // How far back the arrows and the month picker reach. There is nothing to
  // show before the patient was born, and with no floor at all the arrows
  // would walk into empty centuries. A patient whose date of birth we do
  // not hold falls back to a lifetime rather than to no bound — the arrows
  // and the picker then agree, instead of the picker guessing a limit the
  // arrows ignored.
  const calendarFloor = useMemo(() => {
    const lifetime = today.with({ day: 1 }).subtract({ years: 120 });
    const isoDay = patient?.dateOfBirth?.slice(0, 10);
    if (!isoDay || !/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) return lifetime;
    try {
      return Temporal.PlainDate.from(isoDay).with({ day: 1 });
    } catch {
      return lifetime;
    }
  }, [patient?.dateOfBirth, today]);

  // Two years ahead covers anything that can actually be booked, and keeps
  // the picker's year list finite.
  const calendarCeiling = useMemo(
    () => today.with({ day: 1 }).add({ years: 2 }),
    [today],
  );

  // The reachable range, as the two anchors see it.
  const earliestWeekAnchor = useMemo(
    () => startOfWeek(calendarFloor),
    [calendarFloor],
  );
  const latestWeekAnchor = useMemo(
    () =>
      startOfWeek(calendarCeiling.with({ day: calendarCeiling.daysInMonth })),
    [calendarCeiling],
  );

  const clampMonthAnchor = useCallback(
    (monthStart: Temporal.PlainDate) => {
      if (Temporal.PlainDate.compare(monthStart, calendarFloor) < 0) {
        return calendarFloor;
      }
      if (Temporal.PlainDate.compare(monthStart, calendarCeiling) > 0) {
        return calendarCeiling;
      }
      return monthStart;
    },
    [calendarCeiling, calendarFloor],
  );

  const clampWeekAnchor = useCallback(
    (weekStart: Temporal.PlainDate) => {
      if (Temporal.PlainDate.compare(weekStart, earliestWeekAnchor) < 0) {
        return earliestWeekAnchor;
      }
      if (Temporal.PlainDate.compare(weekStart, latestWeekAnchor) > 0) {
        return latestWeekAnchor;
      }
      return weekStart;
    },
    [earliestWeekAnchor, latestWeekAnchor],
  );

  // Where the week and month pages have to sit for a given date to be on
  // screen. Runtime anchors on the date itself; fixture mode can only land
  // on a page its curated array actually holds, so it answers null when the
  // date is outside the demo.
  const anchorsForDate = useCallback(
    (
      dateISO: string,
    ): { weekAnchorISO: string | null; monthAnchorISO: string | null } => {
      if (!showFixtureData) {
        let date: Temporal.PlainDate;
        try {
          date = Temporal.PlainDate.from(dateISO);
        } catch {
          return { weekAnchorISO: null, monthAnchorISO: null };
        }
        return {
          weekAnchorISO: clampWeekAnchor(startOfWeek(date)).toString(),
          monthAnchorISO: clampMonthAnchor(date.with({ day: 1 })).toString(),
        };
      }
      const weekPage = calendarWeekPages.find((page) =>
        page.days.some(
          (day) => getCalendarDayIsoDate(day, page.isoPrefix) === dateISO,
        ),
      );
      const monthPage = calendarMonthPages.find((page) =>
        page.cells.some(
          (day) =>
            day !== null &&
            getCalendarDayIsoDate(day, page.isoPrefix) === dateISO,
        ),
      );
      return {
        weekAnchorISO: weekPage ? weekPageAnchor(weekPage) : null,
        monthAnchorISO: monthPage ? monthPageAnchor(monthPage) : null,
      };
    },
    [clampMonthAnchor, clampWeekAnchor, showFixtureData],
  );

  const defaultSelectedDate = showFixtureData ? "19" : String(today.day);

  const defaultWeekAnchorISO = useMemo(() => {
    if (!showFixtureData) return startOfWeek(today).toString();
    const index = findCalendarPageIndex(
      calendarWeekPages,
      (page) => page.days.some((day) => day.state === "today"),
      1,
    );
    return weekPageAnchor(calendarWeekPages[index] ?? calendarWeekPages[0]!);
  }, [showFixtureData, today]);

  const defaultMonthAnchorISO = useMemo(() => {
    if (!showFixtureData) return today.with({ day: 1 }).toString();
    const index = findCalendarPageIndex(
      calendarMonthPages,
      (page) => page.cells.some((day) => day?.state === "today"),
      1,
    );
    return monthPageAnchor(calendarMonthPages[index] ?? calendarMonthPages[0]!);
  }, [showFixtureData, today]);

  const defaultSelectedDateISO = useMemo(() => {
    if (!showFixtureData) return today.toString();
    const page =
      calendarWeekPages.find(
        (candidate) => weekPageAnchor(candidate) === defaultWeekAnchorISO,
      ) ?? calendarWeekPages[0];
    if (!page) return today.toString();
    const day = page.days.find(
      (candidate) => candidate.date === defaultSelectedDate,
    );
    return day
      ? getCalendarDayIsoDate(day, page.isoPrefix)
      : getCalendarDateKey(page.isoPrefix, defaultSelectedDate);
  }, [defaultSelectedDate, defaultWeekAnchorISO, showFixtureData, today]);

  const resolvedInitialCalendarState = useMemo(() => {
    const selectedDateISO = isCalendarRouteDate(initialCalendarState?.day)
      ? initialCalendarState.day
      : defaultSelectedDateISO;
    const viewMode: "week" | "month" =
      initialCalendarState?.view === "month" ? "month" : "week";
    const calendarMode: CalendarMode =
      initialCalendarState?.mode === "check-in" ? "check-in" : "view";
    const dateLogTab: DateLogTab =
      initialCalendarState?.logTab === "monitoring" ||
      initialCalendarState?.logTab === "schedule"
        ? initialCalendarState.logTab
        : "symptoms";
    const anchors = anchorsForDate(selectedDateISO);

    return {
      selectedDateISO,
      viewMode,
      calendarMode,
      dateLogTab,
      weekAnchorISO: anchors.weekAnchorISO ?? defaultWeekAnchorISO,
      monthAnchorISO: anchors.monthAnchorISO ?? defaultMonthAnchorISO,
    };
  }, [
    anchorsForDate,
    defaultMonthAnchorISO,
    defaultSelectedDateISO,
    defaultWeekAnchorISO,
    initialCalendarState,
  ]);

  const [viewMode, setViewMode] = useState<"week" | "month">(
    resolvedInitialCalendarState.viewMode,
  );
  const [weekAnchorISO, setWeekAnchorISO] = useState(
    resolvedInitialCalendarState.weekAnchorISO,
  );
  const [monthAnchorISO, setMonthAnchorISO] = useState(
    resolvedInitialCalendarState.monthAnchorISO,
  );
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);

  // The floor is only real once the patient record loads, so anchors
  // resolved before that — from a route parameter, say — can sit outside
  // it. Pull them back when the range moves under them rather than leaving
  // the arrows pointing at a month the calendar will not show.
  useEffect(() => {
    if (showFixtureData) return;
    setMonthAnchorISO((current) =>
      clampMonthAnchor(Temporal.PlainDate.from(current)).toString(),
    );
    setWeekAnchorISO((current) =>
      clampWeekAnchor(Temporal.PlainDate.from(current)).toString(),
    );
  }, [clampMonthAnchor, clampWeekAnchor, showFixtureData]);

  // Real calendar data — without this wiring the grid would render with
  // empty `markers: []` arrays and the agenda would always be empty even
  // after the user added meds/appointments (UAT issue #35).
  //
  // The window follows the anchors instead of spanning every month the
  // grid can reach. Now that the grid can reach any month of the patient's
  // life, a window that covered all of them would be a years-long fetch on
  // every open; a window that covers the visible month is one month at
  // open and one more when she asks for an older one. The month is padded
  // a week each side so a week page straddling two months is still whole,
  // and the patient-scoped GET returns 400 without an explicit range.
  const calendarFetchWindow = useMemo(() => {
    const anchor = Temporal.PlainDate.from(
      viewMode === "week" ? weekAnchorISO : monthAnchorISO,
    ).with({ day: 1 });
    return {
      startDate: anchor.subtract({ days: 7 }).toString(),
      endDate: anchor.add({ months: 1 }).add({ days: 6 }).toString(),
    };
  }, [monthAnchorISO, viewMode, weekAnchorISO]);
  const medicationsQuery = usePatientMedications({
    window: {
      from: calendarFetchWindow.startDate,
      to: calendarFetchWindow.endDate,
    },
  });
  const apiMedications = useMemo(
    () => medicationsQuery.data ?? [],
    [medicationsQuery.data],
  );
  const appointmentsQuery = useAppointments({
    startDate: calendarFetchWindow.startDate,
    endDate: calendarFetchWindow.endDate,
    sort: "asc",
    sortBy: "startDate",
  });
  const apiAppointments = useMemo(
    () => appointmentsQuery.data ?? [],
    [appointmentsQuery.data],
  );
  // A push notification hands the screen an appointment id and no date, so
  // the visible month is not enough to find it. This wider read is the
  // on-demand cost of arriving that way: it only runs when an id came in,
  // and never on an ordinary open.
  const notifiedAppointmentsQuery = useAppointments({
    startDate: today.subtract({ months: 3 }).toString(),
    endDate: today.add({ months: 12 }).toString(),
    sort: "asc",
    sortBy: "startDate",
    enabled: Boolean(initialAppointmentId),
  });
  const appointmentsForNotifiedLookup =
    notifiedAppointmentsQuery.data ?? apiAppointments;
  const openedInitialAppointmentId = useRef<string | null>(null);
  useEffect(() => {
    if (
      !initialAppointmentId ||
      openedInitialAppointmentId.current === initialAppointmentId
    ) {
      return;
    }
    const appointment = appointmentsForNotifiedLookup.find(
      (item) => item.id === initialAppointmentId,
    );
    if (!appointment) return;

    openedInitialAppointmentId.current = initialAppointmentId;
    const fallbackDate = appointmentDateForEdit(
      appointment.startDate,
      Temporal.Now.plainDateISO().toString(),
    );
    onOpenAppointment(
      buildAppointmentDetailItem({
        row: {
          id: `notification-${appointment.id}`,
          time: appointment.startTimeLocal ?? appointment.startTime ?? "",
          title: appointment.title ?? "Appointment",
          detail: appointment.meetingRoom ?? "",
          icon: "process",
          kind: "appointment",
          appointmentId: appointment.id,
        },
        appointment,
        fallbackDate,
      }),
    );
  }, [appointmentsForNotifiedLookup, initialAppointmentId, onOpenAppointment]);
  const testResultsQuery = useTestResults();
  const apiTestResults = useMemo(
    () => testResultsQuery.data ?? [],
    [testResultsQuery.data],
  );
  // Symptoms across the visible runtime range (~14 weeks). useSymptoms
  // returns the patient's recent log; we only need it for marker dots.
  const symptomsQuery = useSymptoms({
    enabled: !showFixtureData,
  });
  const apiSymptoms = useMemo(
    () => symptomsQuery.data ?? [],
    [symptomsQuery.data],
  );

  // The period log the calendar's own logging writes to. Without it the
  // estimate could only ever see the three dates typed into medical history,
  // so a patient who logged every period on this very screen still got told
  // there was not enough history.
  const { data: periodLogs } = usePeriodLogs({ enabled: !showFixtureData });
  // Both records of a bleeding day the app can actually produce: the period
  // log, and the Care check-in, which stores its bleeding levels as symptom
  // rows because no period-log write exists on this screen. Spotting is
  // excluded upstream — see `menstrualBleeding`.
  const loggedPeriodDays = useMemo(
    () => [
      ...(periodLogs ?? []).map((log) => log.date),
      ...periodDaysFromBleedingSymptoms(apiSymptoms),
    ],
    [apiSymptoms, periodLogs],
  );
  const fertileWindowCalendarData = useMemo(() => {
    if (showFixtureData || !patient) return null;
    const menstrualHealth = patient.obGynHistory?.menstrualHealth;
    return buildFertileWindowCalendarData({
      loggedPeriodDays,
      periodRanges: [
        {
          start: menstrualHealth?.period1StartDate,
          end: menstrualHealth?.period1EndDate,
        },
        {
          start: menstrualHealth?.period2StartDate,
          end: menstrualHealth?.period2EndDate,
        },
        {
          start: menstrualHealth?.period3StartDate,
          end: menstrualHealth?.period3EndDate,
        },
      ],
      dateOfBirth: patient.dateOfBirth,
      declaredCycleLengthDays: menstrualHealth
        ? {
            min: menstrualHealth.minAverageCycleLength ?? null,
            max: menstrualHealth.maxAverageCycleLength ?? null,
          }
        : null,
      today: today.toString(),
    });
  }, [loggedPeriodDays, patient, showFixtureData, today]);
  const devClinicalDataState = getDevClinicalDataState("calendar");
  const devCalendarLoading = isDevClinicalDataLoading("calendar");
  const devScheduleDataState = getDevClinicalDataState("calendar-schedule");
  const devScheduleLoading = isDevClinicalDataLoading("calendar-schedule");
  const calendarQueries = [
    medicationsQuery,
    appointmentsQuery,
    testResultsQuery,
    symptomsQuery,
  ];
  // The calendar shell stays available while each clinical surface reports
  // its own read state. A schedule failure must not become an empty Meds &
  // visits day or block the Symptoms and Monitoring tabs.
  const calendarDataState: CalendarDataState = devClinicalDataState
    ? devClinicalDataState
    : devCalendarLoading
      ? "loading"
      : null;
  const isRetryingCalendar = calendarQueries.some((query) => query.isFetching);
  const retryCalendar = () => {
    void medicationsQuery.refetch();
    void appointmentsQuery.refetch();
    void testResultsQuery.refetch();
    void symptomsQuery.refetch();
  };

  const scheduleQueries = [medicationsQuery, appointmentsQuery];
  const scheduleError = scheduleQueries.find((query) => query.error)?.error;
  const hasResolvedScheduleSource = scheduleQueries.some(
    (query) => query.data !== undefined,
  );
  const scheduleDataState: ScheduleDataState = devScheduleDataState
    ? devScheduleDataState
    : devScheduleLoading
      ? "loading"
      : showFixtureData
        ? null
        : scheduleError
          ? hasResolvedScheduleSource
            ? "stale"
            : "unavailable"
          : // `keepPreviousData` holds the last month's list while the next
            // one loads, which keeps `isLoading` false. Without this the
            // agenda would read that as loaded and tell her nothing is
            // scheduled for a day whose appointments are still in flight.
            scheduleQueries.some(
                (query) => query.isLoading || query.isPlaceholderData,
              )
            ? "loading"
            : null;
  const isRetryingSchedule = scheduleQueries.some((query) => query.isFetching);
  const retrySchedule = () => {
    void medicationsQuery.refetch();
    void appointmentsQuery.refetch();
  };

  const apiMarkersByDate = useMemo(
    () =>
      buildMarkersByDate({
        medications: apiMedications,
        appointments: apiAppointments,
        testResults: apiTestResults,
        symptoms: apiSymptoms,
        startDate: calendarFetchWindow.startDate,
        endDate: calendarFetchWindow.endDate,
        medicationDoseFallback: t("home.take_as_prescribed"),
        t,
        locale,
      }),
    [
      apiMedications,
      apiAppointments,
      apiTestResults,
      apiSymptoms,
      calendarFetchWindow.startDate,
      calendarFetchWindow.endDate,
      t,
      locale,
    ],
  );

  const apiRowsByDate = useMemo(
    () =>
      buildTodayRowsByDate({
        medications: apiMedications,
        appointments: apiAppointments,
        testResults: apiTestResults,
        symptoms: apiSymptoms,
        startDate: calendarFetchWindow.startDate,
        endDate: calendarFetchWindow.endDate,
        medicationDoseFallback: t("home.take_as_prescribed"),
        t,
        locale,
      }),
    [
      apiMedications,
      apiAppointments,
      apiTestResults,
      apiSymptoms,
      calendarFetchWindow.startDate,
      calendarFetchWindow.endDate,
      t,
      locale,
    ],
  );

  // Overlay markers onto the visible page so each day reflects what the
  // patient has actually saved. Fixture mode keeps the hand-tuned demo
  // arrays.
  const decorateCalendarDay = useCallback(
    (day: CalendarDay | null): CalendarDay | null => {
      if (!day) return null;
      const apiMarkers = day.isoDate
        ? (apiMarkersByDate.get(day.isoDate) ?? [])
        : [];
      const predictionMarkers = day.isoDate
        ? (fertileWindowCalendarData?.markersByDate.get(day.isoDate) ?? [])
        : [];
      const next = [...apiMarkers, ...predictionMarkers];
      if (next.length === 0) return day;
      const merged: ConceptDayMarker[] = [...day.markers];
      for (const marker of next) {
        if (!merged.includes(marker)) merged.push(marker);
      }
      return { ...day, markers: merged };
    },
    [apiMarkersByDate, fertileWindowCalendarData],
  );

  // Fixture mode still pages through its curated array, so it keeps an
  // index; runtime has none, because the page is built from the anchor.
  const fixtureWeekPageIndex = showFixtureData
    ? calendarWeekPages.findIndex(
        (page) => weekPageAnchor(page) === weekAnchorISO,
      )
    : -1;
  const fixtureMonthPageIndex = showFixtureData
    ? calendarMonthPages.findIndex(
        (page) => monthPageAnchor(page) === monthAnchorISO,
      )
    : -1;

  const [selectedDateISO, setSelectedDateISO] = useState(
    resolvedInitialCalendarState.selectedDateISO,
  );
  const [calendarMode, setCalendarMode] = useState<CalendarMode>(
    resolvedInitialCalendarState.calendarMode,
  );
  const [showAllSymptoms, setShowAllSymptoms] = useState(false);
  const [dateLogTab, setDateLogTab] = useState<DateLogTab>(
    resolvedInitialCalendarState.dateLogTab,
  );
  const [savedCheckIns, setSavedCheckIns] = useState<
    Record<string, DailyCheckInDraft>
  >({});
  const [draftCheckIns, setDraftCheckIns] = useState<
    Record<string, DailyCheckInDraft>
  >({});
  const [failedCheckInDates, setFailedCheckInDates] = useState<
    Record<string, boolean>
  >({});

  const hasInitializedCalendarState = useRef(false);
  useEffect(() => {
    if (!hasInitializedCalendarState.current) {
      hasInitializedCalendarState.current = true;
      return;
    }
    // Keep the calendar on the page that actually contains today. Fixture mode
    // can still use its curated default page, while runtime mode lands on the
    // current week/month.
    setWeekAnchorISO(defaultWeekAnchorISO);
    setMonthAnchorISO(defaultMonthAnchorISO);
    setSelectedDateISO(defaultSelectedDateISO);
  }, [defaultMonthAnchorISO, defaultSelectedDateISO, defaultWeekAnchorISO]);

  useEffect(() => {
    onCalendarStateChange?.({
      day: selectedDateISO,
      view: viewMode,
      mode: calendarMode,
      logTab: dateLogTab,
    });
  }, [
    calendarMode,
    dateLogTab,
    onCalendarStateChange,
    selectedDateISO,
    viewMode,
  ]);

  const visibleWeek = useMemo(() => {
    if (showFixtureData) {
      return calendarWeekPages[fixtureWeekPageIndex] ?? calendarWeekPages[1]!;
    }
    const page = buildRuntimeWeekPage(
      Temporal.PlainDate.from(weekAnchorISO),
      today,
    );
    return {
      ...page,
      days: page.days.map((day) => decorateCalendarDay(day) as CalendarDay),
    };
  }, [
    decorateCalendarDay,
    fixtureWeekPageIndex,
    showFixtureData,
    today,
    weekAnchorISO,
  ]);
  const visibleMonth = useMemo(() => {
    if (showFixtureData) {
      return (
        calendarMonthPages[fixtureMonthPageIndex] ?? calendarMonthPages[1]!
      );
    }
    const page = buildRuntimeMonthPage(
      Temporal.PlainDate.from(monthAnchorISO),
      today,
    );
    return { ...page, cells: page.cells.map(decorateCalendarDay) };
  }, [
    decorateCalendarDay,
    fixtureMonthPageIndex,
    monthAnchorISO,
    showFixtureData,
    today,
  ]);
  const selectedDay =
    (viewMode === "week"
      ? visibleWeek.days.find(
          (day) =>
            getCalendarDayIsoDate(day, visibleWeek.isoPrefix) ===
            selectedDateISO,
        )
      : visibleMonth.cells.find(
          (day) =>
            day &&
            getCalendarDayIsoDate(day, visibleMonth.isoPrefix) ===
              selectedDateISO,
        )) ?? null;
  const hasSelectedDay = selectedDay !== null;
  const selectedPage = viewMode === "month" ? visibleMonth : visibleWeek;
  const selectedMonthName =
    selectedDay?.monthName ?? selectedPage.monthName ?? "May";
  const selectedDateKey = selectedDateISO;
  const selectedIsoDate = selectedDateISO;
  const isCheckInMode = calendarMode === "check-in";
  const selectedRows = showFixtureData
    ? !selectedDay || (viewMode === "month" && fixtureMonthPageIndex !== 1)
      ? []
      : (calendarRowsByDate[selectedDay.date] ?? [])
    : (apiRowsByDate.get(selectedIsoDate) ?? []);
  const openAppointmentRow = useCallback(
    (row: TodayRow) => {
      const appointment = row.appointmentId
        ? apiAppointments.find((item) => item.id === row.appointmentId)
        : undefined;
      onOpenAppointment(
        buildAppointmentDetailItem({
          row,
          appointment,
          fallbackDate: selectedIsoDate,
        }),
      );
    },
    [apiAppointments, onOpenAppointment, selectedIsoDate],
  );
  const canLogSelectedDay = Boolean(
    selectedDay && selectedDay.state !== "upcoming",
  );
  const persistedSymptomsQuery = useSymptoms({
    date: selectedIsoDate,
    enabled: canLogSelectedDay,
  });
  const {
    data: persistedSymptoms,
    error: persistedSymptomsError,
    isFetching: isFetchingPersistedSymptoms,
    isLoading: isLoadingPersistedSymptoms,
    refetch: refetchPersistedSymptoms,
  } = persistedSymptomsQuery;
  /**
   * Destructured, not held as objects: `useMutation` returns a fresh
   * `{ ...result, mutate, mutateAsync }` on every render, so a callback that
   * depended on the mutation itself was rebuilt on every render. `mutateAsync`
   * is the observer's bound method and the observer is created once, so it is
   * stable; `isPending` is a value that must stay reactive.
   */
  const { mutateAsync: createSymptomsAsync, isPending: isCreatingSymptoms } =
    useCreateSymptoms();
  const { mutateAsync: deleteSymptomAsync, isPending: isDeletingSymptom } =
    useDeleteSymptom();
  const patientNotesQuery = usePatientNotes();
  const {
    data: patientNotes,
    error: patientNotesError,
    isFetching: isFetchingPatientNotes,
    isLoading: isLoadingPatientNotes,
    refetch: refetchPatientNotes,
  } = patientNotesQuery;
  const { mutateAsync: createNoteAsync, isPending: isCreatingNote } =
    useCreatePatientNote();
  const { mutateAsync: updateNoteAsync, isPending: isUpdatingNote } =
    useUpdatePatientNote();
  const { mutateAsync: deleteNoteAsync } = useDeletePatientNote();
  const selectedNote = useMemo(
    () =>
      patientNotes?.find(
        // Her words about a pregnancy that ended are their own record, not
        // the day's check-in note.
        (note) =>
          note.date === selectedIsoDate && !isPregnancyDetailsNote(note.text),
      ),
    [patientNotes, selectedIsoDate],
  );
  const persistedCheckIn = useMemo(
    () =>
      buildDailyCheckInFromRecords(
        persistedSymptoms as PersistedSymptomRecord[] | undefined,
        selectedNote?.text,
      ),
    [persistedSymptoms, selectedNote?.text],
  );
  const savedCheckIn = selectedDay
    ? (savedCheckIns[selectedDateKey] ?? persistedCheckIn)
    : undefined;
  const activeDraft = selectedDay
    ? (draftCheckIns[selectedDateKey] ?? savedCheckIn ?? emptyDailyCheckIn)
    : emptyDailyCheckIn;
  const savedCheckInForEditor =
    Boolean(selectedDay && savedCheckIn) &&
    !failedCheckInDates[selectedDateKey];
  const selectedCheckInDataState: SelectedCheckInDataState = !canLogSelectedDay
    ? null
    : persistedSymptomsError || patientNotesError
      ? "unavailable"
      : isLoadingPersistedSymptoms || isLoadingPatientNotes
        ? "loading"
        : null;
  const isRetryingSelectedCheckIn =
    isFetchingPersistedSymptoms || isFetchingPatientNotes;
  const isPersistingCheckIn =
    isCreatingSymptoms || isDeletingSymptom || isCreatingNote || isUpdatingNote;
  const canSaveCheckIn =
    calendarMode === "check-in" &&
    canLogSelectedDay &&
    selectedCheckInDataState === null &&
    !isPersistingCheckIn;
  const calendarLabel =
    viewMode === "week" ? visibleWeek.label : visibleMonth.label;
  // One step of the previous/next arrow. Runtime steps the anchor and lets
  // the range clamp decide how far it may go; fixture mode still steps an
  // index inside its curated array.
  const steppedWeekAnchorISO = (direction: -1 | 1) => {
    if (showFixtureData) {
      const index = Math.max(
        0,
        Math.min(
          calendarWeekPages.length - 1,
          Math.max(fixtureWeekPageIndex, 0) + direction,
        ),
      );
      return weekPageAnchor(calendarWeekPages[index] ?? calendarWeekPages[0]!);
    }
    return clampWeekAnchor(
      Temporal.PlainDate.from(weekAnchorISO).add({ days: 7 * direction }),
    ).toString();
  };

  const steppedMonthAnchorISO = (direction: -1 | 1) => {
    if (showFixtureData) {
      const index = Math.max(
        0,
        Math.min(
          calendarMonthPages.length - 1,
          Math.max(fixtureMonthPageIndex, 0) + direction,
        ),
      );
      return monthPageAnchor(
        calendarMonthPages[index] ?? calendarMonthPages[0]!,
      );
    }
    return clampMonthAnchor(
      Temporal.PlainDate.from(monthAnchorISO).add({ months: direction }),
    ).toString();
  };

  // Boundaries are read off the range directly. "Stepping would not move the
  // anchor" looked equivalent and is not: an anchor sitting outside the range
  // — a deep link to 1900, or a saved date of birth that raises the floor
  // under a patient already browsing below it — clamps back into range in
  // BOTH directions, which enabled the previous arrow and made it jump
  // forward.
  const isAtPreviousBoundary = showFixtureData
    ? viewMode === "week"
      ? Math.max(fixtureWeekPageIndex, 0) === 0
      : Math.max(fixtureMonthPageIndex, 0) === 0
    : viewMode === "week"
      ? Temporal.PlainDate.compare(
          Temporal.PlainDate.from(weekAnchorISO),
          earliestWeekAnchor,
        ) <= 0
      : Temporal.PlainDate.compare(
          Temporal.PlainDate.from(monthAnchorISO),
          calendarFloor,
        ) <= 0;
  const isAtNextBoundary = showFixtureData
    ? viewMode === "week"
      ? Math.max(fixtureWeekPageIndex, 0) === calendarWeekPages.length - 1
      : Math.max(fixtureMonthPageIndex, 0) === calendarMonthPages.length - 1
    : viewMode === "week"
      ? Temporal.PlainDate.compare(
          Temporal.PlainDate.from(weekAnchorISO),
          latestWeekAnchor,
        ) >= 0
      : Temporal.PlainDate.compare(
          Temporal.PlainDate.from(monthAnchorISO),
          calendarCeiling,
        ) >= 0;

  const switchCalendarView = (nextMode: "week" | "month") => {
    const anchors = anchorsForDate(selectedDateISO);
    if (nextMode === "week") {
      if (anchors.weekAnchorISO) setWeekAnchorISO(anchors.weekAnchorISO);
    } else if (anchors.monthAnchorISO) {
      setMonthAnchorISO(anchors.monthAnchorISO);
    }
    setViewMode(nextMode);
  };

  const navigateCalendar = (direction: -1 | 1) => {
    // Navigating months/weeks used to auto-select the "default" day of
    // the target page (the 1st for future months). That made the 1st
    // look like the actively-selected day everywhere the user hadn't
    // touched yet. Instead, keep whatever the user last selected —
    // selection stays user-driven, and the newly-navigated month
    // shows no selected cell if the last tap isn't in that month.
    if (viewMode === "week") {
      const nextWeek = steppedWeekAnchorISO(direction);
      setWeekAnchorISO(nextWeek);
      // The month anchor travels with the week. It is what the picker opens
      // on and what the fetch window is cut from, so leaving it behind put
      // the header in one year and the picker in another.
      if (!showFixtureData) {
        setMonthAnchorISO(
          clampMonthAnchor(
            Temporal.PlainDate.from(nextWeek).with({ day: 1 }),
          ).toString(),
        );
      }
      return;
    }
    setMonthAnchorISO(steppedMonthAnchorISO(direction));
  };

  // The month picker behind the header label: the arrows walk, this jumps.
  const jumpToMonth = (year: number, month: number) => {
    const target = clampMonthAnchor(
      Temporal.PlainDate.from({ year, month, day: 1 }),
    );
    const isThisMonth = target.equals(today.with({ day: 1 }));
    setMonthAnchorISO(target.toString());
    // Week view has to land on a week that is actually in the month asked
    // for; and asking for this month means "take me back to today", which
    // is a week and a selected day, not just a grid position.
    setWeekAnchorISO(
      clampWeekAnchor(
        isThisMonth ? startOfWeek(today) : firstWholeWeekOfMonth(target),
      ).toString(),
    );
    if (isThisMonth) setSelectedDateISO(today.toString());
    setIsMonthPickerOpen(false);
  };

  const getDisplayDay = (
    day: CalendarDay,
    page: { isoPrefix: string },
  ): CalendarDay => {
    // `day.markers` is already populated by `decorateCalendarDay` from
    // the patient's API data — don't blank it back to `[]` here.
    const baseDay = day;
    const dateKey =
      day.isoDate ??
      getCalendarDateKey(day.isoPrefix ?? page.isoPrefix, day.date);
    const checkIn =
      draftCheckIns[dateKey] ??
      savedCheckIns[dateKey] ??
      (dateKey === selectedDateKey ? persistedCheckIn : undefined);
    if (
      !checkIn ||
      !hasBleedingSelection(checkIn.bleeding) ||
      baseDay.markers.includes("period")
    ) {
      return baseDay;
    }
    return { ...baseDay, markers: [...baseDay.markers, "period"] };
  };

  const updateActiveDraft = useCallback(
    (next: DailyCheckInDraft) => {
      setDraftCheckIns((previous) => ({
        ...previous,
        [selectedDateKey]: next,
      }));
    },
    [selectedDateKey],
  );

  const refreshCheckInSources = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: calendarKeys.patient(tenantId ?? "", patientId ?? ""),
      }),
      queryClient.invalidateQueries({
        queryKey: patientNoteKeys.list(tenantId ?? "", patientId ?? ""),
      }),
    ]);
  }, [patientId, queryClient, tenantId]);

  const retrySelectedCheckIn = useCallback(() => {
    void refetchPersistedSymptoms();
    void refetchPatientNotes();
  }, [refetchPatientNotes, refetchPersistedSymptoms]);

  const saveActiveCheckIn = useCallback(async () => {
    if (!canSaveCheckIn) return;

    setFailedCheckInDates((previous) => {
      if (!previous[selectedDateKey]) return previous;
      const next = { ...previous };
      delete next[selectedDateKey];
      return next;
    });

    const targetLabels = getDraftSymptomLabels(activeDraft);
    const targetLabelSet = new Set(targetLabels);
    const existingByLabel = new Map<string, string>();
    for (const record of (persistedSymptoms ??
      []) as PersistedSymptomRecord[]) {
      if (record.symptom && !existingByLabel.has(record.symptom)) {
        existingByLabel.set(record.symptom, record.id);
      }
    }

    const labelsToCreate = targetLabels.filter(
      (label) => !existingByLabel.has(label),
    );
    const symptomIdsToDelete = [...existingByLabel.entries()]
      .filter(
        ([label]) =>
          CHECK_IN_SYMPTOM_LABELS.has(label) && !targetLabelSet.has(label),
      )
      .map(([, id]) => id);
    const notesText = activeDraft.notes.trim();

    try {
      const symptomWrites = await Promise.allSettled([
        ...symptomIdsToDelete.map((id) => deleteSymptomAsync(id)),
        labelsToCreate.length > 0
          ? createSymptomsAsync({
              date: selectedIsoDate,
              symptoms: labelsToCreate,
              notes: notesText || undefined,
            })
          : Promise.resolve(),
      ]);

      const failedSymptomWrite = symptomWrites.find(
        (result) => result.status === "rejected",
      );
      if (failedSymptomWrite?.status === "rejected") {
        throw failedSymptomWrite.reason;
      }

      if (selectedNote) {
        if (!notesText) {
          // Clearing the note is a delete, not an update-to-empty — the API
          // supports the full lifecycle and an empty row would linger in
          // every notes read (#2244).
          await deleteNoteAsync(selectedNote.id);
        } else if (notesText !== selectedNote.text) {
          await updateNoteAsync({
            noteId: selectedNote.id,
            text: notesText,
          });
        }
      } else if (notesText) {
        await createNoteAsync({
          date: selectedIsoDate,
          text: notesText,
          cycleId: cycleId ?? null,
        });
      }
    } catch (error) {
      void refreshCheckInSources().catch(() => undefined);
      setFailedCheckInDates((previous) => ({
        ...previous,
        [selectedDateKey]: true,
      }));
      console.error("[Care] Failed to save calendar check-in", error);
      showToast({
        variant: "error",
        message: t("care.overview.calendar.check_in.save_failed"),
      });
      return;
    }

    await refreshCheckInSources().catch(() => undefined);
    setSavedCheckIns((previous) => ({
      ...previous,
      [selectedDateKey]: activeDraft,
    }));
    setDraftCheckIns((previous) => {
      const next = { ...previous };
      delete next[selectedDateKey];
      return next;
    });
    setCalendarMode("view");
  }, [
    activeDraft,
    canSaveCheckIn,
    createNoteAsync,
    createSymptomsAsync,
    cycleId,
    deleteNoteAsync,
    deleteSymptomAsync,
    persistedSymptoms,
    selectedDateKey,
    selectedIsoDate,
    selectedNote,
    refreshCheckInSources,
    showToast,
    t,
    updateNoteAsync,
  ]);

  const cancelActiveCheckIn = useCallback(() => {
    setFailedCheckInDates((previous) => {
      if (!previous[selectedDateKey]) return previous;
      const next = { ...previous };
      delete next[selectedDateKey];
      return next;
    });
    setDraftCheckIns((previous) => ({
      ...previous,
      [selectedDateKey]: savedCheckIn ?? emptyDailyCheckIn,
    }));
    setCalendarMode("view");
  }, [savedCheckIn, selectedDateKey]);

  const selectToday = useCallback(() => {
    setWeekAnchorISO(defaultWeekAnchorISO);
    setMonthAnchorISO(defaultMonthAnchorISO);
    setSelectedDateISO(defaultSelectedDateISO);
  }, [defaultMonthAnchorISO, defaultSelectedDateISO, defaultWeekAnchorISO]);

  const selectMonitoringDate = useCallback(
    (nextDateISO: string) => {
      const anchors = anchorsForDate(nextDateISO);
      if (viewMode === "week") {
        if (!anchors.weekAnchorISO) return;
        setWeekAnchorISO(anchors.weekAnchorISO);
      } else {
        if (!anchors.monthAnchorISO) return;
        setMonthAnchorISO(anchors.monthAnchorISO);
      }
      setSelectedDateISO(nextDateISO);
    },
    [anchorsForDate, viewMode],
  );

  /**
   * The check-in overlay lives in the tab bar, which `MainTabNavigator` owns,
   * so handing it a payload writes state above this screen. That made the
   * payload's identity load-bearing: `saveActiveCheckIn` depended on whole
   * `useMutation` objects, which are new on every render, so this effect fired
   * on every render, set state in the parent, and the tab-bar override
   * re-rendered the whole tab subtree back into this card — an unbounded loop.
   * It only appeared in check-in mode, because view mode passes `null` and
   * React bails out when the value has not changed.
   *
   * The identity now follows what the overlay actually shows, and the actions
   * are read through a ref. A callback recreated for any reason can no longer
   * reach an ancestor's state.
   */
  const checkInOverlayActions = useRef({
    save: saveActiveCheckIn,
    cancel: cancelActiveCheckIn,
  });
  useEffect(() => {
    checkInOverlayActions.current = {
      save: saveActiveCheckIn,
      cancel: cancelActiveCheckIn,
    };
  }, [cancelActiveCheckIn, saveActiveCheckIn]);
  const requestCheckInSave = useCallback(() => {
    void checkInOverlayActions.current.save();
  }, []);
  const requestCheckInCancel = useCallback(() => {
    checkInOverlayActions.current.cancel();
  }, []);

  useEffect(() => {
    if (!onLoggingOverlayChange) return;
    onLoggingOverlayChange(
      isCheckInMode
        ? {
            canSave: canSaveCheckIn,
            onCancel: requestCheckInCancel,
            onSave: requestCheckInSave,
          }
        : null,
    );
  }, [
    canSaveCheckIn,
    isCheckInMode,
    onLoggingOverlayChange,
    requestCheckInCancel,
    requestCheckInSave,
  ]);

  useEffect(() => {
    return () => onLoggingOverlayChange?.(null);
  }, [onLoggingOverlayChange]);

  const modeProgress = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(modeProgress, {
      toValue: isCheckInMode ? 1 : 0,
      duration: 520,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [isCheckInMode, modeProgress]);

  const calendarCardAnimatedStyle = {
    backgroundColor: modeProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [
        semanticColors.surface.glass,
        semanticColors.onDark.fill.subtle,
      ],
    }),
    borderColor: modeProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [
        semanticColors.border.glass,
        semanticColors.onDark.fill.strong,
      ],
    }),
    shadowOpacity: modeProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0.08, 0.12],
    }),
  };
  const calendarTextureAnimatedStyle = {
    opacity: modeProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
  };
  const calendarGlassOverlayStyle = {
    opacity: modeProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
  };
  const calendarCardTransitionStyle =
    Platform.OS === "web"
      ? ({
          transition:
            "background-color 280ms cubic-bezier(0.22, 1, 0.36, 1), border-color 280ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 280ms cubic-bezier(0.22, 1, 0.36, 1)",
        } as Record<string, unknown>)
      : null;
  const calendarStickyStripStyle =
    Platform.OS === "web" && isCheckInMode
      ? ({
          position: "sticky",
          top: 8,
          zIndex: 20,
          ...(showStickyBackdrop
            ? {
                borderRadius: 16,
                padding: 8,
                marginHorizontal: -8,
                marginTop: -8,
                marginBottom: -2,
                backgroundColor: "rgba(83, 51, 41, 0.9)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                boxShadow: "0 10px 24px rgba(31, 18, 12, 0.14)",
              }
            : {}),
        } as Record<string, unknown>)
      : null;

  return (
    <Animated.View
      style={[
        cardSurface(),
        {
          padding: 14,
          gap: 13,
          overflow: isCheckInMode ? "visible" : "hidden",
        },
        isCheckInMode ? { shadowRadius: 24 } : null,
        calendarCardAnimatedStyle,
        calendarCardTransitionStyle,
      ]}
    >
      <View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFillObject,
          { borderRadius: CARD_RADIUS, overflow: "hidden" },
        ]}
      >
        <Animated.Image
          source={silkBrown}
          resizeMode="cover"
          style={[
            StyleSheet.absoluteFillObject,
            { transform: [{ scale: 1.2 }] },
            calendarTextureAnimatedStyle,
          ]}
        />
        <Animated.View
          style={[
            StyleSheet.absoluteFillObject,
            {
              backgroundColor: semanticColors.onDark.fill.subtle,
              ...(Platform.OS === "web"
                ? ({
                    backdropFilter: "blur(40px)",
                    WebkitBackdropFilter: "blur(40px)",
                  } as Record<string, string>)
                : {}),
            },
            calendarGlassOverlayStyle,
          ]}
        />
      </View>
      {calendarDataState === "unavailable" ? (
        <ClinicalDataStatus
          state="unavailable"
          title={t("clinical_data.unavailable_title")}
          message={t("clinical_data.unavailable_body")}
          retryLabel={t("clinical_data.retry")}
          retryingLabel={t("clinical_data.retrying")}
          onRetry={retryCalendar}
          isRetrying={isRetryingCalendar}
          embedded
          testID="care-calendar-unavailable"
        />
      ) : calendarDataState === "loading" ? (
        <View
          testID="care-calendar-loading"
          style={{
            minHeight: 210,
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <LoadingState caption={t("clinical_data.loading")} />
        </View>
      ) : (
        <>
          {calendarDataState === "stale" ? (
            <ClinicalDataStatus
              state="stale"
              message={t("clinical_data.stale_body")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={retryCalendar}
              isRetrying={isRetryingCalendar}
              testID="care-calendar-stale"
            />
          ) : null}
          {fertileWindowCalendarData ? (
            <FertileWindowPredictionNotice
              prediction={fertileWindowCalendarData.prediction}
              onDark={isCheckInMode}
            />
          ) : null}
          <CalendarMonthPicker
            visible={isMonthPickerOpen}
            onClose={() => setIsMonthPickerOpen(false)}
            anchorISO={monthAnchorISO}
            floor={calendarFloor}
            ceiling={calendarCeiling}
            today={today}
            onSelect={jumpToMonth}
          />
          <View style={[{ gap: 13 }, calendarStickyStripStyle]}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <View style={{ flex: 1 }}>
                <View
                  style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
                >
                  <CalendarNavButton
                    label={t(
                      viewMode === "week"
                        ? "care.overview.calendar.nav.previous_week_a11y"
                        : "care.overview.calendar.nav.previous_month_a11y",
                    )}
                    disabled={isAtPreviousBoundary}
                    onPress={() => navigateCalendar(-1)}
                    icon={
                      <ChevronLeft
                        size={14}
                        color={
                          isCheckInMode
                            ? calendarCheckInColors.navigationForeground
                            : TEXT_LIGHT
                        }
                        strokeWidth={2}
                      />
                    }
                  />
                  {showFixtureData ? (
                    <CardTitleText
                      style={
                        isCheckInMode
                          ? { color: calendarCheckInColors.foreground }
                          : undefined
                      }
                    >
                      {translateCalendarLabel(t, calendarLabel)}
                    </CardTitleText>
                  ) : (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={t(
                        "care.overview.calendar.jump.open_month_picker_a11y",
                      )}
                      onPress={() => {
                        capturePress(
                          pressLabel(
                            t(
                              "care.overview.calendar.jump.open_month_picker_a11y",
                            ),
                          ),
                          "calendar_month_picker",
                        );
                        setIsMonthPickerOpen(true);
                      }}
                      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
                      testID="care-calendar-month-label"
                    >
                      <CardTitleText
                        style={
                          isCheckInMode
                            ? { color: calendarCheckInColors.foreground }
                            : undefined
                        }
                      >
                        {translateCalendarLabel(t, calendarLabel)}
                      </CardTitleText>
                    </Pressable>
                  )}
                  <CalendarNavButton
                    label={t(
                      viewMode === "week"
                        ? "care.overview.calendar.nav.next_week_a11y"
                        : "care.overview.calendar.nav.next_month_a11y",
                    )}
                    disabled={isAtNextBoundary}
                    onPress={() => navigateCalendar(1)}
                    icon={
                      <ChevronRight
                        size={14}
                        color={
                          isCheckInMode
                            ? calendarCheckInColors.navigationForeground
                            : TEXT_LIGHT
                        }
                        strokeWidth={2}
                      />
                    }
                  />
                </View>
              </View>
              <MoonSegmentedControl
                tabs={(["week", "month"] as const).map((mode) => ({
                  value: mode,
                  label: t(
                    mode === "week"
                      ? "care.calendar.week"
                      : "care.calendar.month",
                  ),
                  accessibilityLabel: t(
                    mode === "week"
                      ? "care.overview.calendar.nav.show_week_view_a11y"
                      : "care.overview.calendar.nav.show_month_view_a11y",
                  ),
                  action: "calendar_view_mode",
                }))}
                active={viewMode}
                onChange={switchCalendarView}
                shape="rounded"
                tone={isCheckInMode ? "inverse" : "default"}
                style={{ marginTop: 0 }}
              />
            </View>

            <CalendarDateGridTransition
              mode={viewMode}
              weekContent={
                <View style={{ flexDirection: "row", gap: 7 }}>
                  {visibleWeek.days.map((day) => {
                    const displayDay = getDisplayDay(day, visibleWeek);
                    return (
                      <CalendarDayCell
                        key={`${day.label}-${day.date}`}
                        day={displayDay}
                        monthName={visibleWeek.monthName}
                        isoPrefix={visibleWeek.isoPrefix}
                        selected={
                          getCalendarDayIsoDate(day, visibleWeek.isoPrefix) ===
                          selectedDateISO
                        }
                        mode={calendarMode}
                        onPress={() =>
                          setSelectedDateISO(
                            getCalendarDayIsoDate(day, visibleWeek.isoPrefix),
                          )
                        }
                      />
                    );
                  })}
                </View>
              }
              monthContent={
                <View style={{ gap: 7 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    {(
                      ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const
                    ).map((dayKey, index) => (
                      <Text
                        key={`${dayKey}-${index}`}
                        className="font-dm-sans"
                        style={{
                          width: "13.4%",
                          textAlign: "center",
                          fontSize: 10,
                          lineHeight: 12,
                          color: isCheckInMode
                            ? calendarCheckInColors.dayLabel
                            : TEXT_LIGHT,
                          fontWeight: "800",
                        }}
                      >
                        {t(`care.weekday_short.${dayKey}`).charAt(0)}
                      </Text>
                    ))}
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      rowGap: 7,
                      columnGap: "1%",
                    }}
                  >
                    {visibleMonth.cells.map((day, index) => {
                      const displayDay = day
                        ? getDisplayDay(day, visibleMonth)
                        : null;
                      return (
                        <CalendarMonthCell
                          key={`${day?.date ?? "blank"}-${index}`}
                          day={displayDay}
                          monthName={visibleMonth.monthName}
                          isoPrefix={visibleMonth.isoPrefix}
                          selected={Boolean(
                            day &&
                            getCalendarDayIsoDate(
                              day,
                              visibleMonth.isoPrefix,
                            ) === selectedDateISO,
                          )}
                          mode={calendarMode}
                          onPress={() => {
                            if (day) {
                              setSelectedDateISO(
                                getCalendarDayIsoDate(
                                  day,
                                  visibleMonth.isoPrefix,
                                ),
                              );
                            }
                          }}
                        />
                      );
                    })}
                  </View>
                </View>
              }
            />
            {isCheckInMode ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11.5,
                  lineHeight: 15,
                  color: calendarCheckInColors.navigationForeground,
                  fontWeight: "700",
                  marginTop: -3,
                }}
              >
                Tap another day above to keep logging symptoms without leaving
                this view.
              </Text>
            ) : null}
          </View>

          <CalendarModeContent
            mode={calendarMode}
            viewContent={
              <CalendarViewModeContent
                selectedDate={selectedDay?.date ?? ""}
                hasSelectedDay={hasSelectedDay}
                selectedRows={selectedRows}
                canLogSelectedDay={canLogSelectedDay}
                savedCheckIn={savedCheckIn}
                selectedCheckInDataState={selectedCheckInDataState}
                isRetryingSelectedCheckIn={isRetryingSelectedCheckIn}
                onRetrySelectedCheckIn={retrySelectedCheckIn}
                selectedMonthName={selectedMonthName}
                scheduleDataState={scheduleDataState}
                onStartCheckIn={() => setCalendarMode("check-in")}
                onOpenMedicationFactSheet={onOpenMedicationFactSheet}
                onOpenAppointment={openAppointmentRow}
                onOpenProcedureFactSheet={onOpenProcedureFactSheet}
                onRequestDocumentAttachment={onRequestDocumentAttachment}
              />
            }
            checkInContent={
              <CalendarCheckInModeContent
                draft={activeDraft}
                saved={savedCheckInForEditor}
                canSave={canSaveCheckIn}
                canEditSelectedDay={canLogSelectedDay}
                hasSelectedDay={hasSelectedDay}
                selectedCheckInDataState={selectedCheckInDataState}
                isRetryingSelectedCheckIn={isRetryingSelectedCheckIn}
                onRetrySelectedCheckIn={retrySelectedCheckIn}
                onChange={(next) => updateActiveDraft(next)}
                onSelectToday={selectToday}
                selectedRows={selectedRows}
                canLogSelectedDay={canLogSelectedDay}
                scheduleDataState={scheduleDataState}
                isRetryingSchedule={isRetryingSchedule}
                onRetrySchedule={retrySchedule}
                selectedDateISO={selectedIsoDate}
                activeTab={dateLogTab}
                onTabChange={setDateLogTab}
                onOpenMedicationFactSheet={onOpenMedicationFactSheet}
                onOpenAppointment={openAppointmentRow}
                onAddMedication={() => onAddMedication(selectedIsoDate)}
                onAddAppointment={() => onAddAppointment(selectedIsoDate)}
                showAllSymptoms={showAllSymptoms}
                onToggleAllSymptoms={() =>
                  setShowAllSymptoms((current) => !current)
                }
                onUploadReport={onUploadMonitoringReport}
                onMonitoringDateChange={selectMonitoringDate}
              />
            }
          />
        </>
      )}
    </Animated.View>
  );
}

function CalendarDateGridTransition({
  mode,
  weekContent,
  monthContent,
}: {
  mode: "week" | "month";
  weekContent: ReactNode;
  monthContent: ReactNode;
}) {
  const progress = useRef(new Animated.Value(mode === "month" ? 1 : 0)).current;
  const heightAnimation = useRef(new Animated.Value(0)).current;
  const hasInitializedHeight = useRef(false);
  const [weekHeight, setWeekHeight] = useState(0);
  const [monthHeight, setMonthHeight] = useState(0);
  const hasMeasuredContent = weekHeight > 0 && monthHeight > 0;
  const targetHeight = mode === "month" ? monthHeight : weekHeight;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: mode === "month" ? 1 : 0,
      duration: 260,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [mode, progress]);

  useEffect(() => {
    if (!hasMeasuredContent || targetHeight <= 0) return;
    if (!hasInitializedHeight.current) {
      heightAnimation.setValue(targetHeight);
      hasInitializedHeight.current = true;
      return;
    }
    Animated.timing(heightAnimation, {
      toValue: targetHeight,
      duration: 260,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [hasMeasuredContent, heightAnimation, targetHeight]);

  const weekOpacity = progress.interpolate({
    inputRange: [0, 0.42, 0.58, 1],
    outputRange: [1, 1, 0, 0],
  });
  const monthOpacity = progress.interpolate({
    inputRange: [0, 0.42, 0.58, 1],
    outputRange: [0, 0, 1, 1],
  });
  const weekTranslateY = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -6],
  });
  const monthTranslateY = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [6, 0],
  });

  return (
    <View style={{ position: "relative" }}>
      <View
        pointerEvents="none"
        // Offscreen measurement clone of both faces. It must be hidden from the
        // accessibility tree — on web `accessibilityElementsHidden` alone does
        // not emit `aria-hidden`, so this clone would otherwise expose duplicate
        // focusable controls (e.g. a duplicate day cell or button) that
        // shadow the real ones for screen readers and role/label-based queries.
        aria-hidden
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={{
          position: "absolute",
          opacity: 0,
          left: 0,
          right: 0,
          top: 0,
          zIndex: -1,
        }}
      >
        <View
          onLayout={(event) => setWeekHeight(event.nativeEvent.layout.height)}
        >
          {weekContent}
        </View>
        <View
          onLayout={(event) => setMonthHeight(event.nativeEvent.layout.height)}
        >
          {monthContent}
        </View>
      </View>

      <Animated.View
        style={[
          { overflow: "hidden" },
          hasMeasuredContent ? { height: heightAnimation } : null,
        ]}
      >
        {hasMeasuredContent ? (
          <>
            <Animated.View
              pointerEvents={mode === "week" ? "auto" : "none"}
              aria-hidden={mode !== "week"}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                opacity: weekOpacity,
                transform: [{ translateY: weekTranslateY }],
              }}
            >
              {weekContent}
            </Animated.View>
            <Animated.View
              pointerEvents={mode === "month" ? "auto" : "none"}
              aria-hidden={mode !== "month"}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                opacity: monthOpacity,
                transform: [{ translateY: monthTranslateY }],
              }}
            >
              {monthContent}
            </Animated.View>
          </>
        ) : mode === "month" ? (
          monthContent
        ) : (
          weekContent
        )}
      </Animated.View>
    </View>
  );
}

function CalendarModeContent({
  mode,
  viewContent,
  checkInContent,
}: {
  mode: CalendarMode;
  viewContent: ReactNode;
  checkInContent: ReactNode;
}) {
  const progress = useRef(
    new Animated.Value(mode === "check-in" ? 1 : 0),
  ).current;
  const heightAnimation = useRef(new Animated.Value(0)).current;
  const hasInitializedHeight = useRef(false);
  const [viewHeight, setViewHeight] = useState(0);
  const [checkInHeight, setCheckInHeight] = useState(0);
  const hasMeasuredContent = viewHeight > 0 && checkInHeight > 0;
  const targetHeight = mode === "check-in" ? checkInHeight : viewHeight;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: mode === "check-in" ? 1 : 0,
      duration: 520,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [mode, progress]);

  useEffect(() => {
    if (!hasMeasuredContent || targetHeight <= 0) return;
    if (!hasInitializedHeight.current) {
      heightAnimation.setValue(targetHeight);
      hasInitializedHeight.current = true;
      return;
    }
    Animated.timing(heightAnimation, {
      toValue: targetHeight,
      duration: 260,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [hasMeasuredContent, heightAnimation, targetHeight]);

  const frontRotateY = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });
  const backRotateY = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ["-180deg", "0deg"],
  });
  const viewOpacity = progress.interpolate({
    inputRange: [0, 0.49, 0.5, 1],
    outputRange: [1, 1, 0, 0],
  });
  const checkInOpacity = progress.interpolate({
    inputRange: [0, 0.49, 0.5, 1],
    outputRange: [0, 0, 1, 1],
  });
  const flipFaceStyle =
    Platform.OS === "web"
      ? ({
          backfaceVisibility: "hidden",
          transformStyle: "preserve-3d",
        } as Record<string, unknown>)
      : ({ backfaceVisibility: "hidden" } as Record<string, unknown>);

  return (
    <View style={{ position: "relative" }}>
      <View
        pointerEvents="none"
        // Offscreen measurement clone of both faces. It must be hidden from the
        // accessibility tree — on web `accessibilityElementsHidden` alone does
        // not emit `aria-hidden`, so this clone would otherwise expose duplicate
        // focusable controls (e.g. a duplicate day cell or button) that
        // shadow the real ones for screen readers and role/label-based queries.
        aria-hidden
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={{
          position: "absolute",
          opacity: 0,
          left: 0,
          right: 0,
          top: 0,
          zIndex: -1,
        }}
      >
        <View
          onLayout={(event) => setViewHeight(event.nativeEvent.layout.height)}
        >
          {viewContent}
        </View>
        <View
          onLayout={(event) =>
            setCheckInHeight(event.nativeEvent.layout.height)
          }
        >
          {checkInContent}
        </View>
      </View>

      <Animated.View
        style={[
          { overflow: "hidden" },
          hasMeasuredContent ? { height: heightAnimation } : null,
        ]}
      >
        {hasMeasuredContent ? (
          <>
            <Animated.View
              pointerEvents={mode === "view" ? "auto" : "none"}
              aria-hidden={mode !== "view"}
              style={[
                {
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  opacity: viewOpacity,
                  transform: [{ perspective: 1200 }, { rotateY: frontRotateY }],
                },
                flipFaceStyle,
              ]}
            >
              {viewContent}
            </Animated.View>
            <Animated.View
              pointerEvents={mode === "check-in" ? "auto" : "none"}
              aria-hidden={mode !== "check-in"}
              style={[
                {
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  opacity: checkInOpacity,
                  transform: [{ perspective: 1200 }, { rotateY: backRotateY }],
                },
                flipFaceStyle,
              ]}
            >
              {checkInContent}
            </Animated.View>
          </>
        ) : mode === "check-in" ? (
          checkInContent
        ) : (
          viewContent
        )}
      </Animated.View>
    </View>
  );
}

function CalendarSelectedCheckInStatus({
  state,
  isRetrying,
  onRetry,
  surface,
}: {
  state: Exclude<SelectedCheckInDataState, null>;
  isRetrying: boolean;
  onRetry: () => void;
  /** `"card"` is the light calendar card; `"log"` is the dark logging sheet. */
  surface: "card" | "log";
}) {
  const t = useTranslations();
  const appearance = surface === "log" ? "dark" : "light";

  if (state === "loading") {
    return (
      <View
        testID="care-selected-check-in-loading"
        accessibilityLiveRegion="polite"
        style={{
          minHeight: 132,
          alignItems: "center",
          justifyContent: "center",
          gap: 9,
          borderRadius: 14,
          backgroundColor: semanticColors.onDark.fill.subtle,
          paddingHorizontal: 14,
          paddingVertical: 18,
        }}
      >
        <LoadingState
          appearance={appearance}
          caption={t("care.overview.calendar.check_in.selected_day_loading")}
          style={{ paddingVertical: 0 }}
        />
      </View>
    );
  }

  return (
    <ClinicalDataStatus
      state="unavailable"
      appearance={appearance}
      title={t(
        "care.overview.calendar.check_in.selected_day_unavailable_title",
      )}
      message={t(
        "care.overview.calendar.check_in.selected_day_unavailable_body",
      )}
      retryLabel={t("clinical_data.retry")}
      retryingLabel={t("clinical_data.retrying")}
      onRetry={onRetry}
      isRetrying={isRetrying}
      embedded
      testID="care-selected-check-in-unavailable"
    />
  );
}

function CalendarMonitoringDataStatus({
  state,
  isRetrying,
  onRetry,
}: {
  state: Exclude<MonitoringDataState, null>;
  isRetrying: boolean;
  onRetry: () => void;
}) {
  const t = useTranslations();

  if (state === "loading") {
    return (
      <View
        testID="care-monitoring-read-loading"
        accessibilityLiveRegion="polite"
        style={{
          minHeight: 132,
          alignItems: "center",
          justifyContent: "center",
          gap: 9,
          borderRadius: 14,
          backgroundColor: semanticColors.onDark.fill.subtle,
          paddingHorizontal: 14,
          paddingVertical: 18,
        }}
      >
        <LoadingState
          appearance="dark"
          caption={t("care.overview.calendar.monitoring_log.read_loading")}
          style={{ paddingVertical: 0 }}
        />
      </View>
    );
  }

  return (
    <ClinicalDataStatus
      state="unavailable"
      appearance="dark"
      title={t("care.overview.calendar.monitoring_log.read_unavailable_title")}
      message={t("care.overview.calendar.monitoring_log.read_unavailable_body")}
      retryLabel={t("clinical_data.retry")}
      retryingLabel={t("clinical_data.retrying")}
      onRetry={onRetry}
      isRetrying={isRetrying}
      embedded
      testID="care-monitoring-read-unavailable"
    />
  );
}

function CalendarViewModeContent({
  selectedDate,
  hasSelectedDay,
  selectedRows,
  canLogSelectedDay,
  savedCheckIn,
  selectedCheckInDataState,
  isRetryingSelectedCheckIn,
  onRetrySelectedCheckIn,
  selectedMonthName,
  scheduleDataState,
  onStartCheckIn,
  onOpenMedicationFactSheet,
  onOpenAppointment,
  onOpenProcedureFactSheet,
  onRequestDocumentAttachment,
}: {
  selectedDate: string;
  hasSelectedDay: boolean;
  selectedRows: TodayRow[];
  canLogSelectedDay: boolean;
  savedCheckIn: DailyCheckInDraft | undefined;
  selectedCheckInDataState: SelectedCheckInDataState;
  isRetryingSelectedCheckIn: boolean;
  onRetrySelectedCheckIn: () => void;
  selectedMonthName: string;
  /**
   * Whether the day's meds and visits have actually arrived. The empty
   * state is an assertion — "nothing scheduled for the 14th" — so it must
   * not stand in for a month still loading.
   */
  scheduleDataState: ScheduleDataState;
  onStartCheckIn: () => void;
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenAppointment: (row: TodayRow) => void;
  onOpenProcedureFactSheet: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
}) {
  const t = useTranslations();
  const careMarkers = [
    {
      label: t("care.overview.calendar.markers.meds"),
      marker: "med" as const,
    },
    {
      label: t("care.overview.calendar.markers.appointment"),
      marker: "visit" as const,
    },
    {
      label: t("care.overview.calendar.markers.test"),
      marker: "lab" as const,
    },
    {
      label: t("care.overview.calendar.markers.period"),
      marker: "period" as const,
    },
    {
      label: t("care.overview.calendar.markers.fertileWindow"),
      marker: "fertile" as const,
    },
  ];
  return (
    <View style={{ gap: 13 }}>
      <View
        style={{
          flexDirection: "row",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {careMarkers.map((item) => (
          <View
            key={item.label}
            style={{ flexDirection: "row", alignItems: "center", gap: 5 }}
          >
            <CalendarMarker marker={item.marker} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 14,
                color: TEXT_LIGHT,
                fontWeight: "700",
              }}
            >
              {item.label}
            </Text>
          </View>
        ))}
        <CompactActionButton
          label={t("care.overview.calendar.check_in.log_symptom")}
          accessibilityLabel={t(
            "care.overview.calendar.check_in.log_symptoms_a11y",
          )}
          action="start_check_in"
          size="sm"
          variant="secondary"
          leadingIcon={Plus}
          onPress={onStartCheckIn}
          style={{ marginLeft: "auto" }}
        />
      </View>
      <View style={{ gap: 8 }}>
        {!hasSelectedDay ? (
          <CalendarNoSelectedDate surface="card" />
        ) : selectedCheckInDataState ? (
          <CalendarSelectedCheckInStatus
            state={selectedCheckInDataState}
            isRetrying={isRetryingSelectedCheckIn}
            onRetry={onRetrySelectedCheckIn}
            surface="card"
          />
        ) : null}
        {hasSelectedDay && selectedRows.length > 0 ? (
          <TodayList
            key={`calendar-rows-${selectedDate}-${canLogSelectedDay ? "log" : "preview"}`}
            rows={selectedRows}
            showCompletion={canLogSelectedDay}
            onOpenMedicationFactSheet={onOpenMedicationFactSheet}
            onOpenAppointment={onOpenAppointment}
            onOpenProcedureFactSheet={onOpenProcedureFactSheet}
            onRequestDocumentAttachment={onRequestDocumentAttachment}
            completionSource="care"
          />
        ) : hasSelectedDay &&
          !selectedCheckInDataState &&
          scheduleDataState === "loading" ? (
          <View
            testID="care-calendar-schedule-loading"
            accessibilityLiveRegion="polite"
            style={{ paddingVertical: 12 }}
          >
            <LoadingState
              caption={t("care.overview.calendar.still_loading_this_day")}
              style={{ paddingVertical: 0 }}
            />
          </View>
        ) : !hasSelectedDay ||
          selectedCheckInDataState ||
          savedCheckIn ? null : (
          <CalendarEmptyState
            selectedDate={selectedDate}
            monthName={selectedMonthName}
          />
        )}
        {hasSelectedDay && selectedCheckInDataState === null && savedCheckIn ? (
          <SavedCheckInSummary checkIn={savedCheckIn} />
        ) : null}
      </View>
    </View>
  );
}

function CalendarCheckInModeContent({
  draft,
  saved,
  canSave,
  canEditSelectedDay,
  hasSelectedDay,
  canLogSelectedDay,
  selectedCheckInDataState,
  isRetryingSelectedCheckIn,
  onRetrySelectedCheckIn,
  onChange,
  onSelectToday,
  selectedRows,
  scheduleDataState,
  isRetryingSchedule,
  onRetrySchedule,
  selectedDateISO,
  activeTab,
  onTabChange,
  onOpenMedicationFactSheet,
  onOpenAppointment,
  onAddMedication,
  onAddAppointment,
  showAllSymptoms,
  onToggleAllSymptoms,
  onUploadReport,
  onMonitoringDateChange,
}: {
  draft: DailyCheckInDraft;
  saved: boolean;
  canSave: boolean;
  canEditSelectedDay: boolean;
  hasSelectedDay: boolean;
  canLogSelectedDay: boolean;
  selectedCheckInDataState: SelectedCheckInDataState;
  isRetryingSelectedCheckIn: boolean;
  onRetrySelectedCheckIn: () => void;
  onChange: (next: DailyCheckInDraft) => void;
  onSelectToday: () => void;
  selectedRows: TodayRow[];
  scheduleDataState: ScheduleDataState;
  isRetryingSchedule: boolean;
  onRetrySchedule: () => void;
  selectedDateISO: string;
  activeTab: DateLogTab;
  onTabChange: (tab: DateLogTab) => void;
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenAppointment: (row: TodayRow) => void;
  onAddMedication: () => void;
  onAddAppointment: () => void;
  showAllSymptoms: boolean;
  onToggleAllSymptoms: () => void;
  onUploadReport?: MonitoringUploadRequest;
  onMonitoringDateChange: (dateISO: string) => void;
}) {
  return (
    <View style={{ gap: 13 }}>
      <CalendarCheckInPanel
        draft={draft}
        saved={saved}
        canSave={canSave}
        canEditSelectedDay={canEditSelectedDay}
        hasSelectedDay={hasSelectedDay}
        selectedCheckInDataState={selectedCheckInDataState}
        isRetryingSelectedCheckIn={isRetryingSelectedCheckIn}
        onRetrySelectedCheckIn={onRetrySelectedCheckIn}
        onChange={onChange}
        onSelectToday={onSelectToday}
        selectedRows={selectedRows}
        scheduleDataState={scheduleDataState}
        isRetryingSchedule={isRetryingSchedule}
        onRetrySchedule={onRetrySchedule}
        selectedDateISO={selectedDateISO}
        canLogSelectedDay={canLogSelectedDay}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onOpenMedicationFactSheet={onOpenMedicationFactSheet}
        onOpenAppointment={onOpenAppointment}
        onAddMedication={onAddMedication}
        onAddAppointment={onAddAppointment}
        showAllSymptoms={showAllSymptoms}
        onToggleAllSymptoms={onToggleAllSymptoms}
        onUploadReport={onUploadReport}
        onMonitoringDateChange={onMonitoringDateChange}
      />
    </View>
  );
}

function CalendarCheckInPanel({
  draft,
  saved,
  canSave,
  canEditSelectedDay,
  hasSelectedDay,
  selectedCheckInDataState,
  isRetryingSelectedCheckIn,
  onRetrySelectedCheckIn,
  onChange,
  onSelectToday,
  selectedRows,
  scheduleDataState,
  isRetryingSchedule,
  onRetrySchedule,
  selectedDateISO,
  canLogSelectedDay,
  activeTab,
  onTabChange,
  onOpenMedicationFactSheet,
  onOpenAppointment,
  onAddMedication,
  onAddAppointment,
  showAllSymptoms,
  onToggleAllSymptoms,
  onUploadReport,
  onMonitoringDateChange,
}: {
  draft: DailyCheckInDraft;
  saved: boolean;
  canSave: boolean;
  canEditSelectedDay: boolean;
  hasSelectedDay: boolean;
  selectedCheckInDataState: SelectedCheckInDataState;
  isRetryingSelectedCheckIn: boolean;
  onRetrySelectedCheckIn: () => void;
  onChange: (next: DailyCheckInDraft) => void;
  onSelectToday: () => void;
  selectedRows: TodayRow[];
  scheduleDataState: ScheduleDataState;
  isRetryingSchedule: boolean;
  onRetrySchedule: () => void;
  selectedDateISO: string;
  canLogSelectedDay: boolean;
  activeTab: DateLogTab;
  onTabChange: (tab: DateLogTab) => void;
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenAppointment: (row: TodayRow) => void;
  onAddMedication: () => void;
  onAddAppointment: () => void;
  showAllSymptoms: boolean;
  onToggleAllSymptoms: () => void;
  onUploadReport?: MonitoringUploadRequest;
  onMonitoringDateChange: (dateISO: string) => void;
}) {
  const capturePress = usePressCapture("CalendarCheckInPanel");
  const t = useTranslations();
  const localizedCategories = getCheckInSymptomCategories(t);
  const visibleSymptomCategories = showAllSymptoms
    ? localizedCategories
    : localizedCategories.slice(0, 2);
  const previewCategory = showAllSymptoms
    ? null
    : localizedCategories[visibleSymptomCategories.length];

  return (
    <CardSurface variant="panel" tone="onDark" style={{ padding: 14, gap: 14 }}>
      {saved ? (
        <Tag
          label={t("care.overview.calendar.check_in.saved")}
          tone="onDark"
          shape="pill"
          style={{ alignSelf: "flex-end", marginBottom: -4 }}
        />
      ) : null}

      <DateLogTabs activeTab={activeTab} onTabChange={onTabChange} />

      {hasSelectedDay &&
      activeTab === "symptoms" &&
      !canSave &&
      selectedCheckInDataState === null ? (
        <View
          style={{
            borderRadius: 14,
            backgroundColor: semanticColors.onDark.fill.subtle,
            paddingHorizontal: 12,
            paddingVertical: 10,
          }}
        >
          <View style={{ gap: 8 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 16,
                color: semanticColors.onDark.text.secondary,
                fontWeight: "700",
              }}
            >
              {t("care.overview.calendar.check_in.future_date")}
            </Text>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  color: semanticColors.onDark.text.secondary,
                  fontWeight: "700",
                }}
              >
                {t("care.overview.calendar.check_in.choose")}
              </Text>
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(
                      t("care.overview.calendar.check_in.select_today_a11y"),
                    ),
                    "select_today",
                  );
                  onSelectToday();
                }}
                accessibilityRole="button"
                accessibilityLabel={t(
                  "care.overview.calendar.check_in.select_today_a11y",
                )}
                style={({ pressed }) => ({
                  borderRadius: 999,
                  backgroundColor: "#FFF7EF",
                  paddingHorizontal: 9,
                  paddingVertical: 4,
                  opacity: pressed ? 0.72 : 1,
                })}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11.5,
                    lineHeight: 14,
                    color: "#5A3125",
                    fontWeight: "900",
                  }}
                >
                  {t("care.overview.calendar.check_in.today")}
                </Text>
              </Pressable>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  color: semanticColors.onDark.text.secondary,
                  fontWeight: "700",
                }}
              >
                {t("care.overview.calendar.check_in.or_past_day")}
              </Text>
            </View>
          </View>
        </View>
      ) : null}

      {!hasSelectedDay ? (
        <CalendarNoSelectedDate surface="log" />
      ) : activeTab === "symptoms" ? (
        selectedCheckInDataState ? (
          <CalendarSelectedCheckInStatus
            state={selectedCheckInDataState}
            isRetrying={isRetryingSelectedCheckIn}
            onRetry={onRetrySelectedCheckIn}
            surface="log"
          />
        ) : !canSave ? null : (
          <>
            <CheckInGroup
              title={t("care.overview.calendar.check_in.bleeding_title")}
              icon={Droplet}
            >
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {bleedingOptions.map((option) => (
                  <CheckInChip
                    key={option.value}
                    label={t(option.labelKey)}
                    emoji={option.emoji}
                    active={draft.bleeding.includes(option.value)}
                    disabled={!canSave}
                    onPress={() =>
                      onChange({
                        ...draft,
                        bleeding: toggleBleedingSelection(
                          draft.bleeding,
                          option.value,
                        ),
                      })
                    }
                  />
                ))}
              </View>
            </CheckInGroup>

            {visibleSymptomCategories.map((category) => (
              <CheckInGroup
                key={category.id}
                title={category.label}
                icon={category.icon}
              >
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {category.options.map((option) => (
                    <CheckInChip
                      key={option.label}
                      label={t(option.labelKey)}
                      emoji={option.emoji}
                      active={draft.symptoms.includes(option.label)}
                      disabled={!canSave}
                      onPress={() =>
                        onChange({
                          ...draft,
                          symptoms: toggleListValue(
                            draft.symptoms,
                            option.label,
                          ),
                        })
                      }
                    />
                  ))}
                </View>
              </CheckInGroup>
            ))}

            {previewCategory ? (
              <View
                pointerEvents="none"
                style={{
                  opacity: 0.42,
                  marginTop: -2,
                  maxHeight: 48,
                  overflow: "hidden",
                }}
              >
                <CheckInGroup
                  title={previewCategory.label}
                  icon={previewCategory.icon}
                >
                  <View
                    style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                  >
                    {previewCategory.options.slice(0, 3).map((option) => (
                      <CheckInChip
                        key={option.label}
                        label={t(option.labelKey)}
                        emoji={option.emoji}
                        active={draft.symptoms.includes(option.label)}
                        disabled
                        onPress={() => undefined}
                      />
                    ))}
                  </View>
                </CheckInGroup>
              </View>
            ) : null}

            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(
                    showAllSymptoms
                      ? t("care.overview.calendar.check_in.view_fewer_a11y")
                      : t("care.overview.calendar.check_in.view_all_a11y"),
                  ),
                  "toggle_all_symptoms",
                );
                onToggleAllSymptoms();
              }}
              accessibilityRole="button"
              accessibilityLabel={
                showAllSymptoms
                  ? t("care.overview.calendar.check_in.view_fewer_a11y")
                  : t("care.overview.calendar.check_in.view_all_a11y")
              }
              style={({ pressed }) => ({
                alignSelf: "center",
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                paddingHorizontal: 12,
                paddingVertical: 7,
                borderRadius: 999,
                opacity: pressed ? 0.68 : 1,
              })}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 15,
                  color: semanticColors.onDark.text.secondary,
                  fontWeight: "800",
                }}
              >
                {showAllSymptoms
                  ? t("care.overview.calendar.check_in.view_fewer")
                  : t("care.overview.calendar.check_in.view_all")}
              </Text>
              {showAllSymptoms ? (
                <ChevronUp
                  size={14}
                  color={semanticColors.onDark.text.secondary}
                />
              ) : (
                <ChevronDown
                  size={14}
                  color={semanticColors.onDark.text.secondary}
                />
              )}
            </Pressable>

            <CheckInGroup
              title={t("care.overview.calendar.check_in.notes_title")}
            >
              <TextInput
                className="font-dm-sans"
                editable={canSave}
                multiline
                value={draft.notes}
                onChangeText={(notes) => onChange({ ...draft, notes })}
                placeholder={t(
                  "care.overview.calendar.check_in.notes_placeholder",
                )}
                placeholderTextColor={semanticColors.onDark.text.tertiary}
                style={{
                  minHeight: 78,
                  borderRadius: 14,
                  backgroundColor: semanticColors.onDark.fill.subtle,
                  borderWidth: 1,
                  borderColor: semanticColors.onDark.fill.strong,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  color: semanticColors.onDark.text.primary,
                  fontSize: 13,
                  lineHeight: 18,
                  textAlignVertical: "top",
                  opacity: canSave ? 1 : 0.58,
                }}
              />
            </CheckInGroup>
          </>
        )
      ) : activeTab === "monitoring" ? (
        <CalendarMonitoringLogPanel
          canEditDate={canEditSelectedDay}
          onSelectToday={onSelectToday}
          selectedDateISO={selectedDateISO}
          onUploadReport={onUploadReport}
          onDateChange={onMonitoringDateChange}
        />
      ) : (
        <CalendarScheduleLogPanel
          selectedRows={selectedRows}
          canLogSelectedDay={canLogSelectedDay}
          scheduleDataState={scheduleDataState}
          isRetryingSchedule={isRetryingSchedule}
          onRetrySchedule={onRetrySchedule}
          onOpenMedicationFactSheet={onOpenMedicationFactSheet}
          onOpenAppointment={onOpenAppointment}
          onAddMedication={onAddMedication}
          onAddAppointment={onAddAppointment}
        />
      )}
    </CardSurface>
  );
}

function DateLogTabs({
  activeTab,
  onTabChange,
}: {
  activeTab: DateLogTab;
  onTabChange: (tab: DateLogTab) => void;
}) {
  const capturePress = usePressCapture("DateLogTabs");
  const t = useTranslations();
  const tabs = getDateLogTabs(t);
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 5,
        borderRadius: 14,
        backgroundColor: semanticColors.onDark.fill.subtle,
        padding: 4,
      }}
    >
      {tabs.map((tab) => {
        const active = activeTab === tab.id;
        const Icon = tab.icon;
        return (
          <Pressable
            key={tab.id}
            onPress={() => {
              capturePress(pressLabel(tab.label), "date_log_tab");
              onTabChange(tab.id);
            }}
            testID={`care-calendar-log-tab-${tab.id}${active ? "-selected" : ""}`}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            accessibilityLabel={tab.label}
            style={({ pressed }) => ({
              flex: 1,
              minHeight: 36,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              gap: 5,
              backgroundColor: active ? "#FFF7EF" : "transparent",
              opacity: pressed ? 0.74 : 1,
            })}
          >
            <Icon
              size={12}
              color={active ? "#5A3125" : semanticColors.onDark.text.secondary}
              strokeWidth={2}
            />
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              adjustsFontSizeToFit
              style={{
                fontSize: 11,
                lineHeight: 13,
                color: active
                  ? "#5A3125"
                  : semanticColors.onDark.text.secondary,
                fontWeight: "900",
              }}
            >
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function parseSizeList(raw: string): number[] {
  if (!raw.trim()) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => parseFloat(s))
    .filter((n) => Number.isFinite(n));
}

const MONITORING_SUMMARY_FIELDS = [
  {
    key: "follicles",
    labelKey: "care.overview.calendar.monitoring_log.field.follicles_label",
    emptyValueKey:
      "care.overview.calendar.monitoring_log.field.follicles_value",
  },
  {
    key: "leadSize",
    labelKey: "care.overview.calendar.monitoring_log.field.lead_size_label",
    emptyValueKey:
      "care.overview.calendar.monitoring_log.field.lead_size_value",
  },
  {
    key: "lining",
    labelKey: "care.overview.calendar.monitoring_log.field.lining_label",
    emptyValueKey: "care.overview.calendar.monitoring_log.field.lining_value",
  },
  {
    key: "estradiol",
    labelKey: "care.overview.calendar.monitoring_log.field.estradiol_label",
    emptyValueKey:
      "care.overview.calendar.monitoring_log.field.estradiol_value",
  },
  {
    key: "lh",
    labelKey: "care.overview.calendar.monitoring_log.field.lh_label",
    emptyValueKey: "care.overview.calendar.monitoring_log.field.lh_value",
  },
  {
    key: "progesterone",
    labelKey: "care.overview.calendar.monitoring_log.field.progesterone_label",
    emptyValueKey:
      "care.overview.calendar.monitoring_log.field.progesterone_value",
  },
] as const;

function monitoringLeadSize(draft: MonitoringEntryDraft): number {
  const sizes = [draft.leftSizes, draft.rightSizes]
    .flatMap((value) => parseSizeList(value))
    .filter((value) => Number.isFinite(value));
  return sizes.length > 0 ? Math.max(...sizes) : 0;
}

function monitoringSummaryValue(
  field: (typeof MONITORING_SUMMARY_FIELDS)[number],
  draft: MonitoringEntryDraft,
): string {
  switch (field.key) {
    case "follicles": {
      const left = parseSizeList(draft.leftSizes).length || draft.leftCount;
      const right = parseSizeList(draft.rightSizes).length || draft.rightCount;
      return left || right ? `${left || "—"} · ${right || "—"}` : "";
    }
    case "leadSize": {
      const lead = monitoringLeadSize(draft);
      return lead ? `${lead} mm` : "";
    }
    case "lining":
      return draft.liningThickness ? `${draft.liningThickness} mm` : "";
    case "estradiol":
      return draft.estradiol ? `${draft.estradiol} ${draft.estradiolUnit}` : "";
    case "lh":
      return draft.lh ? `${draft.lh} ${draft.lhUnit}` : "";
    case "progesterone":
      return draft.progesterone
        ? `${draft.progesterone} ${draft.progesteroneUnit}`
        : "";
  }
}

function CalendarMonitoringLogPanel({
  canEditDate,
  onSelectToday,
  selectedDateISO,
  onUploadReport,
  onDateChange,
}: {
  canEditDate: boolean;
  onSelectToday: () => void;
  selectedDateISO: string;
  onUploadReport?: MonitoringUploadRequest;
  onDateChange: (dateISO: string) => void;
}) {
  const capturePress = usePressCapture("CalendarMonitoringLogPanel");
  const t = useTranslations();
  const testResultsQuery = useTestResults();
  const cyclesQuery = useCycles();
  const createTestResult = useCreateTestResult();
  const updateTestResult = useUpdateTestResult();
  const deleteTestResult = useDeleteTestResult();
  const createMonitoringEncounter = useCreateCycleMonitoringEncounter();
  const updateMonitoringEncounter = useUpdateCycleMonitoringEncounter();
  const monitoringLedger = useRef(
    createBatchRetryLedger<MonitoringWriteReceipt>(),
  );
  const monitoringRequestIds = useRef(new Map<string, string>());
  // Receipts for the legacy per-biomarker writer: survives a partially-failed
  // save so the retry updates accepted rows instead of duplicating them.

  // Resolve the cycle whose date range contains the selected date so
  // the saved test-result rows attach to it (matching the canonical
  // monitoring flowsheet's date attribution).
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryDateISO, setEntryDateISO] = useState(selectedDateISO);

  useEffect(() => {
    if (!entryOpen) setEntryDateISO(selectedDateISO);
  }, [entryOpen, selectedDateISO]);

  const cycleForEntryDate = useMemo(() => {
    if (!cyclesQuery.data || !entryDateISO) return null;
    return cyclesQuery.data.find((c) => {
      const start = c.startDate ?? null;
      const end = c.endDate ?? null;
      if (!start) return false;
      if (entryDateISO < start) return false;
      if (end && entryDateISO > end) return false;
      return true;
    });
  }, [cyclesQuery.data, entryDateISO]);
  const cycleIdForDate = cycleForEntryDate?.id ?? null;

  const monitoringEncountersQuery =
    useCycleMonitoringEncounters(cycleIdForDate);
  const { data: monitoringEncounters } = monitoringEncountersQuery;
  const { data: testResults } = testResultsQuery;
  const monitoringQueries = [
    testResultsQuery,
    cyclesQuery,
    ...(cycleIdForDate ? [monitoringEncountersQuery] : []),
  ];
  const monitoringDataState: MonitoringDataState = !canEditDate
    ? null
    : monitoringQueries.some((query) => query.error)
      ? "unavailable"
      : monitoringQueries.some((query) => query.isLoading)
        ? "loading"
        : null;
  const isRetryingMonitoring = monitoringQueries.some(
    (query) => query.isFetching,
  );
  const retryMonitoring = () => {
    void testResultsQuery.refetch();
    void cyclesQuery.refetch();
    if (cycleIdForDate) void monitoringEncountersQuery.refetch();
  };

  const openEntry = () => {
    setEntryDateISO(selectedDateISO);
    setEntryOpen(true);
  };

  const handleEntryDateChange = (nextDateISO: string) => {
    setEntryDateISO(nextDateISO);
    onDateChange(nextDateISO);
  };

  const current = useMemo(
    () =>
      buildMonitoringDraftFromSources(
        monitoringEncounters,
        testResults,
        entryDateISO,
        cycleIdForDate,
        {
          cycleStartDate: cycleForEntryDate?.startDate,
          includeUnlinkedResults: cycleForEntryDate?.status === "open",
        },
      ),
    [
      cycleForEntryDate?.startDate,
      cycleForEntryDate?.status,
      cycleIdForDate,
      entryDateISO,
      monitoringEncounters,
      testResults,
    ],
  );
  const previous = useMemo(
    () =>
      buildPreviousMonitoringFromSources(
        monitoringEncounters,
        testResults,
        entryDateISO,
        cycleIdForDate,
        {
          cycleStartDate: cycleForEntryDate?.startDate,
          includeUnlinkedResults: cycleForEntryDate?.status === "open",
        },
      ),
    [
      cycleForEntryDate?.startDate,
      cycleForEntryDate?.status,
      cycleIdForDate,
      entryDateISO,
      monitoringEncounters,
      testResults,
    ],
  );

  if (!canEditDate) {
    return (
      <CalendarFutureDateMessage
        actionLabel={t("care.overview.calendar.monitoring_log.action_label")}
        onSelectToday={onSelectToday}
      />
    );
  }

  const handleSave = async (
    draft: MonitoringEntryDraft,
    sourceDocumentIds: string[],
  ) => {
    if (cycleIdForDate) {
      const existing = findMonitoringEncounterForDate(
        monitoringEncounters,
        entryDateISO,
      );
      const requestKey = `${cycleIdForDate}:${entryDateISO}:${JSON.stringify(draft)}`;
      const clientRequestId =
        monitoringRequestIds.current.get(requestKey) ??
        createCycleMonitoringRequestId();
      monitoringRequestIds.current.set(requestKey, clientRequestId);
      await persistMonitoringEncounter({
        draft,
        dateISO: entryDateISO,
        existing,
        clientRequestId,
        sourceDocumentIds,
        mutations: {
          create: (payload) =>
            createMonitoringEncounter.mutateAsync({
              cycleId: cycleIdForDate,
              payload,
            }),
          update: ({ encounterId, data }) =>
            updateMonitoringEncounter.mutateAsync({
              cycleId: cycleIdForDate,
              encounterId,
              payload: data,
            }),
        },
      });
      return;
    }

    // Per-biomarker settlement against a receipt ledger (#2236): a partial
    // failure keeps the accepted ids, so retrying the form re-runs only the
    // failed or edited values instead of duplicating rows the server already
    // accepted while the query cache catches up.
    await persistMonitoringDraft({
      draft,
      dateISO: entryDateISO,
      cycleId: cycleIdForDate,
      results: testResults,
      mutations: {
        create: (body) => createTestResult.mutateAsync(body),
        update: (arg) => updateTestResult.mutateAsync(arg),
        remove: (id) => deleteTestResult.mutateAsync(id),
      },
      ledger: monitoringLedger.current,
    });
  };

  return (
    <View style={{ gap: 11 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: semanticColors.onDark.text.secondary,
          fontWeight: "700",
        }}
      >
        {t("care.overview.calendar.monitoring_log.subtitle")}
      </Text>
      {monitoringDataState ? (
        <CalendarMonitoringDataStatus
          state={monitoringDataState}
          isRetrying={isRetryingMonitoring}
          onRetry={retryMonitoring}
        />
      ) : (
        <>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {MONITORING_SUMMARY_FIELDS.map((field) => {
              const display = monitoringSummaryValue(field, current);
              const isEmpty = display === "";
              return (
                <Pressable
                  key={field.key}
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        `${t(field.labelKey)}. ${isEmpty ? t(field.emptyValueKey) : display}`,
                      ),
                      "open_monitoring_entry",
                    );
                    openEntry();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`${t(field.labelKey)}. ${isEmpty ? t(field.emptyValueKey) : display}`}
                  style={({ pressed }) => ({
                    ...cardSurface({ variant: "panel", tone: "onDark" }),
                    ...(pressed
                      ? { backgroundColor: PANEL_ON_DARK_PRESSED_FILL }
                      : null),
                    minWidth: "30%",
                    flexGrow: 1,
                    paddingHorizontal: 11,
                    paddingVertical: 10,
                    gap: 2,
                  })}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10.5,
                      lineHeight: 13,
                      color: semanticColors.onDark.text.tertiary,
                      fontWeight: "800",
                    }}
                  >
                    {t(field.labelKey)}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12.5,
                      lineHeight: 16,
                      color: isEmpty
                        ? semanticColors.onDark.text.tertiary
                        : semanticColors.onDark.text.primary,
                      fontWeight: "900",
                    }}
                  >
                    {isEmpty ? t(field.emptyValueKey) : display}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <GlassButton
            label={t("care.overview.calendar.monitoring_log.add_result")}
            action="calendar_date_action"
            onPress={openEntry}
            fill
            leading={
              <Plus
                size={13}
                color={semanticColors.onDark.text.primary}
                strokeWidth={2}
              />
            }
          />
          <MonitoringEntryModal
            open={entryOpen}
            dateISO={entryDateISO}
            current={current}
            previous={previous}
            onClose={() => setEntryOpen(false)}
            onDateChange={handleEntryDateChange}
            onSave={handleSave}
            onUploadReport={onUploadReport}
          />
        </>
      )}
    </View>
  );
}

function CalendarScheduleDataStatus({
  state,
  isRetrying,
  onRetry,
  staleMessage,
}: {
  state: Exclude<ScheduleDataState, null>;
  isRetrying: boolean;
  onRetry: () => void;
  staleMessage?: string;
}) {
  const t = useTranslations();

  if (state === "loading") {
    return (
      <View
        testID="care-schedule-read-loading"
        accessibilityLiveRegion="polite"
        style={{
          minHeight: 132,
          alignItems: "center",
          justifyContent: "center",
          gap: 9,
          borderRadius: 14,
          backgroundColor: semanticColors.onDark.fill.subtle,
          paddingHorizontal: 14,
          paddingVertical: 18,
        }}
      >
        <LoadingState
          appearance="dark"
          caption={t("care.overview.calendar.schedule_log.read_loading")}
          style={{ paddingVertical: 0 }}
        />
      </View>
    );
  }

  return (
    <ClinicalDataStatus
      state={state === "stale" ? "stale" : "unavailable"}
      appearance="dark"
      title={
        state === "stale"
          ? undefined
          : t("care.overview.calendar.schedule_log.read_unavailable_title")
      }
      message={
        state === "stale"
          ? (staleMessage ??
            t("care.overview.calendar.schedule_log.read_stale_body"))
          : t("care.overview.calendar.schedule_log.read_unavailable_body")
      }
      retryLabel={t("clinical_data.retry")}
      retryingLabel={t("clinical_data.retrying")}
      onRetry={onRetry}
      isRetrying={isRetrying}
      embedded
      testID={
        state === "stale"
          ? "care-schedule-read-stale"
          : "care-schedule-read-unavailable"
      }
    />
  );
}

function CalendarScheduleLogPanel({
  selectedRows,
  canLogSelectedDay,
  scheduleDataState,
  isRetryingSchedule,
  onRetrySchedule,
  onOpenMedicationFactSheet,
  onOpenAppointment,
  onAddMedication,
  onAddAppointment,
}: {
  selectedRows: TodayRow[];
  canLogSelectedDay: boolean;
  scheduleDataState: ScheduleDataState;
  isRetryingSchedule: boolean;
  onRetrySchedule: () => void;
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenAppointment: (row: TodayRow) => void;
  onAddMedication: () => void;
  onAddAppointment: () => void;
}) {
  const t = useTranslations();
  const [medicationLogReadState, setMedicationLogReadState] =
    useState<MedicationLogReadState | null>(null);
  const handleMedicationLogReadStateChange = useCallback(
    (state: MedicationLogReadState) => setMedicationLogReadState(state),
    [],
  );
  const scheduledRows = selectedRows.filter(
    (row) => row.kind === "medication" || row.kind === "appointment",
  );
  const hasMedicationRows = scheduledRows.some(
    (row) => row.kind === "medication",
  );
  const medicationLogReadError =
    canLogSelectedDay &&
    hasMedicationRows &&
    medicationLogReadState?.status === "error";
  const effectiveScheduleDataState: ScheduleDataState =
    scheduleDataState ?? (medicationLogReadError ? "stale" : null);
  const effectiveIsRetryingSchedule =
    isRetryingSchedule ||
    (medicationLogReadError && (medicationLogReadState?.isRetrying ?? false));
  const retryScheduleReads = () => {
    if (scheduleDataState !== null) onRetrySchedule();
    if (medicationLogReadError) medicationLogReadState?.retry();
  };

  return (
    <View style={{ gap: 11 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: semanticColors.onDark.text.secondary,
          fontWeight: "700",
        }}
      >
        {t("care.overview.calendar.schedule_log.subtitle")}
      </Text>
      <View style={{ flexDirection: "row", gap: 8 }}>
        <GlassButton
          label={t("care.overview.calendar.schedule_log.add_medication")}
          action="calendar_date_action"
          onPress={onAddMedication}
          fill
          leading={
            <Pill
              size={13}
              color={semanticColors.onDark.text.primary}
              strokeWidth={2}
            />
          }
        />
        <GlassButton
          label={t("care.overview.calendar.schedule_log.add_visit")}
          action="calendar_date_action"
          onPress={onAddAppointment}
          fill
          leading={
            <CalendarPlus
              size={13}
              color={semanticColors.onDark.text.primary}
              strokeWidth={2}
            />
          }
        />
      </View>
      <View style={{ gap: 8 }}>
        {effectiveScheduleDataState === "loading" ||
        effectiveScheduleDataState === "unavailable" ? (
          <CalendarScheduleDataStatus
            state={effectiveScheduleDataState}
            isRetrying={effectiveIsRetryingSchedule}
            onRetry={retryScheduleReads}
          />
        ) : (
          <>
            {effectiveScheduleDataState === "stale" ? (
              <CalendarScheduleDataStatus
                state={effectiveScheduleDataState}
                isRetrying={effectiveIsRetryingSchedule}
                onRetry={retryScheduleReads}
                staleMessage={
                  medicationLogReadError && scheduleDataState === null
                    ? t(
                        "care.overview.calendar.schedule_log.read_medication_log_stale_body",
                      )
                    : undefined
                }
              />
            ) : null}
            {scheduledRows.length > 0 ? (
              <TodayList
                rows={scheduledRows}
                showCompletion={canLogSelectedDay}
                onOpenMedicationFactSheet={onOpenMedicationFactSheet}
                onOpenAppointment={onOpenAppointment}
                completionSource="care"
                rowAppearance="calendar-glass"
                onMedicationLogReadStateChange={
                  handleMedicationLogReadStateChange
                }
              />
            ) : (
              <View
                style={{
                  borderRadius: 14,
                  backgroundColor: semanticColors.onDark.fill.subtle,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                }}
              >
                <SupportingSubtitleText
                  style={{
                    color: semanticColors.onDark.text.secondary,
                    marginTop: 0,
                  }}
                >
                  {t("care.overview.calendar.schedule_log.empty")}
                </SupportingSubtitleText>
              </View>
            )}
          </>
        )}
      </View>
    </View>
  );
}

/**
 * Why the day panel is empty — the one thing on this card that has to be
 * readable, and it was not.
 *
 * Both lines were `rgba(255,247,239,…)`: the app's cream, at 82% and 68%
 * opacity. That is right on the dark logging surface and invisible on the
 * light one, and this card renders on both — once under the calendar's tab
 * row, once inside the symptom log. One colour could not serve both, so the
 * card is told which surface it is on.
 */
function CalendarNoSelectedDate({
  surface,
}: {
  /** `"card"` is the light calendar card; `"log"` is the dark logging sheet. */
  surface: "card" | "log";
}) {
  const t = useTranslations();
  const onLog = surface === "log";
  return (
    <View
      testID="care-calendar-no-selected-day"
      style={{
        borderRadius: 14,
        backgroundColor: semanticColors.onDark.fill.subtle,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 4,
      }}
    >
      <EmptyStateTitleText
        style={{
          color: onLog ? semanticColors.onDark.text.primary : TEXT,
        }}
      >
        {t("care.overview.calendar.no_selected_day.select_day_to_review")}
      </EmptyStateTitleText>
      <EmptyStateSubtitleText
        style={{
          marginTop: 0,
          color: onLog ? semanticColors.onDark.text.secondary : TEXT_MID,
        }}
      >
        {t(
          "care.overview.calendar.no_selected_day.choose_day_for_care_details",
        )}
      </EmptyStateSubtitleText>
    </View>
  );
}

function CalendarFutureDateMessage({
  actionLabel,
  onSelectToday,
}: {
  actionLabel: string;
  onSelectToday: () => void;
}) {
  const capturePress = usePressCapture("CalendarFutureDateMessage");
  const t = useTranslations();
  return (
    <View
      style={{
        borderRadius: 14,
        backgroundColor: semanticColors.onDark.fill.subtle,
        paddingHorizontal: 12,
        paddingVertical: 10,
      }}
    >
      <View style={{ gap: 8 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            color: semanticColors.onDark.text.secondary,
            fontWeight: "700",
          }}
        >
          {t("care.overview.calendar.monitoring_log.future_date", {
            params: { action: actionLabel },
          })}
        </Text>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 5,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: semanticColors.onDark.text.secondary,
              fontWeight: "700",
            }}
          >
            {t("care.overview.calendar.check_in.choose")}
          </Text>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(
                  t("care.overview.calendar.check_in.select_today_a11y"),
                ),
                "select_today",
              );
              onSelectToday();
            }}
            accessibilityRole="button"
            accessibilityLabel={t(
              "care.overview.calendar.check_in.select_today_a11y",
            )}
            style={({ pressed }) => ({
              borderRadius: 999,
              backgroundColor: "#FFF7EF",
              paddingHorizontal: 9,
              paddingVertical: 4,
              opacity: pressed ? 0.72 : 1,
            })}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11.5,
                lineHeight: 14,
                color: "#5A3125",
                fontWeight: "900",
              }}
            >
              {t("care.overview.calendar.check_in.today")}
            </Text>
          </Pressable>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: semanticColors.onDark.text.secondary,
              fontWeight: "700",
            }}
          >
            {t("care.overview.calendar.check_in.or_past_day")}
          </Text>
        </View>
      </View>
    </View>
  );
}

function CheckInGroup({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: LucideIcon;
  children: ReactNode;
}) {
  return (
    <View style={{ gap: 8 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        }}
      >
        {Icon ? (
          <Icon
            size={12}
            color={semanticColors.onDark.text.tertiary}
            strokeWidth={1.8}
          />
        ) : null}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11.5,
            lineHeight: 15,
            color: semanticColors.onDark.text.tertiary,
            fontWeight: "800",
            letterSpacing: 0,
          }}
        >
          {title}
        </Text>
      </View>
      {children}
    </View>
  );
}

function CheckInChip({
  label,
  emoji,
  active,
  disabled = false,
  onPress,
}: {
  label: string;
  emoji?: string;
  active: boolean;
  disabled?: boolean;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("CheckInChip");
  return (
    <Pressable
      onPress={
        disabled
          ? undefined
          : () => {
              capturePress(pressLabel(label), "check_in_chip");
              onPress();
            }
      }
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: active, disabled }}
      style={({ pressed }) => ({
        borderRadius: 999,
        paddingHorizontal: 11,
        paddingVertical: 8,
        backgroundColor: active ? "#FFF7EF" : semanticColors.onDark.fill.subtle,
        borderWidth: 1,
        borderColor: active
          ? "rgba(255,247,239,0.92)"
          : semanticColors.onDark.fill.strong,
        opacity: disabled ? 0.46 : pressed ? 0.76 : 1,
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
      })}
    >
      {emoji ? (
        <Text style={{ fontSize: 13.5, lineHeight: 16 }}>{emoji}</Text>
      ) : null}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 15,
          color: active ? "#5A3125" : semanticColors.onDark.text.primary,
          fontWeight: "800",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export function CalendarSymptomScreenOverlay({
  canSave,
  onCancel,
  onSave,
}: {
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  const t = useTranslations();
  const insets = useSafeAreaInsets();
  const bottomPadding = Math.max(insets.bottom, 12);
  const flip = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(flip, {
      toValue: 1,
      duration: 360,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [flip]);

  const rotateX = flip.interpolate({
    inputRange: [0, 1],
    outputRange: ["72deg", "0deg"],
  });

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        alignItems: "center",
        zIndex: 200,
        paddingBottom: bottomPadding,
      }}
    >
      <Animated.View
        style={[
          {
            width: 280,
            opacity: flip,
            transform: [{ perspective: 900 }, { rotateX }],
          },
          Platform.OS === "web"
            ? ({
                transformOrigin: "center bottom",
              } as Record<string, unknown>)
            : null,
        ]}
      >
        <FloatingActionDock>
          <FloatingActionDockButton
            label={t("care.overview.calendar.overlay.cancel")}
            accessibilityLabel={t("care.overview.calendar.overlay.cancel")}
            action="symptom_bar"
            flex={1}
            icon={
              <XCircle
                size={15}
                color={semanticColors.onDark.text.primary}
                strokeWidth={2}
              />
            }
            onPress={onCancel}
          />
          <FloatingActionDockButton
            label={t("care.overview.calendar.overlay.save")}
            accessibilityLabel={t("care.overview.calendar.overlay.save")}
            action="symptom_bar"
            flex={1.18}
            variant="selected"
            icon={<CheckCircle2 size={15} color="#5A3125" strokeWidth={2.2} />}
            disabled={!canSave}
            onPress={onSave}
          />
        </FloatingActionDock>
      </Animated.View>
    </View>
  );
}

export function useCalendarLoggingTabBarOverride(
  overlay: CalendarSymptomOverlayState | null,
  onOverlayActiveChange?: (active: boolean) => void,
) {
  const setTabBarOverride = useTabBarOverride();

  useEffect(() => {
    if (!overlay) {
      onOverlayActiveChange?.(false);
      setTabBarOverride(null);
      return;
    }

    onOverlayActiveChange?.(true);
    setTabBarOverride(
      <CalendarSymptomScreenOverlay
        canSave={overlay.canSave}
        onCancel={overlay.onCancel}
        onSave={overlay.onSave}
      />,
    );

    return () => setTabBarOverride(null);
  }, [onOverlayActiveChange, overlay, setTabBarOverride]);
}

function SavedCheckInSummary({
  checkIn,
}: {
  checkIn: DailyCheckInDraft | undefined;
}) {
  const t = useTranslations();
  if (!checkIn) return null;
  const bleedingLabels = bleedingOptions
    .filter(
      (option) =>
        option.value !== "none" && checkIn.bleeding.includes(option.value),
    )
    .map((option) => t(option.labelKey));
  const details = [
    bleedingLabels.length > 0 ? bleedingLabels.join(" + ") : null,
    checkIn.symptoms.length > 0
      ? t(
          checkIn.symptoms.length === 1
            ? "care.overview.calendar.summary.symptom_one"
            : "care.overview.calendar.summary.symptom_other",
          { params: { count: String(checkIn.symptoms.length) } },
        )
      : null,
    checkIn.notes.trim().length > 0
      ? t("care.overview.calendar.summary.notes_added")
      : null,
  ].filter(Boolean);

  return (
    <View
      style={{
        paddingVertical: 4,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
      }}
    >
      <CheckCircle2 size={18} color={ACCENT} strokeWidth={2.1} />
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13.5,
            lineHeight: 17,
            color: TEXT,
            fontWeight: "900",
          }}
        >
          {t("care.overview.calendar.summary.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11.5,
            lineHeight: 15,
            color: TEXT_MID,
            marginTop: 1,
          }}
        >
          {details.length > 0
            ? details.join(" · ")
            : t("care.overview.calendar.summary.no_symptoms")}
        </Text>
      </View>
    </View>
  );
}

function CalendarEmptyState({
  selectedDate,
  monthName,
}: {
  selectedDate: string;
  monthName: string;
}) {
  const t = useTranslations();
  return (
    <View style={{ paddingVertical: 4, gap: 4 }}>
      <EmptyStateTitleText>
        {t("care.overview.calendar.empty.nothing_scheduled_on_date", {
          params: {
            monthName: translateMonthName(t, monthName),
            date: selectedDate,
          },
        })}
      </EmptyStateTitleText>
      <EmptyStateSubtitleText style={{ marginTop: 0 }}>
        {t("care.overview.calendar.empty.add_from_quick_actions")}
      </EmptyStateSubtitleText>
    </View>
  );
}

function CalendarNavButton({
  icon,
  label,
  onPress,
  disabled = false,
}: {
  icon: ReactNode;
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <IconButton
      accessibilityLabel={label}
      action="calendar_nav"
      onPress={onPress}
      disabled={disabled}
      size={24}
      pressedBackgroundColor={semanticColors.overlay.pressed}
      disabledOpacity={0.32}
      pressedOpacity={0.76}
    >
      {icon}
    </IconButton>
  );
}

type TestingActionCardProps = {
  title: string;
  subtitle: string;
  organicIcon: OrganicMiniIconKind;
  onPress: () => void;
};

function TestingActionCard({
  title,
  subtitle,
  organicIcon,
  onPress,
}: TestingActionCardProps) {
  const capturePress = usePressCapture("TestingActionCard");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(title), "testing_action_card");
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={title}
      style={({ pressed }) => ({
        ...cardSurface(),
        flex: 1,
        minWidth: 0,
        minHeight: 118,
        padding: 14,
        justifyContent: "space-between",
        opacity: pressed ? 0.82 : 1,
        transform: [{ translateY: pressed ? 1 : 0 }],
      })}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <View style={{ width: 36, height: 36, justifyContent: "center" }}>
          <OrganicMiniIcon kind={organicIcon} size={36} />
        </View>
        <ChevronRight size={19} color={TEXT_LIGHT} strokeWidth={1.7} />
      </View>

      <View style={{ flex: 1, minWidth: 0, marginTop: 14 }}>
        <CardTitleText>{title}</CardTitleText>
        <CardSubtitleText>{subtitle}</CardSubtitleText>
      </View>
    </Pressable>
  );
}

function TestingActionsCard({
  navigation,
  topSpacing = 12,
}: {
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  topSpacing?: number;
}) {
  const t = useTranslations();
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const openTesting = () => {
    rootNavigation?.navigate("Access", {
      screen: "Testing",
      params: { returnTab: "Care" },
    });
  };
  const openTestsLibrary = () => {
    rootNavigation?.navigate("Learn", {
      screen: "Tests",
      params: { returnTab: "Care" },
    });
  };

  return (
    <View style={{ flexDirection: "row", gap: 10, marginTop: topSpacing }}>
      <TestingActionCard
        title={t("care.overview.testing_actions.order_title")}
        subtitle={t("care.overview.testing_actions.order_subtitle")}
        organicIcon="panel"
        onPress={openTesting}
      />
      <TestingActionCard
        title={t("care.overview.testing_actions.learn_title")}
        subtitle={t("care.overview.testing_actions.learn_subtitle")}
        organicIcon="amh"
        onPress={openTestsLibrary}
      />
    </View>
  );
}

function TestingAndLabsSection({
  navigation,
  biomarkerCount,
  measurementCount,
  onUploadResults,
}: {
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  biomarkerCount: number;
  measurementCount: number;
  onUploadResults: () => void;
}) {
  const t = useTranslations();
  const hasTestResults = biomarkerCount > 0;
  const markersLabel = t(
    biomarkerCount === 1
      ? "all_lab_results.markers_tested_one"
      : "all_lab_results.markers_tested_other",
    { params: { count: String(biomarkerCount) } },
  );
  const measurementsLabel = t(
    measurementCount === 1
      ? "all_lab_results.measurements_one"
      : "all_lab_results.measurements_other",
    { params: { count: String(measurementCount) } },
  );

  return (
    <ConceptSection
      title={t("care.overview.testing.title")}
      icon={FlaskConical}
      action={
        hasTestResults
          ? {
              label: t("care.overview.testing.all_tests"),
              onPress: () => navigation.navigate("AllLabResults"),
            }
          : undefined
      }
    >
      <View style={{ gap: 10 }}>
        <ActionRowCard
          organicIcon="medical-records"
          title={
            hasTestResults
              ? t("care.overview.testing.lab_results_title")
              : t("care.overview.testing.upload_results_title")
          }
          subtitle={
            hasTestResults
              ? `${markersLabel} · ${measurementsLabel}`
              : t("care.overview.testing.upload_results_subtitle")
          }
          trailing={
            hasTestResults ? (
              <StatusBadge label={`${biomarkerCount}`} />
            ) : undefined
          }
          onPress={() =>
            hasTestResults
              ? navigation.navigate("AllLabResults")
              : onUploadResults()
          }
        />
        <TestingActionsCard navigation={navigation} topSpacing={0} />
      </View>
    </ConceptSection>
  );
}

function CareGuidanceSection({
  cycleType,
  outcome,
  userAge,
  navigation,
  onAction,
}: {
  cycleType: CycleType | null | undefined;
  /**
   * The recorded ending of the cycle Care is showing, when there is one.
   *
   * This section used to take the cycle type and nothing else, so it could not
   * change after a result — a patient with a positive beta was still offered the
   * stimulation-and-transfer guide and an estimate of what her cycle might yield.
   * Recording a result no longer closes the cycle, so the type cannot stand in
   * for "where in the journey is this person".
   */
  outcome?: CycleOutcome | null;
  userAge?: number | null;
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  // The same resolver Home uses, so a refuted or withdrawn result cannot be
  // treated as a pregnancy on one surface and not the other.
  const clinicalState = resolvePostProcedureOutcomeClinicalState(outcome);
  const hasRecordedPregnancy = clinicalState === "pregnancy";
  const guidance = getCycleGuidanceContent(t, cycleType, hasRecordedPregnancy);
  const funnelVariant = getCareOutcomeFunnelVariant(
    cycleType,
    Boolean(outcome?.type),
  );

  return (
    <ConceptSection
      title={t("care.overview.good_to_know.title")}
      icon={Sparkles}
    >
      <View style={{ gap: 10 }}>
        <ActionRowCard
          organicIcon={guidance.organicIcon}
          title={guidance.title}
          subtitle={guidance.subtitle}
          onPress={() => {
            if (guidance.articleId) {
              rootNavigation?.navigate("Learn", {
                screen: "ArticleDetail",
                params: {
                  articleId: guidance.articleId,
                  returnTab: "Care",
                },
              });
            } else {
              rootNavigation?.navigate("Learn", { screen: "Learn" });
            }
          }}
        />
        <ActionRowCard
          organicIcon="amh"
          title={t("care.overview.good_to_know.tests_markers_title")}
          subtitle={t("care.overview.good_to_know.tests_markers_subtitle")}
          onPress={() =>
            rootNavigation?.navigate("Learn", {
              screen: "Tests",
              params: { returnTab: "Care" },
            })
          }
        />
        {funnelVariant ? (
          <CareOutcomeEstimateFunnelCard
            variant={funnelVariant}
            userAge={userAge}
            onAction={onAction}
          />
        ) : null}
      </View>
    </ConceptSection>
  );
}

function CareOutcomeEstimateFunnelCard({
  variant,
  userAge,
  onAction,
}: {
  variant: "egg-freezing" | "embryo-freezing";
  userAge?: number | null;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);
  const isEmbryoFreezing = variant === "embryo-freezing";
  const expandLabel = expanded
    ? t("care.outcome_funnel_card.collapse")
    : t("care.outcome_funnel_card.expand");
  const a11ySubject = isEmbryoFreezing
    ? t("care.outcome_funnel_card.embryo_a11y")
    : t("care.outcome_funnel_card.egg_a11y");

  return (
    <CardSurface style={{ padding: 15 }}>
      <Accordion
        type="multiple"
        value={expanded ? ["outcome-funnel"] : []}
        onValueChange={(value: string[]) =>
          setExpanded(value.includes("outcome-funnel"))
        }
      >
        <AccordionItem value="outcome-funnel">
          <AccordionTrigger
            accessibilityLabel={`${expandLabel} ${a11ySubject}`}
            action="toggle_outcome_funnel"
            className="gap-2.5 py-0"
          >
            <View
              style={{
                width: 38,
                height: 38,
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <OrganicMiniIcon
                kind={isEmbryoFreezing ? "genetics" : "eggs"}
                size={36}
              />
            </View>
            <View style={{ flex: 1 }}>
              <CardTitleText>
                {isEmbryoFreezing
                  ? t("care.outcome_funnel_card.embryo_title")
                  : t("care.outcome_funnel_card.egg_title")}
              </CardTitleText>
              <CardSubtitleText>
                {isEmbryoFreezing
                  ? t("care.outcome_funnel_card.embryo_subtitle")
                  : t("care.outcome_funnel_card.egg_subtitle")}
              </CardSubtitleText>
            </View>
          </AccordionTrigger>

          <AccordionContent className="pb-0">
            <View style={{ paddingTop: 12 }}>
              <OutcomeEstimateFunnelSection
                userAge={userAge ?? undefined}
                onAction={onAction}
                variant={variant}
              />
            </View>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </CardSurface>
  );
}

function MedicalHistoryProgressRing({
  percent,
  size = 52,
  strokeWidth = 5,
  trackColor = semanticColors.onDark.fill.subtle,
  progressColor = semanticColors.onDark.text.secondary,
  completeColor = semanticColors.onDark.text.primary,
  children,
}: {
  percent: number;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  progressColor?: string;
  completeColor?: string;
  children: ReactNode;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percent / 100);

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={percent >= 100 ? completeColor : progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View
        style={{
          position: "absolute",
          inset: 0,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {children}
      </View>
    </View>
  );
}

function MedicalHistoryCategoryIcon({
  category,
  onPress,
}: {
  category: MedicalHistoryProgressCategory;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("MedicalHistoryCategoryIcon");
  const t = useTranslations();
  const Icon = category.icon;
  const completed = category.items.filter(
    (item) => item.percent >= 0.999,
  ).length;
  const progressPercent =
    category.items.length > 0
      ? Math.round((completed / category.items.length) * 100)
      : 0;
  const isComplete = progressPercent >= 100;

  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            t(
              "med_history.overview.progress_card.accessibility_open_category",
              { params: { title: category.title } },
            ),
          ),
          "open_medical_history_category",
        );
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={t(
        "med_history.overview.progress_card.accessibility_open_category",
        { params: { title: category.title } },
      )}
      style={({ pressed }) => ({
        width: 102,
        alignItems: "center",
        gap: 7,
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <MedicalHistoryProgressRing
        percent={progressPercent}
        size={42}
        strokeWidth={2}
        trackColor={semanticColors.onDark.fill.subtle}
        progressColor={semanticColors.onDark.text.tertiary}
        completeColor={semanticColors.onDark.text.secondary}
      >
        {isComplete ? (
          <CheckCircle2
            size={14}
            color={semanticColors.onDark.text.secondary}
            strokeWidth={2}
          />
        ) : (
          <Icon
            size={14}
            color={semanticColors.onDark.text.tertiary}
            strokeWidth={1.75}
          />
        )}
      </MedicalHistoryProgressRing>
      <Text
        className="font-dm-sans"
        numberOfLines={2}
        style={{
          minHeight: 28,
          fontSize: 10.5,
          lineHeight: 13,
          color: semanticColors.onDark.text.tertiary,
          fontWeight: "700",
          textAlign: "center",
        }}
      >
        {category.title}
      </Text>
    </Pressable>
  );
}

function CareMedicalHistoryProgressCard({
  navigation,
}: {
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
}) {
  const t = useTranslations();
  const { data: patient, isLoading } = usePatient();

  const completionData = useMemo(() => {
    if (!patient) return null;
    return getMedicalHistoryProgressSummary(patient).completionData;
  }, [patient]);

  const categories = useMemo<MedicalHistoryProgressCategory[]>(() => {
    if (!completionData) return [];
    return getVisibleMedicalHistoryGroups(patient).map((group) => ({
      key: group.key,
      title: t(group.titleKey),
      icon: group.icon,
      items: group.sections.map((section) => {
        const completion = completionData[section.key];
        return {
          sectionKey: section.reviewKey,
          title: t(section.titleKey),
          percent: completion.percent,
          route: section.route as MedicalHistoryFlowRoute,
          icon: section.icon,
        };
      }),
    }));
  }, [completionData, patient, t]);

  const items = categories.flatMap((category) => category.items);
  const completedSections = items.filter(
    (item) => item.percent >= 0.999,
  ).length;
  const totalSections =
    items.length || getMedicalHistoryProgressSummary(patient).totalSections;
  const progressPercent =
    totalSections > 0
      ? Math.round((completedSections / totalSections) * 100)
      : 0;
  const hasNoProgress = completedSections === 0;

  const openSection = (item: MedicalHistoryProgressItem | undefined) => {
    if (!item) {
      navigation.navigate("MedicalHistory");
      return;
    }
    if (item.percent >= 0.999) {
      // Completed section — show review. Fertility-treatments opts out to
      // the canonical past-cycles surface (AllPastCyclesScreen) so we don't
      // duplicate that view as chips.
      if (item.sectionKey === "fertility-treatments") {
        navigation.navigate("AllPastCycles");
      } else {
        navigation.navigate("SectionReview", { sectionKey: item.sectionKey });
      }
      return;
    }
    navigation.navigate(item.route);
  };

  const openCategory = (category: MedicalHistoryProgressCategory) => {
    const nextItem =
      category.items.find((item) => item.percent < 0.999) ?? category.items[0];
    openSection(nextItem);
  };

  const openProgressCard = () => {
    navigation.navigate(hasNoProgress ? "BasicInfoFlow" : "MedicalHistory");
  };

  return (
    <CardSurface
      variant="feature"
      style={{
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
      }}
    >
      <Image
        source={silkBrown}
        resizeMode="cover"
        style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
      />
      <View
        style={{
          padding: 17,
          backgroundColor: semanticColors.onDark.fill.subtle,
          borderRadius: FEATURE_CARD_RADIUS,
          borderWidth: 1,
          borderColor: semanticColors.onDark.fill.strong,
          ...(Platform.OS === "web"
            ? ({
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
              } as Record<string, string>)
            : {}),
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 13 }}>
          <MedicalHistoryProgressRing
            percent={isLoading ? 0 : progressPercent}
            size={64}
            strokeWidth={6}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                color: semanticColors.onDark.text.primary,
                fontWeight: "700",
              }}
            >
              {isLoading ? "--" : `${progressPercent}%`}
            </Text>
          </MedicalHistoryProgressRing>

          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 19,
                lineHeight: 24,
                color: semanticColors.onDark.text.primary,
                fontWeight: "700",
                letterSpacing: -0.3,
              }}
            >
              {t("med_history.overview.progress_card.title")}
            </Text>

            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11.5,
                lineHeight: 16,
                color: semanticColors.onDark.text.secondary,
                marginTop: 7,
                fontWeight: "500",
              }}
            >
              {t("med_history.overview.progress_card.blurb")}
            </Text>
          </View>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 12,
            paddingTop: 16,
            paddingBottom: 2,
            paddingRight: 4,
          }}
        >
          {categories.map((category) => (
            <MedicalHistoryCategoryIcon
              key={category.key}
              category={category}
              onPress={() => openCategory(category)}
            />
          ))}
        </ScrollView>

        <GlassButton
          label={t(
            hasNoProgress
              ? "med_history.overview.progress_card.start"
              : "med_history.overview.progress_card.continue",
          )}
          accessibilityLabel={t(
            hasNoProgress
              ? "med_history.overview.progress_card.accessibility_start"
              : "med_history.overview.progress_card.accessibility_continue",
          )}
          action="open_progress_card"
          variant="shimmer"
          size="large"
          onPress={openProgressCard}
          trailing={
            <ChevronRight
              size={14}
              color={semanticColors.onDark.text.primary}
              strokeWidth={2}
            />
          }
          style={{ marginTop: 18 }}
        />
      </View>
    </CardSurface>
  );
}

function ConditionDiagnosisCard({
  condition,
  width,
  onViewDetails,
  equalizedHeight,
}: {
  condition: ConditionDiagnosisSnapshot;
  width: number;
  onViewDetails: (condition: ConditionDiagnosisSnapshot) => void;
  equalizedHeight?: number;
}) {
  const capturePress = usePressCapture("ConditionDiagnosisCard");
  const t = useTranslations();
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            t("care.conditions.detail_modal.view_details_a11y", {
              params: { title: condition.title },
            }),
          ),
          "view_condition_details",
        );
        onViewDetails(condition);
      }}
      accessibilityRole="button"
      accessibilityLabel={t("care.conditions.detail_modal.view_details_a11y", {
        params: { title: condition.title },
      })}
      style={({ pressed }) => ({
        ...cardSurface(),
        width,
        minHeight: equalizedHeight,
        padding: 14,
        gap: 12,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View>
        <CardTitleText numberOfLines={2}>{condition.title}</CardTitleText>
        <MetaText numberOfLines={2}>{condition.source}</MetaText>
        <View style={{ alignSelf: "flex-start", marginTop: 8 }}>
          <StatusBadge label={condition.status} />
        </View>
      </View>

      <Text
        className="font-dm-sans"
        numberOfLines={4}
        style={{
          fontSize: 12,
          lineHeight: 16,
          color: TEXT_MID,
          fontWeight: "600",
        }}
      >
        {condition.summary}
      </Text>
    </Pressable>
  );
}

function ConditionsAndDiagnosesCarousel({
  conditions,
  navigation,
  onViewDetails,
}: {
  conditions: ConditionDiagnosisSnapshot[];
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  onViewDetails: (condition: ConditionDiagnosisSnapshot) => void;
}) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const dismissalKey = `care.conditions.quick_suggestions.${
    patient?.gender === "AMAB" ? "amab" : "afab"
  }`;
  const {
    isDismissed: areQuickSuggestionsDismissed,
    dismiss: dismissQuickSuggestions,
    restore: restoreQuickSuggestions,
  } = useDismissible(dismissalKey);

  const openDiseaseHistoryFlow = () => {
    navigation.navigate("DiseaseHistoryFlow");
  };

  return (
    <CardSurface style={{ padding: 15, gap: 12 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <OrganicMiniIcon kind="medical-records" size={36} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <CardTitleText>
            {t("care.overview.conditions.section_title")}
          </CardTitleText>
          <CardSubtitleText>
            {t("care.overview.conditions.section_subtitle")}
          </CardSubtitleText>
        </View>
        <CompactActionButton
          onPress={openDiseaseHistoryFlow}
          label={t("care.overview.conditions.add_short")}
          action="add_condition"
          accessibilityLabel={t("care.overview.conditions.add_a11y")}
          size="sm"
          variant="secondary"
          leadingIcon={Plus}
        />
      </View>

      {conditions.length > 0 ? (
        <HorizontalSnapCarousel
          items={conditions}
          gap={10}
          fallbackCardWidth={236}
          getCardWidth={(containerWidth) =>
            Math.min(252, Math.max(222, containerWidth * 0.56))
          }
          keyExtractor={(condition) => condition.id}
          equalizeItemHeights
          measureKey="care-conditions-diagnoses"
          renderItem={(condition, width, _index, equalizedHeight) => (
            <ConditionDiagnosisCard
              condition={condition}
              width={width}
              onViewDetails={onViewDetails}
              equalizedHeight={equalizedHeight}
            />
          )}
        />
      ) : areQuickSuggestionsDismissed ? (
        <View style={{ gap: 12 }}>
          <View>
            <CardTitleText>
              {t("care.overview.conditions.suggestions_hidden_title")}
            </CardTitleText>
            <CardSubtitleText>
              {t("care.overview.conditions.suggestions_hidden_subtitle")}
            </CardSubtitleText>
          </View>
          <View style={{ flexDirection: "row", gap: 9 }}>
            <CompactActionButton
              label={t("care.overview.conditions.restore_suggestions")}
              accessibilityLabel={t(
                "care.overview.conditions.restore_suggestions_a11y",
              )}
              action="restore_quick_suggestions"
              variant="secondary"
              flexGrow={1}
              flexBasis={0}
              onPress={restoreQuickSuggestions}
            />
            <CompactActionButton
              label={t("care.overview.conditions.add_short")}
              accessibilityLabel={t("care.overview.conditions.add_a11y")}
              action="add_condition"
              variant="secondary"
              leadingIcon={Plus}
              flexGrow={1}
              flexBasis={0}
              onPress={openDiseaseHistoryFlow}
            />
          </View>
        </View>
      ) : (
        <ConditionsEmptyState
          onAddCondition={openDiseaseHistoryFlow}
          onNoCondition={dismissQuickSuggestions}
        />
      )}
    </CardSurface>
  );
}

type DiseaseCategory = NonNullable<
  NonNullable<
    NonNullable<Patient["medicalHistory"]>["diseases"]
  >[number]["category"]
>;

type ConditionChip =
  | {
      label: string;
      name: DiseaseType;
      category: DiseaseCategory;
      sex?: "AFAB" | "AMAB";
    }
  | { label: string; name: "hadVaricoceles"; sex?: "AFAB" | "AMAB" }
  | {
      label: string;
      name: "hasDifficultiesWithErection";
      sex?: "AFAB" | "AMAB";
    };

function getAfabChips(t: ReturnType<typeof useTranslations>): ConditionChip[] {
  return [
    {
      label: t("care.overview.conditions.pcos"),
      name: "pcos",
      category: "endocrine",
      sex: "AFAB",
    },
    {
      label: t("care.overview.conditions.endometriosis"),
      name: "endometriosis",
      category: "other",
      sex: "AFAB",
    },
    {
      label: t("care.overview.conditions.fibroids"),
      name: "fibroids",
      category: "other",
      sex: "AFAB",
    },
    {
      label: t("care.overview.conditions.low_ovarian_reserve"),
      name: "diminished_ovarian_reserve",
      category: "other",
      sex: "AFAB",
    },
    {
      label: t("care.overview.conditions.early_menopause"),
      name: "primary_ovarian_insufficiency",
      category: "other",
      sex: "AFAB",
    },
  ];
}

function getAmabChips(t: ReturnType<typeof useTranslations>): ConditionChip[] {
  return [
    {
      label: t("care.overview.conditions.varicocele"),
      name: "hadVaricoceles",
      sex: "AMAB",
    },
    {
      label: t("care.overview.conditions.klinefelter"),
      name: "klinefelter_syndrome",
      category: "genetic",
      sex: "AMAB",
    },
    {
      label: t("care.overview.conditions.y_microdeletion"),
      name: "y_chromosome_microdeletions",
      category: "genetic",
      sex: "AMAB",
    },
    {
      label: t("care.overview.conditions.undescended_testicles"),
      name: "undescended_testicles",
      category: "other",
      sex: "AMAB",
    },
    {
      label: t("care.overview.conditions.genital_tract_obstruction"),
      name: "genital_tract_obstruction",
      category: "other",
      sex: "AMAB",
    },
    {
      label: t("care.overview.conditions.retrograde_ejaculation"),
      name: "retrograde_ejaculation",
      category: "other",
      sex: "AMAB",
    },
    {
      label: t("care.overview.conditions.erectile_dysfunction"),
      name: "hasDifficultiesWithErection",
      sex: "AMAB",
    },
  ];
}

function getSharedChips(
  t: ReturnType<typeof useTranslations>,
): ConditionChip[] {
  return [
    {
      label: t("care.overview.conditions.thyroid"),
      name: "thyroid_disease",
      category: "endocrine",
    },
  ];
}

/**
 * A leading icon per suggested condition. The suggestions are a fixed list, so
 * each can carry its own; a condition missing here shows its label alone.
 */
const CONDITION_CHIP_ICONS: Partial<Record<ConditionChip["name"], LucideIcon>> =
  {
    pcos: CircleDot,
    // The endometriosis awareness ribbon.
    endometriosis: Ribbon,
    fibroids: Flower2,
    diminished_ovarian_reserve: Egg,
    primary_ovarian_insufficiency: Hourglass,
    hadVaricoceles: Waves,
    klinefelter_syndrome: Dna,
    y_chromosome_microdeletions: DnaOff,
    undescended_testicles: ArrowUpFromDot,
    genital_tract_obstruction: RouteOff,
    retrograde_ejaculation: Undo2,
    hasDifficultiesWithErection: HeartPulse,
    thyroid_disease: Activity,
  };

function ConditionsEmptyState({
  onAddCondition,
  onNoCondition,
}: {
  onAddCondition: () => void;
  onNoCondition: () => void;
}) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const updatePatient = useQueuedPatientUpdate();
  const acceptingKeys = useRef(new Set<string>());
  const [, setAcceptanceRevision] = useState(0);
  const [acceptanceFailed, setAcceptanceFailed] = useState(false);
  const patientFetching = useIsFetching({ queryKey: ["patient"] }) > 0;
  const userSex =
    patient?.gender === "AMAB"
      ? "AMAB"
      : patient?.gender === "AFAB"
        ? "AFAB"
        : null;

  const chips = [
    ...(userSex === "AMAB" ? getAmabChips(t) : getAfabChips(t)),
    ...getSharedChips(t),
  ];

  const diseases = patient?.medicalHistory?.diseases ?? [];
  const diseaseNames = new Set(diseases.map((d) => d.disease).filter(Boolean));

  const isActive = (chip: ConditionChip): boolean => {
    if (chip.name === "hadVaricoceles") {
      const val = patient?.medicalHistory?.hadVaricoceles;
      return val === "yes_diagnosed" || val === "yes_undiagnosed";
    }
    if (chip.name === "hasDifficultiesWithErection") {
      return (
        patient?.maleHistory?.sexualHealth?.hasDifficultiesWithErection === true
      );
    }
    // Some basic-info screens persist their answer to a flag field on
    // medicalHistory instead of (or in addition to) `diseases[]`. Treat
    // those flags as equivalent to a matching diseases entry so a chip
    // marked through the full flow shows as pre-selected here.
    if (chip.name === "endometriosis") {
      const val = patient?.medicalHistory?.hasEndometriosis;
      if (val === "yes_diagnosed" || val === "yes_undiagnosed") return true;
    }
    if (chip.name === "pcos") {
      if (patient?.medicalHistory?.suspectPCOS === true) return true;
    }
    return diseaseNames.has(chip.name);
  };

  // An optimistically-added disease has no `id` until the post-mutation
  // refetch assigns one. The backend only supports delete-by-id (there is no
  // name-keyed delete in updateMedicalHistory), so until that id arrives the
  // chip cannot be removed. We also treat the patient query's refetch window
  // as "still in flight" — otherwise the chip flashes to disabled (gray) for
  // the gap between the PATCH settling and the refetched id arriving.
  const isUnremovable = (chip: ConditionChip): boolean => {
    if (acceptingKeys.current.has(chip.name)) return true;
    if (chip.name === "hadVaricoceles") return false;
    if (chip.name === "hasDifficultiesWithErection") return false;
    if (!isActive(chip)) return false;
    if (patientFetching) return false;
    const existing = diseases.find((d) => d.disease === chip.name);
    return !existing?.id;
  };

  const toggle = (chip: ConditionChip) => {
    const key = chip.name;
    if (acceptingKeys.current.has(key)) return;

    const active = isActive(chip);
    const mhId = patient?.medicalHistory?.id;
    const persist = (input: UpdatePatientInput) => {
      setAcceptanceFailed(false);
      const started = startClinicalWriteAcceptance({
        activeKeys: acceptingKeys.current,
        key,
        accept: () => updatePatient.mutateAsync(input),
        onRejected: () => setAcceptanceFailed(true),
        onSettled: () => setAcceptanceRevision((revision) => revision + 1),
      });
      if (started) setAcceptanceRevision((revision) => revision + 1);
    };

    if (chip.name === "hadVaricoceles") {
      // Preserve a stored diagnosed/undiagnosed distinction: re-activating a
      // patient who previously had "yes_undiagnosed" must not silently
      // downgrade them to "yes_diagnosed" (which flips patientProfileMapper's
      // male_factor logic). Only default to "yes_diagnosed" when there was no
      // prior yes_* value.
      const prior = patient?.medicalHistory?.hadVaricoceles;
      const reactivated =
        prior === "yes_undiagnosed" ? "yes_undiagnosed" : "yes_diagnosed";
      persist({
        medicalHistory: {
          id: mhId,
          hadVaricoceles: active ? "no" : reactivated,
        },
      });
      return;
    }

    if (chip.name === "hasDifficultiesWithErection") {
      // Erectile dysfunction lives on maleHistory.sexualHealth (a boolean), not
      // on medicalHistory.diseases. Backend routes by maleHistory.id (update vs
      // create), so send it (and the sexualHealth id) when present.
      const maleHistoryId = patient?.maleHistory?.id;
      const sexualHealthId = patient?.maleHistory?.sexualHealth?.id;
      persist({
        maleHistory: {
          ...(maleHistoryId ? { id: maleHistoryId } : {}),
          sexualHealth: {
            ...(sexualHealthId ? { id: sexualHealthId } : {}),
            hasDifficultiesWithErection: !active,
          },
        },
      });
      return;
    }

    const current = patient?.medicalHistory?.diseases ?? [];
    if (active) {
      const existing = current.find((d) => d.disease === chip.name);
      // For conditions also persisted via flag fields on medicalHistory
      // (set through the full basic-info flow), clear the flag on toggle-off
      // so the chip actually deselects instead of re-hydrating.
      const extraFlags: Record<string, unknown> = {};
      if (
        chip.name === "endometriosis" &&
        patient?.medicalHistory?.hasEndometriosis !== "no" &&
        patient?.medicalHistory?.hasEndometriosis != null
      ) {
        extraFlags.hasEndometriosis = "no";
      }
      if (
        chip.name === "pcos" &&
        patient?.medicalHistory?.suspectPCOS === true
      ) {
        extraFlags.suspectPCOS = false;
      }
      if (existing?.id) {
        persist({
          medicalHistory: {
            id: mhId,
            diseases: [{ id: existing.id, op: "delete" }],
            ...extraFlags,
          },
        });
      } else if (Object.keys(extraFlags).length > 0) {
        // Only the flag is set (no diseases entry). Clear the flag.
        persist({
          medicalHistory: {
            id: mhId,
            ...extraFlags,
          },
        });
      } else {
        // No id yet (optimistic add not reconciled): nothing to delete by id,
        // and the backend has no name-keyed delete. The chip stays disabled
        // via isUnremovable until the queued add returns a server id.
        return;
      }
    } else {
      persist({
        medicalHistory: {
          id: mhId,
          diseases: [{ disease: chip.name, category: chip.category }],
        },
      });
    }
  };

  return (
    <CardSurface variant="prompt" style={{ padding: 14, gap: 12 }}>
      <View>
        <CardTitleText>
          {t("care.overview.conditions.empty_title")}
        </CardTitleText>
        <CardSubtitleText>
          {t("care.overview.conditions.empty_subtitle")}
        </CardSubtitleText>
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
        {chips.map((chip) => (
          <MoonFilterChip
            key={chip.name}
            label={chip.label}
            icon={CONDITION_CHIP_ICONS[chip.name]}
            selected={isActive(chip)}
            disabled={isUnremovable(chip)}
            onPress={() => toggle(chip)}
            action="condition_chip"
            tone={isActive(chip) ? "outline" : "default"}
            size="sm"
            showCheck={false}
            style={
              isActive(chip) ? undefined : { backgroundColor: "transparent" }
            }
          />
        ))}
        {/* Answers the question in the same row: none of these apply. It hides
            the suggestions, as the button it replaces did. */}
        <MoonFilterChip
          label={t("care.overview.conditions.modal.no_conditions")}
          accessibilityLabel={t(
            "care.overview.conditions.modal.condition_none_suggestions_a11y",
          )}
          icon={CircleSlash2}
          selected={false}
          onPress={onNoCondition}
          action="no_condition"
          tone="default"
          size="sm"
          showCheck={false}
          style={{ backgroundColor: "transparent" }}
        />
      </View>
      {acceptanceFailed ? (
        <FieldMessage>
          {t("med_history.overview.sync_status.acceptance_failed")}
        </FieldMessage>
      ) : null}

      <View style={{ flexDirection: "row" }}>
        <CompactActionButton
          label={t("care.overview.conditions.modal.condition_add")}
          action="add_condition"
          variant="secondary"
          leadingIcon={Plus}
          onPress={onAddCondition}
        />
      </View>
    </CardSurface>
  );
}

function ConditionDetailBlock({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <View style={{ gap: 7 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        {icon}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 15,
            color: TEXT,
            fontWeight: "900",
          }}
        >
          {title}
        </Text>
      </View>
      {children}
    </View>
  );
}

function ConditionBulletList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 5 }}>
      {items.map((item) => (
        <View
          key={item}
          style={{ flexDirection: "row", gap: 7, alignItems: "flex-start" }}
        >
          <View
            style={{
              width: 4,
              height: 4,
              borderRadius: 999,
              marginTop: 7,
              backgroundColor: ACCENT,
            }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 11.5,
              lineHeight: 16,
              color: TEXT_MID,
              fontWeight: "600",
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function ConditionDetailModal({
  condition,
  onClose,
  onAddRecords,
}: {
  condition: ConditionDiagnosisSnapshot | null;
  onClose: () => void;
  onAddRecords: (condition: ConditionDiagnosisSnapshot) => void;
}) {
  const t = useTranslations();
  return (
    <Dialog
      size="wide"
      visible={condition !== null}
      onClose={onClose}
      dismissOnBackdropTap={false}
    >
      <CloseButton
        accessibilityLabel={t("care.conditions.detail_modal.close_a11y")}
        action="close_condition_detail"
        onPress={onClose}
        style={{ position: "absolute", top: 12, right: 12, zIndex: 1 }}
      />
      {condition ? (
        <>
          <ScrollView
            style={{ flexShrink: 1 }}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ gap: 12 }}
          >
            {/* An article-like detail opens with its illustration, so the
                title and its tags are centred under it (#3848). */}
            <View style={{ alignItems: "center" }}>
              <DialogIcon
                source={CONDITION_DETAIL_ILLUSTRATION}
                width={54}
                height={73}
                style={{ marginBottom: 10 }}
              />
              <DialogHeader align="center" title={condition.title} />
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  gap: 6,
                  marginTop: 8,
                }}
              >
                <StatusBadge label={condition.status} />
                {condition.code ? <StatusBadge label={condition.code} /> : null}
              </View>
              <MetaText style={{ marginTop: 8, textAlign: "center" }}>
                {t("care.conditions.detail_modal.source_label")}:{" "}
                {condition.source}
              </MetaText>
            </View>

            <DialogBodyText style={{ marginTop: 0 }}>
              {condition.summary}
            </DialogBodyText>

            <ConditionDetailBlock
              icon={<HeartPulse size={15} color={ACCENT} strokeWidth={1.8} />}
              title={t("care.conditions.detail_modal.care_impact")}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11.5,
                  lineHeight: 16,
                  color: TEXT_MID,
                  fontWeight: "600",
                }}
              >
                {condition.careImpact}
              </Text>
            </ConditionDetailBlock>

            <ConditionDetailBlock
              icon={<Baby size={15} color={ACCENT} strokeWidth={1.8} />}
              title={t("care.conditions.detail_modal.fertility_context")}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11.5,
                  lineHeight: 16,
                  color: TEXT_MID,
                  fontWeight: "600",
                }}
              >
                {condition.fertilityImpact}
              </Text>
            </ConditionDetailBlock>

            <ConditionDetailBlock
              icon={<ShieldCheck size={15} color={ACCENT} strokeWidth={1.8} />}
              title={t("care.conditions.detail_modal.insurance_and_records")}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11.5,
                  lineHeight: 16,
                  color: TEXT_MID,
                  fontWeight: "600",
                  marginBottom: 7,
                }}
              >
                {condition.insuranceImpact}
              </Text>
              <ConditionBulletList items={condition.records} />
            </ConditionDetailBlock>

            <ConditionDetailBlock
              icon={<ShieldAlert size={15} color={ACCENT} strokeWidth={1.8} />}
              title={t("care.conditions.detail_modal.what_to_watch")}
            >
              <ConditionBulletList items={condition.watchItems} />
            </ConditionDetailBlock>
          </ScrollView>

          <View style={{ flexDirection: "row", gap: 9, marginTop: 12 }}>
            <CompactActionButton
              label={t("care.conditions.detail_modal.add_records_cta")}
              leadingIcon={FilePlus2}
              iconSize={14}
              iconStrokeWidth={1.8}
              variant="secondary"
              action="condition_action"
              style={{ flex: 1 }}
              onPress={() => onAddRecords(condition)}
            />
            <CompactActionButton
              label={t("care.conditions.detail_modal.done_cta")}
              leadingIcon={CheckCircle2}
              iconSize={14}
              iconStrokeWidth={1.8}
              variant="secondary"
              action="condition_action"
              style={{ flex: 1 }}
              onPress={onClose}
            />
          </View>
        </>
      ) : null}
    </Dialog>
  );
}

function CycleTeamSection({
  navigation,
  members,
  cycleId,
  clinicName,
  clinicContact,
}: {
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  members: CycleTeamMember[];
  cycleId?: string;
  clinicName?: string | null;
  clinicContact?: { phone?: string; portalUrl?: string };
}) {
  const t = useTranslations();
  const mainNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const rootNavigation =
    mainNavigation?.getParent<NavigationProp<RootStackParamList>>();
  const [careTeamModalVisible, setCareTeamModalVisible] = useState(false);
  const openCareTeam = () => {
    setCareTeamModalVisible(false);
    rootNavigation?.navigate("Profile", {
      screen: "ConnectionsDetail",
      params: { cycleId },
    });
  };

  return (
    <ConceptSection
      title={t("care.overview.team.section_title")}
      icon={HeartHandshake}
    >
      <CycleClinicTeamCard
        members={members}
        clinicName={clinicName}
        clinicContact={clinicContact}
        onEditTeam={
          cycleId ? () => setCareTeamModalVisible(true) : () => openCareTeam()
        }
        onAddProviderOrClinic={() =>
          rootNavigation?.navigate("Profile", {
            screen: "ConnectionsDetail",
            params: { cycleId },
          })
        }
        onAddProvider={() =>
          rootNavigation?.navigate("Profile", {
            screen: "ConnectionsDetail",
            params: { cycleId, initialKind: "provider" },
          })
        }
      />
      {cycleId ? (
        <CycleCareTeamModal
          visible={careTeamModalVisible}
          cycleId={cycleId}
          clinicName={clinicName}
          onClose={() => setCareTeamModalVisible(false)}
          onOpenCareTeam={openCareTeam}
        />
      ) : null}
    </ConceptSection>
  );
}

function getPharmacyToneStyles(tone: PharmacyFulfillmentTone) {
  if (tone === "pickup") {
    return {
      gradient: false,
      surface: semanticColors.action.tonal.background,
      border: semanticColors.border.strong,
      text: semanticColors.text.secondary,
    };
  }
  // Every other tone marks its steps in the accent: the reached markers take
  // the accent gradient, the line between them the solid accent.
  return {
    gradient: true,
    surface: ACCENT,
    border: ACCENT,
    text: ACCENT,
  };
}

function PharmacyRailStepMarker({
  Icon,
  active,
  isCurrent,
  tone,
}: {
  Icon: LucideIcon;
  active: boolean;
  isCurrent: boolean;
  tone: ReturnType<typeof getPharmacyToneStyles>;
}) {
  const filled = active && tone.gradient;
  return (
    <View
      style={{
        width: 28,
        height: 28,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: active
          ? tone.surface
          : semanticColors.action.quiet.background,
        borderWidth: filled ? 0 : 1,
        borderColor: active ? tone.surface : semanticColors.border.subtle,
      }}
    >
      {filled ? (
        <LinearGradient
          {...gradients.accent}
          style={[StyleSheet.absoluteFill, { borderRadius: 999 }]}
        />
      ) : null}
      {/* Above the gradient: on the web an absolutely placed layer otherwise
          paints over its earlier siblings. */}
      <View style={{ zIndex: 1 }}>
        <Icon
          size={13}
          color={
            active ? semanticColors.text.inverse : semanticColors.text.faint
          }
          strokeWidth={isCurrent ? 2.2 : 1.8}
        />
      </View>
    </View>
  );
}

function PharmacyFulfillmentRail({
  card,
}: {
  card: PharmacyFulfillmentCardData;
}) {
  const tone = getPharmacyToneStyles(card.tone);
  const stepWidth = 66;
  const markerCenter = stepWidth / 2;

  return (
    <View
      style={{
        position: "relative",
      }}
    >
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: markerCenter,
          right: markerCenter,
          top: 17.5,
          height: 1.5,
          borderRadius: 999,
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        {card.steps.slice(0, -1).map((step, index) => (
          <View
            key={`${card.id}-${step.label}-rail-segment`}
            style={{
              flex: 1,
              backgroundColor:
                index < card.currentStep
                  ? tone.border
                  : semanticColors.border.subtle,
            }}
          />
        ))}
      </View>
      <View
        style={{
          flexDirection: "row",
          paddingTop: 4,
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        {card.steps.map((step, index) => {
          const Icon = step.icon;
          const isComplete = index < card.currentStep;
          const isCurrent = index === card.currentStep;
          const isDoneTone = card.tone === "done" && index <= card.currentStep;
          const active = isComplete || isCurrent || isDoneTone;

          return (
            <View
              key={`${card.id}-${step.label}`}
              style={{
                width: stepWidth,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <PharmacyRailStepMarker
                Icon={Icon}
                active={active}
                isCurrent={isCurrent}
                tone={tone}
              />
              <Text
                className="font-dm-sans"
                numberOfLines={2}
                style={{
                  marginTop: 6,
                  minHeight: 24,
                  fontSize: 9.5,
                  lineHeight: 12,
                  color: active ? tone.text : TEXT_LIGHT,
                  fontWeight: isCurrent ? "900" : "700",
                  textAlign: "center",
                }}
              >
                {step.label}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function PharmacyFulfillmentSegmentedRail({
  card,
}: {
  card: PharmacyFulfillmentCardData;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        gap: 10,
      }}
    >
      {card.steps.map((step, index) => {
        const Icon = step.icon;
        const isComplete = index < card.currentStep;
        const isCurrent = index === card.currentStep;
        const isDoneTone = card.tone === "done" && index <= card.currentStep;
        const active = isComplete || isCurrent || isDoneTone;

        return (
          <View
            key={`${card.id}-segmented-${step.label}`}
            style={{
              flex: 1,
              minWidth: 0,
              position: "relative",
            }}
          >
            <View
              style={{
                minHeight: 86,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "space-between",
                paddingHorizontal: 6,
                paddingTop: 13,
                paddingBottom: 9,
                backgroundColor: active
                  ? "rgba(255,255,255,0.78)"
                  : semanticColors.surface.subtle,
              }}
            >
              <Icon
                size={14}
                color={active ? TEXT : semanticColors.text.faint}
                strokeWidth={isCurrent ? 2.5 : 1.8}
              />
              <View style={{ alignItems: "center", minHeight: 34 }}>
                <Text
                  className="font-dm-sans"
                  numberOfLines={2}
                  style={{
                    fontSize: 10,
                    lineHeight: 12,
                    color: active ? TEXT : TEXT_LIGHT,
                    fontWeight: isCurrent ? "900" : "700",
                    textAlign: "center",
                  }}
                >
                  {step.label}
                </Text>
                {step.dateLabel ? (
                  <Text
                    className="font-dm-sans"
                    numberOfLines={1}
                    style={{
                      marginTop: 3,
                      fontSize: 9,
                      lineHeight: 11,
                      color: active ? TEXT_LIGHT : semanticColors.text.faint,
                      fontWeight: isCurrent ? "800" : "700",
                      textAlign: "center",
                    }}
                  >
                    {step.dateLabel}
                  </Text>
                ) : null}
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function PharmacyFulfillmentHeaderIcon({
  tone,
}: {
  tone: PharmacyFulfillmentTone;
}) {
  if (tone === "delivery" || tone === "done") {
    return (
      <Image
        source={parcelBoxIcon}
        resizeMode="contain"
        style={{
          width: 28,
          height: 24,
          marginTop: -1,
          marginLeft: -3,
        }}
      />
    );
  }

  const Icon = tone === "attention" ? Pill : PackageCheck;
  return (
    <Icon
      size={19}
      color={tone === "attention" ? ACCENT : TEXT_MID}
      strokeWidth={1.55}
      style={{ marginTop: 1 }}
    />
  );
}

export function PharmacyActionCard({
  card,
  onOpenMedicationFactSheet,
  onDismiss,
  onCompleteAction,
}: {
  card: PharmacyFulfillmentCardData;
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onDismiss?: () => void;
  onCompleteAction?: () => void;
}) {
  const t = useTranslations();
  const primaryMedication = card.medications[0];
  const isRefill = card.kind === "refill-action";
  const [confirmed, setConfirmed] = useState(false);
  const displayCard: PharmacyFulfillmentCardData = confirmed
    ? {
        ...card,
        statusLabel: t("care.overview.pharmacy.confirmed_status_label"),
        timing:
          card.kind === "pickup-action"
            ? t("care.overview.pharmacy.confirmed_timing_pickup")
            : t("care.overview.pharmacy.confirmed_timing_delivery"),
        estimatedTime:
          card.kind === "pickup-action"
            ? t("care.overview.pharmacy.confirmed_estimated_pickup")
            : t("care.overview.pharmacy.confirmed_estimated_delivery"),
        tone: "done",
        currentStep: Math.max(0, card.steps.length - 1),
        detail: t("care.overview.pharmacy.confirmed_detail"),
      }
    : card;
  const headerForm: PharmacyShipmentMedication["form"] = isRefill
    ? "pill"
    : (primaryMedication?.form ?? "pill");
  const confirmationLabel = confirmed
    ? "Medications on hand"
    : card.confirmationLabel;
  const openMedicationFactSheet = (medication?: PharmacyShipmentMedication) => {
    if (!medication) return;
    onOpenMedicationFactSheet({
      item: getMedicationByBrandName(medication.name) ?? null,
      fallbackName: medication.name,
      userContext: {
        originalName: medication.name,
        dosage: medication.dosage,
      },
    });
  };

  return (
    <CardSurface style={{ padding: 14, gap: 13 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
        <MedFormIcon form={headerForm} size={28} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <CardTitleText>{displayCard.title}</CardTitleText>
            <StatusBadge label={displayCard.statusLabel} />
          </View>
          <CardSubtitleText>
            {displayCard.pharmacy} · {displayCard.timing}
          </CardSubtitleText>
        </View>
        {onDismiss ? (
          <IconButton
            onPress={onDismiss}
            accessibilityLabel={t("care.overview.pharmacy.hide_a11y", {
              params: { title: card.title },
            })}
            hitSlop={8}
            size={26}
            pressedOpacity={0.58}
          >
            <X size={15} color={TEXT_LIGHT} strokeWidth={1.8} />
          </IconButton>
        ) : null}
      </View>

      <View style={{ gap: 7 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 16,
            color: TEXT,
            fontWeight: "800",
          }}
        >
          {displayCard.estimatedTime}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 15,
            color: TEXT_LIGHT,
            fontWeight: "600",
          }}
        >
          {displayCard.detail}
        </Text>
        <View style={{ gap: 2 }}>
          {card.medications.map((medication) => (
            <MedicationRow
              key={`${displayCard.id}-${medication.name}`}
              name={medication.name}
              dose={medication.dosage}
              format={getShipmentMedicationFormat(medication)}
              subtitle=""
              form={medication.form}
              availabilityStatus={confirmed ? "on-hand" : undefined}
              surface="nested"
              rightAction="chevron"
              onPress={() => openMedicationFactSheet(medication)}
            />
          ))}
        </View>
      </View>

      <View style={{ flexDirection: "row", gap: 8 }}>
        {confirmationLabel ? (
          <CompactActionButton
            label={confirmationLabel}
            leadingIcon={CheckCircle2}
            disabled={confirmed}
            onPress={() => {
              // TODO(API): Persist pharmacy fulfillment confirmation for this cycle.
              setConfirmed(true);
              onCompleteAction?.();
            }}
            variant="secondary"
            flexGrow={1}
            minWidth={0}
          />
        ) : null}
        <CompactActionButton
          label={t("care.overview.pharmacy.contact_pharmacy")}
          leadingIcon={Phone}
          onPress={() => void Linking.openURL(`tel:${card.pharmacyPhone}`)}
          variant="secondary"
          flexGrow={1}
          minWidth={0}
        />
      </View>
    </CardSurface>
  );
}

function MedFormIcon({
  form,
  size = 28,
}: {
  form: PharmacyShipmentMedication["form"];
  size?: number;
}) {
  const source =
    form === "pen"
      ? injectionPenImage
      : form === "vial"
        ? vialSyringeImage
        : pillCapsuleImage;

  return (
    <Image
      source={source}
      resizeMode="contain"
      style={{ width: size, height: size }}
    />
  );
}

function getShipmentMedicationFormat(medication: PharmacyShipmentMedication) {
  if (medication.route === "Supplies") return "Supplies";

  const formLabel =
    medication.form === "pen"
      ? "pen"
      : medication.form === "vial"
        ? "vial"
        : medication.form === "suppository"
          ? "suppository"
          : "pill";

  return `${medication.route} ${formLabel}`;
}

function PharmacyVerticalCard({
  title,
  subtitle,
  badgeLabel,
  actionLabel,
  actionIcon,
  actionTrailingIcon,
  onPress,
}: {
  title: string;
  subtitle: string;
  badgeLabel?: string;
  actionLabel: string;
  actionIcon: LucideIcon;
  actionTrailingIcon?: LucideIcon;
  onPress: () => void;
}) {
  return (
    <CardSurface
      style={{
        padding: 14,
        flexGrow: 1,
        flexBasis: 0,
        minWidth: 160,
        minHeight: 172,
      }}
    >
      <View style={{ flex: 1, gap: 10 }}>
        <Image
          source={pillCapsuleImage}
          resizeMode="contain"
          style={{ width: 34, height: 34 }}
        />
        <View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 7,
              flexWrap: "wrap",
            }}
          >
            <CardTitleText>{title}</CardTitleText>
            {badgeLabel ? <StatusBadge label={badgeLabel} /> : null}
          </View>
          <CardSubtitleText>{subtitle}</CardSubtitleText>
        </View>
      </View>

      <View style={{ marginTop: 14 }}>
        <CompactActionButton
          label={actionLabel}
          leadingIcon={actionIcon}
          trailingIcon={actionTrailingIcon}
          variant="secondary"
          action="pharmacy_vertical_action"
          onPress={onPress}
        />
      </View>
    </CardSurface>
  );
}

function PharmacyCoreCards({
  onAddInventory,
  onOpenMedicationGuide,
}: {
  onAddInventory: () => void;
  onOpenMedicationGuide: () => void;
}) {
  const t = useTranslations();
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
      }}
    >
      <PharmacyVerticalCard
        title={t("care.overview.pharmacy.inventory_title")}
        subtitle={t("care.overview.pharmacy.inventory_subtitle")}
        actionLabel={t("care.overview.pharmacy.add_stock")}
        actionIcon={Plus}
        onPress={onAddInventory}
      />
      <PharmacyVerticalCard
        title={t("care.overview.pharmacy.medication_guide_title")}
        subtitle={t("care.overview.pharmacy.medication_guide_subtitle")}
        actionLabel={t("care.overview.pharmacy.open_guide")}
        actionIcon={Pill}
        actionTrailingIcon={ChevronRight}
        onPress={onOpenMedicationGuide}
      />
    </View>
  );
}

function PharmacyInventoryCard({
  medications,
  onOpenMedicationFactSheet,
  onAddInventory,
  onEditInventory,
}: {
  medications: PharmacyInventoryMedication[];
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onAddInventory: () => void;
  onEditInventory: () => void;
}) {
  const t = useTranslations();
  const hasInventory = medications.length > 0;
  const openMedicationFactSheet = (medication: PharmacyInventoryMedication) => {
    onOpenMedicationFactSheet({
      item: getMedicationByBrandName(medication.name) ?? null,
      fallbackName: medication.name,
      userContext: {
        originalName: medication.name,
        dosage: medication.dosage,
      },
    });
  };

  return (
    <CardSurface style={{ padding: 14, gap: 12 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
        <Image
          source={pillCapsuleImage}
          resizeMode="contain"
          style={{ width: 32, height: 32, marginTop: -2 }}
        />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <CardTitleText>
              {t("care.overview.pharmacy.inventory_title")}
            </CardTitleText>
            {medications.length > 0 ? (
              <StatusBadge
                label={t("care.overview.pharmacy.inventory_on_hand_badge", {
                  params: { count: String(medications.length) },
                })}
              />
            ) : null}
          </View>
          <CardSubtitleText>
            {t("care.overview.pharmacy.inventory_subtitle")}
          </CardSubtitleText>
        </View>
      </View>

      <View style={{ gap: 2 }}>
        {medications.map((medication) => (
          <MedicationRow
            key={medication.id}
            name={medication.name}
            dose={medication.dosage}
            format={getShipmentMedicationFormat(medication)}
            subtitle=""
            form={medication.form}
            leftLabel={formatInventoryQuantity(medication)}
            surface="nested"
            rightAction="chevron"
            onPress={() => openMedicationFactSheet(medication)}
          />
        ))}
      </View>
      <View style={{ flexDirection: "row", gap: 8 }}>
        <CompactActionButton
          label={t("care.overview.pharmacy.add_stock")}
          leadingIcon={Plus}
          onPress={onAddInventory}
          variant="secondary"
          flexGrow={1}
          minWidth={0}
        />
        {hasInventory ? (
          <CompactActionButton
            label={t("care.overview.pharmacy.edit_inventory")}
            leadingIcon={Pill}
            onPress={onEditInventory}
            variant="secondary"
            flexGrow={1}
            minWidth={0}
          />
        ) : null}
      </View>
    </CardSurface>
  );
}

function formatInventoryQuantity(medication: PharmacyInventoryMedication) {
  if (medication.quantity === 1) {
    return `1 ${medication.unitLabel}`;
  }
  if (medication.unitLabel === "box") {
    return `${medication.quantity} boxes`;
  }
  if (medication.unitLabel.endsWith("s")) {
    return `${medication.quantity} ${medication.unitLabel}`;
  }
  return `${medication.quantity} ${medication.unitLabel}s`;
}

function PharmacyInventoryEditorDialog({
  visible,
  medications,
  onClose,
  onSave,
}: {
  visible: boolean;
  medications: PharmacyInventoryMedication[];
  onClose: () => void;
  onSave: (next: PharmacyInventoryMedication[]) => void | Promise<void>;
}) {
  const t = useTranslations();
  const [draftMedications, setDraftMedications] = useState(medications);

  useEffect(() => {
    if (!visible) return;
    setDraftMedications(medications);
  }, [medications, visible]);

  const updateQuantity = (id: string, delta: number) => {
    setDraftMedications((current) =>
      current.map((medication) =>
        medication.id === id
          ? {
              ...medication,
              quantity: Math.max(0, medication.quantity + delta),
            }
          : medication,
      ),
    );
  };

  const handleSave = async () => {
    await onSave(
      draftMedications.filter((medication) => medication.quantity > 0),
    );
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
    >
      <View style={{ maxHeight: Dimensions.get("window").height * 0.72 }}>
        <DialogHeader
          title={t("care.overview.pharmacy.inventory_dialog_title")}
          subtitle={t("care.overview.pharmacy.inventory_dialog_subtitle")}
          style={{ marginBottom: 16 }}
        />

        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ maxHeight: Dimensions.get("window").height * 0.42 }}
          contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
        >
          {draftMedications.map((medication) => (
            <CardSurface
              key={medication.id}
              variant="panel"
              style={{
                paddingHorizontal: 11,
                paddingVertical: 10,
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <MedFormIcon form={medication.form} size={28} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={{
                    fontSize: 13.5,
                    lineHeight: 17,
                    color: TEXT,
                    fontWeight: "900",
                  }}
                >
                  {medication.name}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11.5,
                    lineHeight: 15,
                    color: TEXT_LIGHT,
                    marginTop: 1,
                    fontWeight: "700",
                  }}
                >
                  {medication.dosage} ·{" "}
                  {getShipmentMedicationFormat(medication)}
                </Text>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <IconButton
                  accessibilityLabel={t(
                    "care.overview.pharmacy.decrease_a11y",
                    {
                      params: { name: medication.name },
                    },
                  )}
                  action="decrease_medication_quantity"
                  onPress={() => updateQuantity(medication.id, -1)}
                  size="sm"
                  surface="soft"
                  pressedOpacity={0.68}
                >
                  <Minus size={14} color={TEXT} strokeWidth={2.3} />
                </IconButton>
                <Text
                  className="font-dm-sans"
                  style={{
                    minWidth: 54,
                    textAlign: "center",
                    fontSize: 12,
                    lineHeight: 15,
                    color: TEXT,
                    fontWeight: "900",
                  }}
                >
                  {formatInventoryQuantity(medication)}
                </Text>
                <IconButton
                  accessibilityLabel={t(
                    "care.overview.pharmacy.increase_a11y",
                    {
                      params: { name: medication.name },
                    },
                  )}
                  action="increase_medication_quantity"
                  onPress={() => updateQuantity(medication.id, 1)}
                  size="sm"
                  surface="soft"
                  pressedOpacity={0.68}
                >
                  <Plus size={14} color={TEXT} strokeWidth={2.3} />
                </IconButton>
              </View>
            </CardSurface>
          ))}
        </ScrollView>

        <View style={{ flexDirection: "row", gap: 10, marginTop: 18 }}>
          <SecondaryButton
            label={t("care.overview.pharmacy.inventory_dialog_cancel")}
            onPress={onClose}
            flex={1}
          />
          <PrimaryButton
            label={t("care.overview.pharmacy.inventory_dialog_save")}
            onPress={() => void handleSave()}
            flex={1}
          />
        </View>
      </View>
    </Dialog>
  );
}

export function PharmacyFulfillmentCard({
  card,
  defaultExpanded = false,
  railVariant = "timeline",
  onOpenMedicationFactSheet,
}: {
  card: PharmacyFulfillmentCardData;
  defaultExpanded?: boolean;
  railVariant?: "timeline" | "segmented";
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
}) {
  const capturePress = usePressCapture("PharmacyFulfillmentCard");
  const t = useTranslations();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [confirmed, setConfirmed] = useState(card.tone === "done");
  const displayCard: PharmacyFulfillmentCardData = confirmed
    ? {
        ...card,
        statusLabel: t("care.overview.pharmacy.confirmed_status_label"),
        timing:
          card.tone === "done"
            ? card.timing
            : t("care.overview.pharmacy.confirmed_timing_delivery"),
        estimatedTime:
          card.tone === "done"
            ? card.estimatedTime
            : t("care.overview.pharmacy.confirmed_estimated_delivery"),
        tone: "done",
        currentStep: Math.max(0, card.steps.length - 1),
        detail:
          card.tone === "done"
            ? card.detail
            : t("care.overview.pharmacy.confirmed_detail"),
        trackingNumber: card.tone === "done" ? card.trackingNumber : undefined,
      }
    : card;
  const confirmationLabel = confirmed
    ? t("care.overview.pharmacy.confirmation_done")
    : (card.confirmationLabel ??
      t("care.overview.pharmacy.confirmation_default"));
  const openMedicationFactSheet = (medication: PharmacyShipmentMedication) => {
    onOpenMedicationFactSheet({
      item: getMedicationByBrandName(medication.name) ?? null,
      fallbackName: medication.name,
      userContext: {
        originalName: medication.name,
        dosage: medication.dosage,
      },
    });
  };

  return (
    <CardSurface style={{ padding: 14, gap: 12 }}>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              t(
                expanded
                  ? "care.overview.pharmacy.collapse_a11y"
                  : "care.overview.pharmacy.expand_a11y",
                { params: { title: displayCard.title } },
              ),
            ),
            "toggle_pharmacy_fulfillment",
          );
          setExpanded((value) => !value);
        }}
        accessibilityRole="button"
        accessibilityLabel={t(
          expanded
            ? "care.overview.pharmacy.collapse_a11y"
            : "care.overview.pharmacy.expand_a11y",
          { params: { title: displayCard.title } },
        )}
        style={({ pressed }) => ({
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 12,
          opacity: pressed ? 0.78 : 1,
        })}
      >
        <PharmacyFulfillmentHeaderIcon tone={displayCard.tone} />
        <View style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <CardTitleText>{displayCard.title}</CardTitleText>
            <StatusBadge label={displayCard.statusLabel} />
          </View>
          <CardSubtitleText>
            {displayCard.pharmacy} · {displayCard.timing}
          </CardSubtitleText>
        </View>
        <CollapsibleChevronButton
          expanded={expanded}
          size={28}
          iconSize={15}
          color={TEXT_LIGHT}
          strokeWidth={2}
          style={{ marginTop: 1 }}
        />
      </Pressable>

      <View style={{ marginTop: 12 }}>
        {railVariant === "segmented" ? (
          <PharmacyFulfillmentSegmentedRail card={displayCard} />
        ) : (
          <PharmacyFulfillmentRail card={displayCard} />
        )}
      </View>

      <Collapsible expanded={expanded}>
        <View style={{ gap: 13, paddingTop: 13 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Truck size={18} color={TEXT_MID} strokeWidth={1.55} />
            <View style={{ flex: 1 }}>
              <SectionTitle level="subsection">
                {displayCard.estimatedTime}
              </SectionTitle>
              {displayCard.trackingNumber ? (
                <MetaText>Tracking: {displayCard.trackingNumber}</MetaText>
              ) : (
                <MetaText>{displayCard.detail}</MetaText>
              )}
            </View>
          </View>

          <View style={{ gap: 2 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 13,
                color: TEXT_LIGHT,
                fontWeight: "900",
                textTransform: "uppercase",
                letterSpacing: 0.45,
                marginBottom: 3,
              }}
            >
              {t("care.overview.pharmacy.in_this_shipment")}
            </Text>
            {displayCard.medications.map((medication) => (
              <MedicationRow
                key={medication.name}
                name={medication.name}
                dose={medication.dosage}
                format={getShipmentMedicationFormat(medication)}
                subtitle=""
                form={medication.form}
                availabilityStatus={
                  confirmed
                    ? "on-hand"
                    : displayCard.tone === "delivery"
                      ? "in-transit"
                      : undefined
                }
                surface="nested"
                rightAction="chevron"
                onPress={() => openMedicationFactSheet(medication)}
              />
            ))}
          </View>

          <View style={{ flexDirection: "row", gap: 8 }}>
            <CompactActionButton
              label={confirmationLabel}
              leadingIcon={CheckCircle2}
              disabled={confirmed}
              onPress={() => {
                // TODO(API): Persist pharmacy fulfillment confirmation for this cycle.
                setConfirmed(true);
                setExpanded(false);
              }}
              variant="secondary"
              flexGrow={1}
              minWidth={0}
            />
            <CompactActionButton
              label={t("care.overview.pharmacy.contact_pharmacy")}
              leadingIcon={Phone}
              onPress={() => void Linking.openURL(`tel:${card.pharmacyPhone}`)}
              variant="secondary"
              flexGrow={1}
              minWidth={0}
            />
          </View>
        </View>
      </Collapsible>
    </CardSurface>
  );
}

function PharmacyFulfillmentShowcase({
  onOpenMedicationFactSheet,
}: {
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
}) {
  return (
    <View style={{ gap: 10 }}>
      {pharmacyFulfillmentCards.map((card, index) =>
        card.kind === "refill-action" || card.kind === "pickup-action" ? (
          <PharmacyActionCard
            key={card.id}
            card={card}
            onOpenMedicationFactSheet={onOpenMedicationFactSheet}
          />
        ) : card.id === "alto-delivery" ? (
          <View key={`${card.id}-comparison`} style={{ gap: 10 }}>
            <PharmacyFulfillmentCard
              card={card}
              defaultExpanded={index === 0}
              onOpenMedicationFactSheet={onOpenMedicationFactSheet}
            />
            <PharmacyFulfillmentCard
              card={card}
              defaultExpanded={false}
              railVariant="segmented"
              onOpenMedicationFactSheet={onOpenMedicationFactSheet}
            />
          </View>
        ) : (
          <PharmacyFulfillmentCard
            key={card.id}
            card={card}
            defaultExpanded={index === 0}
            onOpenMedicationFactSheet={onOpenMedicationFactSheet}
          />
        ),
      )}
    </View>
  );
}

function TimeSensitiveCareCards({
  cards,
  onDismissCard,
  onOpenMedicationFactSheet,
}: {
  cards: PharmacyFulfillmentCardData[];
  onDismissCard: (id: string) => void;
  onOpenMedicationFactSheet: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
}) {
  if (cards.length === 0) return null;

  return (
    <DismissibleSpotlightStack
      items={cards}
      keyExtractor={(card) => `time-sensitive-${card.id}`}
      onDismiss={(card) => onDismissCard(card.id)}
      renderItem={(card, dismiss) => (
        <PharmacyActionCard
          card={card}
          onDismiss={dismiss}
          onCompleteAction={dismiss}
          onOpenMedicationFactSheet={onOpenMedicationFactSheet}
        />
      )}
    />
  );
}

function LabReportRow({
  report,
  onPress,
}: {
  report: LabReportSnapshot;
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("LabReportRow");
  const isComingSoon = report.status === "Coming soon";

  return (
    <Pressable
      onPress={
        report.enabled
          ? () => {
              capturePress(pressLabel(report.title), "open_lab_report");
              onPress?.();
            }
          : undefined
      }
      accessibilityRole={report.enabled ? "button" : undefined}
      style={({ pressed }) => ({
        ...cardSurface(),
        paddingHorizontal: 14,
        paddingVertical: 13,
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        opacity: pressed ? 0.78 : isComingSoon ? 0.92 : 1,
      })}
    >
      <OrganicMiniIcon kind={report.organicIcon} size={38} />
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 14,
              lineHeight: 18,
              color: TEXT,
              fontWeight: "900",
            }}
          >
            {report.title}
          </Text>
          {isComingSoon ? (
            <StatusBadge type="coming-soon" />
          ) : (
            <StatusBadge label={report.status} />
          )}
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11.5,
            lineHeight: 15,
            color: TEXT_MID,
            marginTop: 3,
          }}
        >
          {report.subtitle}
        </Text>
      </View>
      {report.enabled ? (
        <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.8} />
      ) : null}
    </Pressable>
  );
}

function LabReportsCard({
  navigation,
  reports = labReportSnapshots,
}: {
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  reports?: LabReportSnapshot[];
}) {
  const { openDocumentUpload } = useDocumentUploadController();
  const openLabDocuments = (report: LabReportSnapshot) => {
    if (report.id === "upload-lab-results") {
      openDocumentUpload({ defaultCategory: "medical_records" });
      return;
    }

    navigation.navigate("Documents", {
      initialCategory: "medical_records",
    });
  };

  return (
    <View style={{ gap: 9 }}>
      {reports.map((report) => (
        <LabReportRow
          key={report.id}
          report={report}
          onPress={() => openLabDocuments(report)}
        />
      ))}
    </View>
  );
}

const SPECIMEN_DOC_TAG_PREFIX = "specimen:";
const LAB_DOC_PREVIEW_LIMIT = 3;

function sumPhaseMetric(
  phases: readonly Phase[],
  metricType: string,
): number | null {
  let sum = 0;
  let found = false;
  for (const phase of phases) {
    for (const metric of phase.fertilityMetrics ?? []) {
      if (metric.type === metricType && metric.value != null) {
        sum += metric.value;
        found = true;
      }
    }
  }
  return found ? sum : null;
}

const FIXTURE_LAB_FUNNEL: {
  eggsCollected: number;
  matureEggs: number;
  injected: number;
  fertilized: number;
  day3Embryos: number;
  day5Embryos: number;
  transferred: number;
  frozenCount: number;
} = {
  eggsCollected: 12,
  matureEggs: 10,
  injected: 9,
  fertilized: 8,
  day3Embryos: 6,
  day5Embryos: 4,
  transferred: 1,
  frozenCount: 3,
};

const FIXTURE_LAB_DOCS: readonly {
  id: string;
  name: string;
  dateLabel: string;
}[] = [
  {
    id: "fixture-fertilization",
    name: "Rapport de fécondation",
    dateLabel: "12 mars",
  },
  {
    id: "fixture-freeze",
    name: "Confirmation de congélation",
    dateLabel: "15 mars",
  },
  {
    id: "fixture-embryo",
    name: "Développement embryonnaire J5",
    dateLabel: "14 mars",
  },
];

function LaboratoryCard({
  navigation,
  cycle,
  phases,
  apiSpecimens,
  apiOocyteCohorts,
  documents,
  showFixtureData = false,
}: {
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
  cycle: Cycle;
  phases: readonly Phase[];
  apiSpecimens: readonly Specimen[];
  apiOocyteCohorts: readonly OocyteCohort[];
  documents: readonly Document[];
  showFixtureData?: boolean;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { openDocumentUpload } = useDocumentUploadController();

  // Cycle-scoped specimens for the docs filter. Counts come from phase
  // fertilityMetrics (source of truth for what the patient/clinic recorded),
  // not from specimens — specimens only exist once the vault is populated,
  // while metrics are written as soon as retrieval/lab data lands.
  const cycleCohorts = apiOocyteCohorts.filter(
    (cohort) => cohort.originCycleId === cycle.id,
  );
  const cycleSpecimens = apiSpecimens.filter(
    (specimen) => specimen.cycleId === cycle.id,
  );

  const eggsCollected = showFixtureData
    ? FIXTURE_LAB_FUNNEL.eggsCollected
    : sumPhaseMetric(phases, "retrieved_oocytes");
  const matureEggs = showFixtureData
    ? FIXTURE_LAB_FUNNEL.matureEggs
    : sumPhaseMetric(phases, "m2_count");
  const injected = showFixtureData
    ? FIXTURE_LAB_FUNNEL.injected
    : sumPhaseMetric(phases, "injected_oocytes");
  const fertilized = showFixtureData
    ? FIXTURE_LAB_FUNNEL.fertilized
    : sumPhaseMetric(phases, "fertilized_oocytes");
  const day3Embryos = showFixtureData
    ? FIXTURE_LAB_FUNNEL.day3Embryos
    : sumPhaseMetric(phases, "embryos_day_3");
  const day5Embryos = showFixtureData
    ? FIXTURE_LAB_FUNNEL.day5Embryos
    : sumPhaseMetric(phases, "embryos_day_5");
  const transferred = showFixtureData
    ? FIXTURE_LAB_FUNNEL.transferred
    : sumPhaseMetric(phases, "transferred_embryo");
  const frozenCount = showFixtureData
    ? FIXTURE_LAB_FUNNEL.frozenCount
    : sumPhaseMetric(phases, "frozen_embryo");

  const usesEmbryoFunnel =
    showFixtureData ||
    cycle.cycleType === "ivf" ||
    cycle.cycleType === "icsi" ||
    cycle.cycleType === "embryo_freezing";
  const isIcsiFunnel = showFixtureData || cycle.cycleType === "icsi";
  const funnelRows: OutcomeDotFunnelStage[] = [];
  const pushStage = (
    id: string,
    labelKey: string,
    value: number | null,
    detailKey: string,
    baseline: number | null,
  ) => {
    if (value == null) return;
    const share =
      baseline && baseline > 0 ? Math.round((value / baseline) * 100) : null;
    funnelRows.push({
      id,
      label: t(labelKey),
      value,
      detail:
        share != null
          ? t(detailKey, { params: { share: String(share) } })
          : t("care.overview.lab_cryo.funnel_detail_recorded"),
    });
  };
  const baseline = eggsCollected;
  pushStage(
    "retrieved",
    "care.overview.lab_cryo.funnel_retrieved",
    eggsCollected,
    "care.overview.lab_cryo.funnel_detail_share",
    baseline,
  );
  pushStage(
    "mature",
    "care.overview.lab_cryo.funnel_mature",
    matureEggs,
    "care.overview.lab_cryo.funnel_detail_share",
    baseline,
  );
  if (usesEmbryoFunnel) {
    if (isIcsiFunnel) {
      pushStage(
        "injected",
        "care.overview.lab_cryo.funnel_injected",
        injected,
        "care.overview.lab_cryo.funnel_detail_share",
        baseline,
      );
    }
    pushStage(
      "fertilized",
      "care.overview.lab_cryo.funnel_fertilized",
      fertilized,
      "care.overview.lab_cryo.funnel_detail_share",
      baseline,
    );
    pushStage(
      "day3",
      "care.overview.lab_cryo.funnel_day3",
      day3Embryos,
      "care.overview.lab_cryo.funnel_detail_share",
      baseline,
    );
    pushStage(
      "day5",
      "care.overview.lab_cryo.funnel_blastocysts",
      day5Embryos,
      "care.overview.lab_cryo.funnel_detail_share",
      baseline,
    );
    pushStage(
      "transferred",
      "care.overview.lab_cryo.funnel_transferred",
      transferred,
      "care.overview.lab_cryo.funnel_detail_share",
      baseline,
    );
    pushStage(
      "frozen",
      "care.overview.lab_cryo.funnel_frozen_embryos",
      frozenCount,
      "care.overview.lab_cryo.funnel_detail_share",
      baseline,
    );
  } else {
    pushStage(
      "frozen",
      "care.overview.lab_cryo.funnel_frozen_eggs",
      frozenCount,
      "care.overview.lab_cryo.funnel_detail_share",
      baseline,
    );
  }
  const hasFunnelData = funnelRows.length > 0;
  const funnelTotalDots = Math.max(baseline ?? 1, 1);

  const cycleSpecimenIdSet = new Set<string>([
    ...cycleSpecimens.map((specimen) => specimen.id),
    ...cycleCohorts.flatMap((cohort) =>
      cohort.members.map((member) => member.id),
    ),
  ]);
  const embryologyDocs = documents
    .filter((document) =>
      document.tags.some(
        (tag) =>
          tag.startsWith(SPECIMEN_DOC_TAG_PREFIX) &&
          cycleSpecimenIdSet.has(tag.slice(SPECIMEN_DOC_TAG_PREFIX.length)),
      ),
    )
    .sort((a, b) => {
      const dateA = a.documentDate ?? a.createdAt ?? "";
      const dateB = b.documentDate ?? b.createdAt ?? "";
      return dateB.localeCompare(dateA);
    });
  const previewDocs = embryologyDocs.slice(0, LAB_DOC_PREVIEW_LIMIT);

  const openDocument = (document: Document) => {
    navigation.navigate("Documents", { initialDocumentId: document.id });
  };
  const openAddLabReport = () => {
    const specimenTags = Array.from(cycleSpecimenIdSet).map(
      (id) => `${SPECIMEN_DOC_TAG_PREFIX}${id}`,
    );
    openDocumentUpload({
      defaultCategory: "medical_records",
      defaultTags: [`cycle:${cycle.id}`, ...specimenTags],
    });
  };

  return (
    <CardSurface style={{ padding: 15, gap: 12 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <OrganicMiniIcon kind="panel" size={36} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <CardTitleText>
            {t("care.overview.lab_cryo.laboratory_activity_title")}
          </CardTitleText>
          <CardSubtitleText>
            {t("care.overview.lab_cryo.laboratory_activity_subtitle")}
          </CardSubtitleText>
        </View>
      </View>

      {hasFunnelData ? (
        <OutcomeDotFunnelRows
          stages={funnelRows}
          totalDots={funnelTotalDots}
          embedded
        />
      ) : null}

      {showFixtureData ? (
        <View style={{ gap: 8 }}>
          {FIXTURE_LAB_DOCS.map((doc) => (
            <DocumentRow
              key={doc.id}
              title={doc.name}
              dateLabel={doc.dateLabel}
              icon={<OrganicMiniIcon kind="medical-records" size={28} />}
              onPress={() => navigation.navigate("Documents", {})}
            />
          ))}
        </View>
      ) : previewDocs.length > 0 ? (
        <View style={{ gap: 8 }}>
          {previewDocs.map((document) => (
            <DocumentRow
              key={document.id}
              title={document.name}
              dateLabel={formatShortDate(
                document.documentDate ?? document.createdAt ?? null,
                locale,
              )}
              icon={<OrganicMiniIcon kind="medical-records" size={28} />}
              onPress={() => openDocument(document)}
            />
          ))}
        </View>
      ) : null}

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <SectionActionLink
          label={t("care.overview.lab_cryo.add_lab_report")}
          action="add_lab_report"
          icon={Plus}
          showChevron={false}
          onPress={openAddLabReport}
          style={{ paddingLeft: 0 }}
        />
        <SectionActionLink
          label={t("care.overview.lab_cryo.view_my_cycle")}
          action="view_my_cycle"
          onPress={() =>
            navigation.navigate("CycleDetail", { cycleId: cycle.id })
          }
        />
      </View>
    </CardSurface>
  );
}

function PastCyclesCarousel({
  cycles,
  navigation,
}: {
  cycles: PastCycleSnapshot[];
  navigation: NativeStackNavigationProp<CareStackParamList, "CareOverview">;
}) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const updatePatient = useQueuedPatientUpdate();
  const acceptingKeys = useRef(new Set<string>());
  const [, setAcceptanceRevision] = useState(0);
  const [acceptanceFailed, setAcceptanceFailed] = useState(false);
  const fertilityHistory = (
    patient as typeof patient & {
      fertilityHistory?: {
        id?: string;
        hasPriorFertilityTreatments?: boolean | null;
      } | null;
    }
  )?.fertilityHistory;
  const hasExplicitNoPastTreatment =
    cycles.length === 0 &&
    fertilityHistory?.hasPriorFertilityTreatments === false;
  const priorTreatmentAccepting = acceptingKeys.current.has(
    "prior-fertility-treatment",
  );

  const openPastTreatmentFlow = () => {
    // Route to the canonical past-cycle entry dialog (metrodoras #2741)
    // instead of the anamnesis intake flow. The intake stays reserved for
    // onboarding; every other "+ Add past cycle" affordance funnels through
    // `PastCycleEntryDialog` for a consistent quick-add experience.
    navigation.navigate("AllPastCycles", { openAddDialog: true });
  };

  const setPriorTreatmentAnswer = (
    hasPriorFertilityTreatments: boolean | null,
  ) => {
    setAcceptanceFailed(false);
    const payload: UpdatePatientInput & {
      fertilityHistory: {
        id?: string;
        hasPriorFertilityTreatments: boolean | null;
      };
    } = {
      fertilityHistory: {
        id: fertilityHistory?.id,
        hasPriorFertilityTreatments,
      },
    };
    const started = startClinicalWriteAcceptance({
      activeKeys: acceptingKeys.current,
      key: "prior-fertility-treatment",
      accept: () => updatePatient.mutateAsync(payload),
      onRejected: () => setAcceptanceFailed(true),
      onSettled: () => setAcceptanceRevision((revision) => revision + 1),
    });
    if (started) setAcceptanceRevision((revision) => revision + 1);
  };

  return (
    <CardSurface style={{ padding: 15, gap: 12 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <OrganicMiniIcon kind="eggs" size={36} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <CardTitleText>
            {t(
              cycles.length === 1
                ? "care.overview.past_cycles.title_one"
                : "care.overview.past_cycles.title_other",
              { params: { count: String(cycles.length) } },
            )}
          </CardTitleText>
          <CardSubtitleText>
            {t("care.overview.past_cycles.subtitle")}
          </CardSubtitleText>
        </View>
        <CompactActionButton
          onPress={openPastTreatmentFlow}
          label={t("care.overview.past_cycles.add_short")}
          action="add_past_cycle"
          accessibilityLabel={t("care.overview.past_cycles.add_a11y")}
          size="sm"
          variant="secondary"
          leadingIcon={Plus}
        />
      </View>

      {cycles.length > 0 ? (
        <HorizontalSnapCarousel
          items={cycles}
          gap={10}
          fallbackCardWidth={190}
          getCardWidth={(containerWidth) =>
            Math.min(214, Math.max(178, containerWidth * 0.46))
          }
          keyExtractor={(cycle) => cycle.id}
          equalizeItemHeights
          measureKey="care-past-cycles-snapshot"
          renderItem={(cycle, width, _index, equalizedHeight) => (
            <PastCycleCard
              cycle={cycle}
              width={width}
              equalizedHeight={equalizedHeight}
              onPress={() =>
                navigation.navigate("PastCycleDetail", {
                  cycleId: cycle.id,
                })
              }
            />
          )}
        />
      ) : hasExplicitNoPastTreatment ? (
        <PastCyclesNoTreatmentState
          onAddPastTreatment={openPastTreatmentFlow}
          onUndo={() => setPriorTreatmentAnswer(null)}
          answerAccepting={priorTreatmentAccepting}
        />
      ) : (
        <PastCyclesEmptyState
          onAddPastTreatment={openPastTreatmentFlow}
          onNoPastTreatment={() => setPriorTreatmentAnswer(false)}
          answerAccepting={priorTreatmentAccepting}
        />
      )}
      {acceptanceFailed ? (
        <FieldMessage>
          {t("med_history.overview.sync_status.acceptance_failed")}
        </FieldMessage>
      ) : null}
    </CardSurface>
  );
}

function PastCycleCard({
  cycle,
  width,
  equalizedHeight,
  onPress,
}: {
  cycle: PastCycleSnapshot;
  width: number;
  equalizedHeight?: number;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("PastCycleCard");
  const t = useTranslations();
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            t("care.cycle_history.open_cycle_a11y", {
              params: { title: cycle.title },
            }),
          ),
          "open_past_cycle",
        );
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={t("care.cycle_history.open_cycle_a11y", {
        params: { title: cycle.title },
      })}
      style={({ pressed }) => ({
        ...cardSurface(),
        width,
        minHeight: equalizedHeight,
        padding: 12,
        gap: 12,
        justifyContent: "space-between",
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <OrganicMiniIcon kind={cycle.organicIcon} size={38} />
        <ChevronRight size={15} color={TEXT_LIGHT} strokeWidth={1.7} />
      </View>

      {cycle.metrics.length > 0 ? (
        <View style={{ gap: 6 }}>
          {cycle.metrics.map((metric) => (
            <View
              key={metric.label}
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  lineHeight: 13,
                  color: TEXT_LIGHT,
                  fontWeight: "800",
                }}
              >
                {metric.label}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 16,
                  color: TEXT,
                  fontWeight: "900",
                }}
              >
                {metric.value}
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <CardTitleText>{cycle.title}</CardTitleText>
          <CardSubtitleText>{cycle.subtitle}</CardSubtitleText>
        </View>
        {cycle.statusBadge ? (
          <StatusBadge label={cycle.statusBadge} tone={cycle.statusTone} />
        ) : null}
      </View>
    </Pressable>
  );
}

function PastCyclesNoTreatmentState({
  onAddPastTreatment,
  onUndo,
  answerAccepting,
}: {
  onAddPastTreatment: () => void;
  onUndo: () => void;
  answerAccepting: boolean;
}) {
  const t = useTranslations();

  return (
    <View style={{ gap: 12 }}>
      <View>
        <CardTitleText>
          {t("care.cycle_history.empty.none_saved_title")}
        </CardTitleText>
        <CardSubtitleText>
          {t("care.cycle_history.empty.none_saved_subtitle")}
        </CardSubtitleText>
      </View>
      <View style={{ flexDirection: "row", gap: 9 }}>
        <CompactActionButton
          label={t("care.cycle_history.empty.undo_none_button")}
          accessibilityLabel={t("care.cycle_history.empty.undo_none_a11y")}
          action="undo_no_past_treatment"
          variant="secondary"
          flexGrow={1}
          flexBasis={0}
          disabled={answerAccepting}
          onPress={onUndo}
        />
        <CompactActionButton
          label={t("care.cycle_history.empty.add_button")}
          accessibilityLabel={t("care.cycle_history.empty.add_a11y")}
          action="add_past_treatment"
          variant="secondary"
          leadingIcon={Plus}
          flexGrow={1}
          flexBasis={0}
          onPress={onAddPastTreatment}
        />
      </View>
    </View>
  );
}

function PastCyclesEmptyState({
  onAddPastTreatment,
  onNoPastTreatment,
  answerAccepting,
}: {
  onAddPastTreatment: () => void;
  onNoPastTreatment: () => void;
  answerAccepting: boolean;
}) {
  const t = useTranslations();
  const treatmentTypes = [
    t("care.cycle_history.empty.type_iui"),
    t("care.cycle_history.empty.type_ivf"),
    t("care.cycle_history.empty.type_medicated_cycle"),
    t("care.cycle_history.empty.type_retrieval"),
    t("care.cycle_history.empty.type_transfer"),
    t("care.cycle_history.empty.type_egg_or_embryo_freezing"),
  ];

  return (
    <CardSurface variant="prompt" style={{ padding: 14, gap: 12 }}>
      <View>
        <CardTitleText>{t("care.cycle_history.empty.title")}</CardTitleText>
        <CardSubtitleText>
          {t("care.cycle_history.empty.subtitle")}
        </CardSubtitleText>
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
        {treatmentTypes.map((type) => (
          <Tag key={type} label={type} variant="outline" shape="pill" />
        ))}
      </View>

      <View style={{ flexDirection: "row", gap: 9 }}>
        <CompactActionButton
          label={t("care.cycle_history.empty.none_button")}
          accessibilityLabel={t("care.cycle_history.empty.none_a11y")}
          action="no_past_treatment"
          variant="secondary"
          flexGrow={1}
          flexBasis={0}
          disabled={answerAccepting}
          onPress={onNoPastTreatment}
        />
        <CompactActionButton
          label={t("care.cycle_history.empty.add_button")}
          accessibilityLabel={t("care.cycle_history.empty.add_a11y")}
          action="add_past_treatment"
          variant="secondary"
          leadingIcon={Plus}
          flexGrow={1}
          flexBasis={0}
          onPress={onAddPastTreatment}
        />
      </View>
    </CardSurface>
  );
}

function CycleHandoffAbstractMark() {
  return (
    <View
      pointerEvents="none"
      style={{
        width: 84,
        height: 54,
        position: "relative",
      }}
    >
      <LinearGradient
        colors={["#F3D0AD", "#B9785B", "#794633"]}
        start={{ x: 0, y: 0.15 }}
        end={{ x: 1, y: 1 }}
        style={{
          position: "absolute",
          left: 10,
          top: 14,
          width: 46,
          height: 28,
          borderRadius: 999,
          transform: [{ rotate: "-15deg" }],
          shadowColor: "#4F2314",
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.18,
          shadowRadius: 10,
        }}
      />
      <LinearGradient
        colors={["#F7E6D2", "#D99B73", "#895D4E"]}
        start={{ x: 0.08, y: 0 }}
        end={{ x: 0.9, y: 1 }}
        style={{
          position: "absolute",
          right: 12,
          top: 6,
          width: 32,
          height: 32,
          borderRadius: 999,
          shadowColor: "#4F2314",
          shadowOffset: { width: 0, height: 5 },
          shadowOpacity: 0.14,
          shadowRadius: 9,
        }}
      />
      <LinearGradient
        colors={["#C98962", "#794633"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          position: "absolute",
          left: 32,
          bottom: 8,
          width: 18,
          height: 18,
          borderRadius: 999,
          opacity: 0.92,
        }}
      />
      <View
        style={{
          position: "absolute",
          left: 18,
          top: 20,
          width: 8,
          height: 8,
          borderRadius: 999,
          backgroundColor: "rgba(255,255,255,0.58)",
        }}
      />
    </View>
  );
}

function CycleStatusHandoffCard({ onEndCycle }: { onEndCycle: () => void }) {
  const capturePress = usePressCapture("CycleStatusHandoffCard");
  const t = useTranslations();
  return (
    <View
      style={{
        marginTop: CARE_SECTION_TOP_MARGIN + 24,
        borderTopLeftRadius: 132,
        borderTopRightRadius: 132,
        borderBottomLeftRadius: FEATURE_CARD_RADIUS,
        borderBottomRightRadius: FEATURE_CARD_RADIUS,
        overflow: "visible",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.13,
        shadowRadius: 22,
      }}
    >
      <View
        style={{
          position: "absolute",
          top: -27,
          alignSelf: "center",
          width: 92,
          height: 60,
          alignItems: "center",
          justifyContent: "center",
          zIndex: 3,
        }}
      >
        <CycleHandoffAbstractMark />
      </View>

      <View
        style={{
          borderTopLeftRadius: 132,
          borderTopRightRadius: 132,
          borderBottomLeftRadius: FEATURE_CARD_RADIUS,
          borderBottomRightRadius: FEATURE_CARD_RADIUS,
          overflow: "hidden",
          borderWidth: 1,
          borderColor: semanticColors.onDark.fill.strong,
        }}
      >
        <Image
          source={silkBrown}
          resizeMode="cover"
          style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
        />
        <View
          style={{
            paddingHorizontal: 22,
            paddingTop: 48,
            paddingBottom: 18,
            backgroundColor: semanticColors.onDark.fill.subtle,
            alignItems: "center",
            ...(Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(40px)",
                } as Record<string, string>)
              : {}),
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 22,
              lineHeight: 27,
              color: semanticColors.onDark.text.primary,
              fontWeight: "800",
              letterSpacing: -0.2,
              textAlign: "center",
            }}
          >
            {t("care.overview.cycle_handoff.title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12.5,
              lineHeight: 18,
              color: semanticColors.onDark.text.secondary,
              marginTop: 6,
              textAlign: "center",
              maxWidth: 390,
            }}
          >
            {t("care.overview.cycle_handoff.subtitle")}
          </Text>

          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("care.overview.cycle_handoff.end_cycle_a11y")),
                "end_cycle",
              );
              onEndCycle();
            }}
            accessibilityRole="button"
            accessibilityLabel={t("care.overview.cycle_handoff.end_cycle_a11y")}
            style={({ pressed }) => ({
              marginTop: 18,
              alignSelf: "stretch",
              borderRadius: 14,
              backgroundColor: semanticColors.onDark.fill.subtle,
              borderWidth: 1,
              borderColor: semanticColors.onDark.fill.strong,
              paddingVertical: 14,
              paddingHorizontal: 18,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              opacity: pressed ? 0.78 : 1,
            })}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                lineHeight: 18,
                color: semanticColors.onDark.text.primary,
                fontWeight: "800",
              }}
            >
              {t("care.overview.cycle_handoff.end_cycle_button")}
            </Text>
            <ChevronRight
              size={15}
              color={semanticColors.onDark.text.primary}
              strokeWidth={2}
            />
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function ConceptSection({
  children,
  title,
  icon,
  action,
  top = CARE_SECTION_TOP_MARGIN,
}: {
  children: ReactNode;
  title: string;
  icon: LucideIcon;
  action?: { label: string; onPress: () => void };
  top?: number;
}) {
  return (
    <View style={{ marginTop: top }}>
      <SectionTitleRow title={title} icon={icon} action={action} />
      {children}
    </View>
  );
}

// The Care screen re-renders on every calendar mode change (the logging dock
// state lives here). These sections are the expensive ones and only change
// when their own data does, so they skip those re-renders.
const MemoCalendarOverviewCard = memo(CalendarOverviewCard);
const MemoCryoVaultOverviewCard = memo(CryoVaultOverviewCard);
const MemoLaboratoryCard = memo(LaboratoryCard);
const MemoCareMedicalHistoryProgressCard = memo(CareMedicalHistoryProgressCard);
const MemoPastCyclesCarousel = memo(PastCyclesCarousel);
const MemoConditionsAndDiagnosesCarousel = memo(ConditionsAndDiagnosesCarousel);
const NO_PHASES: readonly Phase[] = [];
const NO_CONDITIONS: ConditionDiagnosisSnapshot[] = [];
const NO_SPECIMENS: NonNullable<ReturnType<typeof useSpecimens>["data"]> = [];
const NO_OOCYTE_COHORTS: NonNullable<
  ReturnType<typeof useOocyteCohorts>["data"]
> = [];
const NO_DOCUMENTS: NonNullable<ReturnType<typeof useDocuments>["data"]> = [];

export default function CareReviewConceptScreen({
  variant,
  navigation,
  initialAppointmentId,
  initialFocus,
  initialCalendarState,
  onCalendarStateChange,
  showFixtureData = false,
}: Props) {
  void variant;
  const t = useTranslations();
  const { openDocumentUpload, pickAndOpenDocumentUpload } =
    useDocumentUploadController();
  const { locale } = useLocale();

  /**
   * Held as the query's own `data`, not defaulted with `= []`. A destructuring
   * default builds a fresh array on every render while the query has no data
   * (pending, errored, or disabled), which made `initialInventoryMedications`
   * a new array each render and the effect that syncs it into state loop until
   * React threw "Maximum update depth exceeded".
   */
  const { data: medicationStockBalances } = useMedicationStock({
    enabled: !showFixtureData,
  });
  /**
   * Destructured, not held as objects. `useMutation` returns
   * `{ ...result, mutate, mutateAsync }` — a fresh object on every render — so
   * a `useCallback` that depends on the mutation itself is rebuilt on every
   * render and its memoisation does nothing. `mutateAsync` is the observer's
   * own bound method and the observer is created once, so naming it is both
   * honest and stable.
   */
  const { mutateAsync: createMedicationStockLotAsync } =
    useCreateMedicationStockLot();
  const { mutateAsync: adjustMedicationStockAsync } =
    useAdjustMedicationStock();
  const initialInventoryMedications = useMemo(
    () =>
      showFixtureData
        ? pharmacyInventoryMedications
        : (medicationStockBalances ?? [])
            .filter((balance) => balance.quantity > 0)
            .map(mapMedicationStockBalance),
    [medicationStockBalances, showFixtureData],
  );
  const [addMedicationVisible, setAddMedicationVisible] = useState(false);
  const [addMedicationMode, setAddMedicationMode] =
    useState<AddMedicationModalMode>("schedule");
  const [addMedicationStartDate, setAddMedicationStartDate] = useState<
    string | null
  >(null);
  const [addAppointmentVisible, setAddAppointmentVisible] = useState(false);
  const [addAppointmentPrefillDate, setAddAppointmentPrefillDate] = useState<
    string | null
  >(null);
  const [devAppointmentDetailMode, setDevAppointmentDetailMode] = useState<
    "empty" | "note" | null
  >(() => getDevAppointmentDetailMode());
  useFocusEffect(
    useCallback(() => {
      const nextMode = getDevAppointmentDetailMode();
      setDevAppointmentDetailMode((currentMode) =>
        currentMode === nextMode ? currentMode : nextMode,
      );
    }, []),
  );
  const [reviewAppointmentSheet, setReviewAppointmentSheet] =
    useState<AppointmentDetailItem | null>(() => {
      if (!devAppointmentDetailMode) return null;
      const fixtureRow = calendarRowsByDate["19"]?.find(
        (row) => row.id === "calendar-19-appointment",
      );
      return fixtureRow
        ? buildAppointmentDetailItem({
            row: fixtureRow,
            fallbackDate: "2026-05-19",
          })
        : null;
    });

  useEffect(() => {
    if (!devAppointmentDetailMode || reviewAppointmentSheet) {
      return;
    }

    const fixtureRow = calendarRowsByDate["19"]?.find(
      (row) => row.id === "calendar-19-appointment",
    );
    if (!fixtureRow) return;

    setReviewAppointmentSheet(
      buildAppointmentDetailItem({
        row: fixtureRow,
        fallbackDate: "2026-05-19",
      }),
    );
  }, [devAppointmentDetailMode, reviewAppointmentSheet, showFixtureData]);
  const [editAppointment, setEditAppointment] =
    useState<AppointmentDetailItem | null>(null);
  const [addSpecimenVisible, setAddSpecimenVisible] = useState(false);
  const resolveCycleNavigation = useCycleNavigation();
  const [selectedSpecimenId, setSelectedSpecimenId] = useState<string | null>(
    null,
  );
  const { openStartTreatment } = useStartTreatmentController();
  const [inventoryEditorVisible, setInventoryEditorVisible] = useState(false);
  const [inventoryMedications, setInventoryMedications] = useState<
    PharmacyInventoryMedication[]
  >(initialInventoryMedications);
  const [endCycleVisible, setEndCycleVisible] = useState(false);
  const [dismissedTimeSensitiveCardIds, setDismissedTimeSensitiveCardIds] =
    useState<string[]>([]);
  const [reviewMedicationSheet, setReviewMedicationSheet] =
    useState<HomepageMedicationFactSheetPayload | null>(null);
  const [editMed, setEditMed] = useState<EditMedicationData | null>(null);
  const { data: editLookupMedications = [] } = usePatientMedications();
  const handleEditMedicationById = useCallback(
    (medicationId: string, fallbackName?: string) => {
      const pm = editLookupMedications.find((p) => p.id === medicationId);
      if (!pm) return;
      const timeTaken = Array.isArray(pm.timeTaken)
        ? (pm.timeTaken as string[])
        : [];
      setEditMed({
        id: pm.id,
        name: pm.medication?.name ?? fallbackName ?? "",
        dose: pm.dose,
        doseUnit: pm.doseUnit,
        route: pm.route ?? null,
        form: pm.form ?? null,
        timeTaken,
        startDate: pm.startDate ?? undefined,
        endDate: pm.endDate ?? null,
        notes: pm.notes,
      });
    },
    [editLookupMedications],
  );
  const [reviewProcedureSheet, setReviewProcedureSheet] =
    useState<HomepageProcedureFactSheetPayload | null>(null);
  const [reviewProcedureEducationOnly, setReviewProcedureEducationOnly] =
    useState(false);
  const [reviewTestSheet, setReviewTestSheet] =
    useState<HomepageTestFactSheetPayload | null>(null);
  const [selectedCondition, setSelectedCondition] =
    useState<ConditionDiagnosisSnapshot | null>(null);
  const [symptomOverlay, setSymptomOverlay] =
    useState<CalendarSymptomOverlayState | null>(null);
  const [showCalendarStickyBackdrop, setShowCalendarStickyBackdrop] =
    useState(false);
  const [isNoFrozenSpecimensConfirmed, setIsNoFrozenSpecimensConfirmed] =
    useState(false);
  const careScrollYRef = useRef(0);

  const requestCalendarDocumentAttachment = useCallback(
    (row: TodayRow) =>
      pickAndOpenDocumentUpload({
        defaultCategory: "medical_records",
        defaultTags: [`today-action:${row.appointmentId ?? row.id}`],
      }),
    [pickAndOpenDocumentUpload],
  );
  const uploadCalendarMonitoringReport = useCallback<MonitoringUploadRequest>(
    (dateISO, onMonitoringExtracted, onDocumentSaved) => {
      void pickAndOpenDocumentUpload({
        defaultCategory: "medical_records",
        defaultTags: ["monitoring-report", `monitoring-date:${dateISO}`],
        onMonitoringExtracted,
        onSave: onDocumentSaved,
      });
    },
    [pickAndOpenDocumentUpload],
  );
  const openCryoVault = useCallback(
    () => navigation.navigate("CryoVault"),
    [navigation],
  );
  const openAddSpecimen = useCallback(() => setAddSpecimenVisible(true), []);
  const confirmNoFrozenSpecimens = useCallback(
    () => setIsNoFrozenSpecimensConfirmed(true),
    [],
  );
  const selectCryoSpecimen = useCallback(
    (specimen: CryoSpecimenSnapshot) =>
      setSelectedSpecimenId(specimen.specimenIds[0] ?? null),
    [],
  );

  const handleOpenProcedureFactSheet = useCallback(
    (payload: HomepageProcedureFactSheetPayload) => {
      setReviewProcedureEducationOnly(false);
      setReviewProcedureSheet(payload);
    },
    [],
  );

  const handleOpenAppointmentEducation = useCallback(
    (appointment: AppointmentDetailItem, activityName?: string) => {
      const item = getProcedureByName(activityName ?? appointment.title);
      if (!item) return;

      setReviewAppointmentSheet(null);
      setReviewProcedureEducationOnly(true);
      setReviewProcedureSheet({
        item,
        appointment: {
          id: appointment.id,
          appointmentId: appointment.appointmentId,
          title: appointment.title,
          subtitle: appointment.subtitle,
          time: appointment.time,
          date: appointment.date,
          notes: appointment.notes ?? undefined,
          ownership: appointment.ownership,
          ownerId: appointment.ownerId,
        },
      });
    },
    [],
  );

  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const openMedicationGuide = useCallback(() => {
    rootNavigation?.navigate("Learn", {
      screen: "Medications",
      params: { returnTab: "Care" },
    });
  }, [rootNavigation]);
  const queryClient = useQueryClient();
  const { tenantId, patientId } = useApiClient();
  const activeCycle = useActiveCycleWithPhases();
  // Snapshots the cycle's phases before it closes; see the end-cycle modal below.
  const createEndCycleSnapshot = useCreateTreatmentSnapshot();
  const persona = useUserPersona();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const canonicalExperience = authorizedExperience.projection?.context;
  const { data: allCycles } = useCycles();
  const activeCycleId = activeCycle.cycle?.id;
  const { data: careTeamMembers } = useCareTeam(activeCycleId);
  const { data: cycleClinic } = useCycleClinic(activeCycleId);
  const { data: careConnections = [] } = useCareConnections();
  const { data: testResults } = useTestResults();
  const { data: apiSpecimens } = useSpecimens();
  const { data: apiOocyteCohorts = NO_OOCYTE_COHORTS } = useOocyteCohorts();
  const cycleLabels = useCycleTypeLabels();
  const cycleHeaderState = useCycleHeaderState();
  const currentCycleContext = useMemo(
    () =>
      presentCycleHeaderForCare({
        state: cycleHeaderState,
        t,
        locale,
        cycleLabels,
      }),
    [cycleHeaderState, t, locale, cycleLabels],
  );
  const pastCycleSnapshots = useMemo(
    () =>
      buildPastCycleSnapshots({
        t,
        cycles: allCycles,
        activeCycleId: activeCycle.cycle?.id,
        labels: cycleLabels,
        locale,
      }),
    [t, activeCycle.cycle?.id, allCycles, cycleLabels, locale],
  );
  const cycleTeamDisplayMembers = useMemo(
    () =>
      showFixtureData
        ? cycleTeamMembers
        : buildRealCycleTeamMembers({
            t,
            members: careTeamMembers,
            clinicName:
              cycleClinic?.organization?.displayName ??
              cycleClinic?.legacyProvider ??
              activeCycle.cycle?.provider,
            recordedConnections: activeCycleId
              ? careConnections.filter((connection) =>
                  connection.cycleIds.includes(activeCycleId),
                )
              : [],
          }),
    [
      t,
      activeCycle.cycle?.provider,
      activeCycleId,
      careTeamMembers,
      careConnections,
      cycleClinic,
      showFixtureData,
    ],
  );
  // Count biomarker series and raw measurements separately: the badge and
  // "N markers tested" number match what `AllLabResultsScreen` renders as rows,
  // the "R results" number tells the patient how many measurements are inside.
  // Nameless rows don't resolve to a biomarker the patient can open, so they
  // are excluded from both totals and from the rendered list.
  const namedTestResults = useMemo(
    () => (testResults ?? []).filter((result) => result.name),
    [testResults],
  );
  const biomarkerCount = useMemo(
    () => groupCanonicalTestResults(namedTestResults).length,
    [namedTestResults],
  );
  const measurementCount = namedTestResults.length;
  const { data: recordsDocuments } = useDocuments();
  const recordsCategoryCounts = useMemo(() => {
    const slotIdToCategory = new Map<string, string>();
    for (const slot of DOCUMENT_SLOTS) {
      slotIdToCategory.set(slot.id, slot.category);
    }
    const categoryTagToKey: Record<string, RecordsCategory> = {
      medical_records: "medical",
      coverage_benefits: "coverage",
      legal_consent: "legal",
      admin: "admin",
    };
    const counts: Record<RecordsCategory, number> = {
      medical: 0,
      coverage: 0,
      legal: 0,
      admin: 0,
    };
    for (const doc of recordsDocuments ?? []) {
      let docCategory: string | undefined;
      for (const tag of doc.tags) {
        const slotCategory = slotIdToCategory.get(tag);
        if (slotCategory) {
          docCategory = slotCategory;
          break;
        }
      }
      if (!docCategory) {
        docCategory = doc.tags.find(
          (tag) => categoryTagToKey[tag] !== undefined,
        );
      }
      if (!docCategory) continue;
      const key = categoryTagToKey[docCategory];
      if (key) counts[key] += 1;
    }
    const result: Partial<Record<RecordsCategory, string>> = {};
    for (const key of Object.keys(counts) as RecordsCategory[]) {
      if (counts[key] > 0) result[key] = String(counts[key]);
    }
    return result;
  }, [recordsDocuments]);
  const cryoSpecimenDisplaySnapshots = useMemo(
    () =>
      buildCryoSpecimenSnapshotsFromApiSpecimens(
        t,
        locale === "fr" ? "fr-FR" : "en-US",
        apiSpecimens ?? [],
        apiOocyteCohorts,
      ),
    [apiOocyteCohorts, apiSpecimens, t, locale],
  );
  const cryoSpecimenDetails = useMemo(
    () =>
      mapApiSpecimensToCryoDetails(apiSpecimens ?? [], (apiType, index) =>
        buildCryoSpecimenLabel(t, apiType, index),
      ),
    [apiSpecimens, t],
  );
  const cryoCycleRole = getCryoCycleRole(
    activeCycle.cycle?.cycleType,
    activeCycle.phases,
    activeCycle.cycle?.oocyteSpecimens ?? [],
  );
  const laboratoryTargetCycle = useMemo(() => {
    if (cryoCycleRole === "adds_to_vault" && activeCycle.cycle) {
      return activeCycle.cycle;
    }
    // Fall back to the most recently started past cycle that added to the vault
    // so the lab card still has something to say when nothing is open.
    const pastAddsToVault = (allCycles ?? [])
      .filter(
        (candidate) =>
          candidate.id !== activeCycle.cycle?.id &&
          getCryoCycleRole(candidate.cycleType) === "adds_to_vault",
      )
      .sort((a, b) => (b.startDate ?? "").localeCompare(a.startDate ?? ""));
    return pastAddsToVault[0] ?? null;
  }, [activeCycle.cycle, allCycles, cryoCycleRole]);
  const laboratoryTargetPhasesQuery = useCyclePhases(laboratoryTargetCycle?.id);
  const laboratoryTargetPhases: readonly Phase[] =
    laboratoryTargetCycle?.id === activeCycle.cycle?.id
      ? activeCycle.phases
      : (laboratoryTargetPhasesQuery.data ?? NO_PHASES);
  const showLaboratoryCard = showFixtureData || laboratoryTargetCycle != null;
  const hasStoredCryoSpecimens = cryoSpecimenDisplaySnapshots.length > 0;
  const shouldShowCryoOverview =
    showFixtureData || hasStoredCryoSpecimens || !isNoFrozenSpecimensConfirmed;
  const shouldShowLabAndCryoSection =
    shouldShowCryoOverview || showLaboratoryCard;
  const startTreatmentPresetCycleType = useMemo(
    () =>
      getStartTreatmentPresetCycleType({
        activeCycleType: activeCycle.cycle?.cycleType,
        treatmentGoal: canonicalExperience?.familyBuildingPlan.treatmentGoal,
      }),
    [
      activeCycle.cycle?.cycleType,
      canonicalExperience?.familyBuildingPlan.treatmentGoal,
    ],
  );
  const startTreatmentGoal = useMemo<"preserve" | "pregnancy">(() => {
    if (canonicalExperience?.familyBuildingPlan.primaryIntent === "preserve") {
      return "preserve";
    }
    return startTreatmentPresetCycleType === "egg_freezing" ||
      startTreatmentPresetCycleType === "embryo_freezing" ||
      startTreatmentPresetCycleType === "sperm_freezing"
      ? "preserve"
      : "pregnancy";
  }, [
    canonicalExperience?.familyBuildingPlan.primaryIntent,
    startTreatmentPresetCycleType,
  ]);
  const startTreatmentGender =
    canonicalExperience?.viewer.sexAtBirth === "AMAB"
      ? "male"
      : canonicalExperience?.viewer.sexAtBirth === "AFAB"
        ? "female"
        : null;
  /**
   * The cycle Care's guidance is about, and how it ended.
   *
   * `/active-cycle` serves nothing once a cycle closes, so reading the outcome off
   * the active cycle alone meant the guidance reverted the moment the patient
   * closed it — a confirmed pregnancy was offered the stimulation-and-transfer
   * guide again, and the live-birth estimate funnel came back with it.
   *
   * The recently-closed cycle fills that gap, bounded by the same recency window
   * Home uses so the two surfaces stop describing an ending at the same time
   * rather than one holding on to it indefinitely.
   */
  const careGuidanceCycle = useMemo(() => {
    if (activeCycle.cycle) {
      return {
        cycleType: activeCycle.cycle.cycleType ?? null,
        outcome: activeCycle.cycle.outcome ?? null,
      };
    }

    const latestClosed = latestClosedCycle(allCycles);
    const ending = readCycleEnding(latestClosed);
    if (!ending.outcomeId || !isCycleEndingRecent(ending.endDate)) {
      return { cycleType: null, outcome: null };
    }
    return {
      cycleType: latestClosed?.cycleType ?? null,
      outcome: latestClosed?.outcome ?? null,
    };
  }, [activeCycle.cycle, allCycles]);

  const careGuidanceCycleType =
    careGuidanceCycle.cycleType ??
    (startTreatmentPresetCycleType
      ? (startTreatmentPresetCycleType as CycleType)
      : null);

  useEffect(() => {
    setInventoryMedications(initialInventoryMedications);
  }, [initialInventoryMedications]);

  useEffect(() => {
    if (cryoSpecimenDisplaySnapshots.length > 0) {
      setIsNoFrozenSpecimensConfirmed(false);
    }
  }, [cryoSpecimenDisplaySnapshots.length]);

  const handleLoggingOverlayChange = useCallback(
    (next: CalendarSymptomOverlayState | null) => {
      setSymptomOverlay(next);
    },
    [],
  );

  const handleCalendarLoggingOverlayActiveChange = useCallback(
    (active: boolean) => {
      if (!active) {
        setShowCalendarStickyBackdrop(false);
        return;
      }
      setShowCalendarStickyBackdrop(careScrollYRef.current > 32);
    },
    [],
  );
  useCalendarLoggingTabBarOverride(
    symptomOverlay,
    handleCalendarLoggingOverlayActiveChange,
  );

  const handleCareScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const y = event.nativeEvent.contentOffset.y;
      careScrollYRef.current = y;
      const next = symptomOverlay !== null && y > 32;
      setShowCalendarStickyBackdrop((current) =>
        current === next ? current : next,
      );
    },
    [symptomOverlay],
  );

  const openStartTreatmentFromCare = useCallback(
    (presetCycleType?: StartTreatmentCycleType) => {
      openStartTreatment({
        goal: startTreatmentGoal,
        gender: startTreatmentGender,
        presetCycleType: presetCycleType ?? startTreatmentPresetCycleType,
        source: "CareReviewConceptScreen",
        onCreated: () => navigation.navigate("CycleDetail"),
      });
    },
    [
      navigation,
      openStartTreatment,
      startTreatmentGender,
      startTreatmentGoal,
      startTreatmentPresetCycleType,
    ],
  );

  const handleAction = useCallback(
    (action: PlanAction) => {
      if (action.signal === "open-document-upload") {
        openDocumentUpload({
          defaultCategory: action.params?.initialCategory ?? "medical_records",
          initialFiles: action.documentFiles,
        });
        return;
      }
      if (action.signal === "open-start-treatment-modal") {
        openStartTreatmentFromCare(action.params?.presetCycleType);
        return;
      }
      if (action.signal === "open-ai-chat") {
        const initialPrompt = action.params?.initialPrompt;
        rootNavigation?.navigate("Chat", {
          screen: "Assistant",
          params: initialPrompt
            ? { initialPrompt, autoSendInitialPrompt: true }
            : undefined,
        });
        return;
      }
      if (action.screen === "CycleDetailScreen") {
        navigation.navigate("CycleDetail");
        return;
      }
      if (action.screen === "DocumentsScreen") {
        if (action.params?.openUpload === "true") {
          openDocumentUpload({
            defaultCategory: action.params.initialCategory ?? "medical_records",
          });
          return;
        }

        navigation.navigate("Documents", {});
        return;
      }
      if (action.screen === "AtHomeFertilityTesting") {
        navigation.navigate("AllLabResults");
        return;
      }
      if (action.screen === "MedicalHistory") {
        navigation.navigate("MedicalHistory");
        return;
      }
      if (action.screen === "CareOverview") {
        return;
      }
    },
    [
      navigation,
      openDocumentUpload,
      openStartTreatmentFromCare,
      rootNavigation,
    ],
  );

  const openAddMedicationModal = useCallback((startDate?: string) => {
    setAddMedicationMode("schedule");
    setAddMedicationStartDate(startDate ?? null);
    setAddMedicationVisible(true);
  }, []);

  const openAddAppointmentModal = useCallback((date?: string) => {
    setAddAppointmentPrefillDate(date ?? null);
    setAddAppointmentVisible(true);
  }, []);

  const initialFocusHandled = useRef(false);
  useEffect(() => {
    if (initialFocusHandled.current || !initialFocus) return;
    initialFocusHandled.current = true;
    if (initialFocus === "appointment") {
      openAddAppointmentModal();
    } else if (initialFocus === "medication") {
      openAddMedicationModal();
    }
  }, [initialFocus, openAddAppointmentModal, openAddMedicationModal]);

  const openInventoryMedicationModal = useCallback(() => {
    setAddMedicationMode("inventory");
    setAddMedicationStartDate(null);
    setAddMedicationVisible(true);
  }, []);

  const handleInventoryMedicationAdded = useCallback(
    async (entry: AddMedicationInventoryEntry) => {
      const existing = inventoryMedications.find((medication) => {
        if (medication.catalogItemId && entry.medicationId) {
          return medication.catalogItemId === entry.medicationId;
        }
        return (
          medication.name.toLowerCase() === entry.name.toLowerCase() &&
          medication.dosage.toLowerCase() === entry.dosage.toLowerCase() &&
          medication.unitLabel.toLowerCase() === entry.unitLabel.toLowerCase()
        );
      });

      if (showFixtureData) {
        setInventoryMedications((current) => {
          const existingIndex = current.findIndex(
            (medication) => medication.id === existing?.id,
          );
          if (existingIndex >= 0) {
            return current.map((medication, index) =>
              index === existingIndex
                ? {
                    ...medication,
                    quantity: medication.quantity + entry.quantity,
                  }
                : medication,
            );
          }
          return [
            ...current,
            {
              id: `inventory-${entry.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${Date.now()}`,
              name: entry.name,
              dosage: entry.dosage,
              route: entry.routeLabel,
              form: entry.form,
              quantity: entry.quantity,
              unitLabel: entry.unitLabel,
            },
          ];
        });
        return;
      }

      if (existing?.lotId) {
        await adjustMedicationStockAsync({
          clientRequestId: createIdempotencyKey(),
          lotId: existing.lotId,
          delta: entry.quantity,
          unit: existing.unitLabel,
          unitSystem: existing.unitSystem ?? PATIENT_INVENTORY_UNIT_SYSTEM,
          unitCode: existing.unitCode ?? inventoryUnitCode(existing.unitLabel),
          type: "adjustment",
          reason: "Patient added stock on hand",
        });
        setInventoryMedications((current) =>
          current.map((medication) =>
            medication.id === existing.id
              ? {
                  ...medication,
                  quantity: medication.quantity + entry.quantity,
                }
              : medication,
          ),
        );
        return;
      }

      const saved = await createMedicationStockLotAsync({
        clientRequestId: createIdempotencyKey(),
        rawProductName: entry.name,
        medicationId: entry.medicationId ?? undefined,
        quantity: entry.quantity,
        unit: entry.unitLabel,
        unitSystem: PATIENT_INVENTORY_UNIT_SYSTEM,
        unitCode: inventoryUnitCode(entry.unitLabel),
        containerDescription: entry.dosage || undefined,
      });
      const savedMedication = mapMedicationStockBalance(saved);
      setInventoryMedications((current) => [
        ...current.filter((medication) => medication.id !== savedMedication.id),
        savedMedication,
      ]);
    },
    [
      adjustMedicationStockAsync,
      createMedicationStockLotAsync,
      inventoryMedications,
      showFixtureData,
    ],
  );

  const handleInventoryEditorSave = useCallback(
    async (next: PharmacyInventoryMedication[]) => {
      if (!showFixtureData) {
        const nextById = new Map(
          next.map((medication) => [medication.id, medication]),
        );
        await Promise.all(
          inventoryMedications
            .filter(
              (
                medication,
              ): medication is PharmacyInventoryMedication & {
                lotId: string;
              } => Boolean(medication.lotId),
            )
            .flatMap((medication) => {
              const target = nextById.get(medication.id);
              const delta = (target?.quantity ?? 0) - medication.quantity;
              if (delta === 0) return [];
              return [
                adjustMedicationStockAsync({
                  clientRequestId: createIdempotencyKey(),
                  lotId: medication.lotId,
                  delta,
                  unit: medication.unitLabel,
                  unitSystem:
                    medication.unitSystem ?? PATIENT_INVENTORY_UNIT_SYSTEM,
                  unitCode:
                    medication.unitCode ??
                    inventoryUnitCode(medication.unitLabel),
                  type: "adjustment",
                  reason: "Patient edited stock on hand",
                }),
              ];
            }),
        );
      }
      setInventoryMedications(next);
      setInventoryEditorVisible(false);
    },
    [adjustMedicationStockAsync, inventoryMedications, showFixtureData],
  );

  const timeSensitivePharmacyCards = useMemo(() => {
    if (!showFixtureData) return [];
    return pharmacyFulfillmentCards
      .filter(
        (card) =>
          card.timeSensitive &&
          !dismissedTimeSensitiveCardIds.includes(card.id),
      )
      .slice(0, 1);
  }, [dismissedTimeSensitiveCardIds, showFixtureData]);
  const dismissTimeSensitiveCard = useCallback((id: string) => {
    setDismissedTimeSensitiveCardIds((current) =>
      current.includes(id) ? current : [...current, id],
    );
  }, []);
  const addConditionRecords = useCallback(
    (_condition: ConditionDiagnosisSnapshot) => {
      setSelectedCondition(null);
      openDocumentUpload({ defaultCategory: "medical_records" });
    },
    [openDocumentUpload],
  );

  const [
    ,
    fixtureTestingSection,
    fixtureGoodToKnowSection,
    fixtureLogisticsSection,
  ] = activeEggFreezingSections;
  const testingSection = showFixtureData ? fixtureTestingSection : null;
  const goodToKnowSection = showFixtureData ? fixtureGoodToKnowSection : null;
  const careLogisticsSection =
    showFixtureData && fixtureLogisticsSection
      ? {
          ...fixtureLogisticsSection,
          widgets: fixtureLogisticsSection.widgets.filter(
            (widget) => widget.type !== "lab-carousel",
          ),
        }
      : null;

  return (
    <GlassScreenLayout>
      <ScrollView
        showsVerticalScrollIndicator={false}
        onScroll={handleCareScroll}
        scrollEventThrottle={16}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 120,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <ScreenHeader title={t("care.overview.screen_title")} />

        <CurrentCycleContextCard
          context={currentCycleContext}
          isTryingNaturally={
            canonicalExperience?.lifecycle.journeyStage === "trying-naturally"
          }
          // Pressable handlers receive a press event. Do not pass it through as
          // the optional cycle-type preset: the event becomes a truthy, invalid
          // treatment type and the modal attempts to translate an undefined
          // label key while opening.
          onStartCycle={() => openStartTreatmentFromCare()}
          onCycleDetail={() => navigation.navigate("CycleDetail")}
          onViewPastCycles={() => navigation.navigate("AllPastCycles")}
          onViewDocuments={() => navigation.navigate("Documents", {})}
          onEndCycle={() => setEndCycleVisible(true)}
          onAddMedication={() => openAddMedicationModal()}
          onAddAppointment={() => openAddAppointmentModal()}
          onUploadDocument={() =>
            openDocumentUpload({ defaultCategory: "medical_records" })
          }
        />
        <TimeSensitiveCareCards
          cards={timeSensitivePharmacyCards}
          onDismissCard={dismissTimeSensitiveCard}
          onOpenMedicationFactSheet={setReviewMedicationSheet}
        />

        <View style={{ marginTop: CARE_SECTION_TOP_MARGIN }}>
          <MemoCalendarOverviewCard
            cycleId={activeCycle.cycle?.id ?? null}
            initialAppointmentId={initialAppointmentId}
            initialCalendarState={initialCalendarState}
            onCalendarStateChange={onCalendarStateChange}
            onOpenMedicationFactSheet={setReviewMedicationSheet}
            onOpenAppointment={setReviewAppointmentSheet}
            onOpenProcedureFactSheet={handleOpenProcedureFactSheet}
            onRequestDocumentAttachment={requestCalendarDocumentAttachment}
            onUploadMonitoringReport={uploadCalendarMonitoringReport}
            onLoggingOverlayChange={handleLoggingOverlayChange}
            onAddMedication={openAddMedicationModal}
            onAddAppointment={openAddAppointmentModal}
            showStickyBackdrop={showCalendarStickyBackdrop}
            showFixtureData={showFixtureData}
          />
        </View>

        {testingSection ? (
          <>
            <ConceptSectionView
              section={testingSection}
              onAction={handleAction}
              onOpenMedicationFactSheet={setReviewMedicationSheet}
              onOpenProcedureFactSheet={handleOpenProcedureFactSheet}
              onOpenTestFactSheet={setReviewTestSheet}
              topSpacing={CARE_SECTION_TOP_MARGIN}
            />
            <TestingActionsCard navigation={navigation} />
          </>
        ) : (
          <TestingAndLabsSection
            navigation={navigation}
            biomarkerCount={biomarkerCount}
            measurementCount={measurementCount}
            onUploadResults={() =>
              openDocumentUpload({ defaultCategory: "medical_records" })
            }
          />
        )}

        <ConceptSection
          title={t("care.overview.pharmacy.section_title")}
          icon={PackageCheck}
        >
          <View style={{ gap: 10 }}>
            {!showFixtureData && inventoryMedications.length === 0 ? (
              <PharmacyCoreCards
                onAddInventory={openInventoryMedicationModal}
                onOpenMedicationGuide={openMedicationGuide}
              />
            ) : (
              <>
                {showFixtureData ? (
                  <PharmacyFulfillmentShowcase
                    onOpenMedicationFactSheet={setReviewMedicationSheet}
                  />
                ) : null}
                <PharmacyInventoryCard
                  medications={inventoryMedications}
                  onOpenMedicationFactSheet={setReviewMedicationSheet}
                  onAddInventory={openInventoryMedicationModal}
                  onEditInventory={() => setInventoryEditorVisible(true)}
                />
                <ActionRowCard
                  icon={
                    <Image
                      source={pillCapsuleImage}
                      resizeMode="contain"
                      style={{ width: 34, height: 34 }}
                    />
                  }
                  title={t("care.overview.pharmacy.medication_guide_title")}
                  subtitle={t(
                    "care.overview.pharmacy.medication_guide_subtitle",
                  )}
                  onPress={openMedicationGuide}
                />
              </>
            )}
          </View>
        </ConceptSection>

        {goodToKnowSection ? (
          <ConceptSectionView
            section={goodToKnowSection}
            onAction={handleAction}
            onOpenMedicationFactSheet={setReviewMedicationSheet}
            onOpenProcedureFactSheet={handleOpenProcedureFactSheet}
            onOpenTestFactSheet={setReviewTestSheet}
            topSpacing={CARE_SECTION_TOP_MARGIN}
          />
        ) : (
          <CareGuidanceSection
            cycleType={careGuidanceCycleType}
            outcome={careGuidanceCycle.outcome}
            userAge={persona.age}
            navigation={navigation}
            onAction={handleAction}
          />
        )}

        {careLogisticsSection && careLogisticsSection.widgets.length > 0 ? (
          <ConceptSectionView
            section={careLogisticsSection}
            onAction={handleAction}
            onOpenMedicationFactSheet={setReviewMedicationSheet}
            onOpenProcedureFactSheet={handleOpenProcedureFactSheet}
            onOpenTestFactSheet={setReviewTestSheet}
            topSpacing={CARE_SECTION_TOP_MARGIN}
          />
        ) : null}

        {shouldShowLabAndCryoSection ? (
          <ConceptSection
            title={t("care.overview.lab_cryo.lab_and_cryo_heading")}
            icon={Snowflake}
          >
            <View style={{ gap: 10 }}>
              {shouldShowCryoOverview ? (
                <MemoCryoVaultOverviewCard
                  onPress={openCryoVault}
                  onAddSpecimen={openAddSpecimen}
                  onNoSpecimens={confirmNoFrozenSpecimens}
                  onPressSpecimen={selectCryoSpecimen}
                  specimens={
                    showFixtureData ? undefined : cryoSpecimenDisplaySnapshots
                  }
                />
              ) : null}
              {showLaboratoryCard && laboratoryTargetCycle ? (
                <MemoLaboratoryCard
                  navigation={navigation}
                  cycle={laboratoryTargetCycle}
                  phases={laboratoryTargetPhases}
                  apiSpecimens={apiSpecimens ?? NO_SPECIMENS}
                  apiOocyteCohorts={apiOocyteCohorts}
                  documents={recordsDocuments ?? NO_DOCUMENTS}
                  showFixtureData={showFixtureData}
                />
              ) : null}
            </View>
          </ConceptSection>
        ) : null}

        <ConceptSection
          title={t("care.overview.medical_context.section_title")}
          icon={History}
        >
          <View style={{ gap: 10 }}>
            <MemoCareMedicalHistoryProgressCard navigation={navigation} />
            <MemoPastCyclesCarousel
              cycles={pastCycleSnapshots}
              navigation={navigation}
            />
            <MemoConditionsAndDiagnosesCarousel
              conditions={
                showFixtureData ? conditionDiagnosisSnapshots : NO_CONDITIONS
              }
              navigation={navigation}
              onViewDetails={setSelectedCondition}
            />
          </View>
        </ConceptSection>

        <View style={{ marginTop: CARE_SECTION_TOP_MARGIN }}>
          <RecordsSection
            onOpenCategory={(category) =>
              navigation.navigate("Documents", {
                initialCategory: RECORDS_CATEGORY_TO_DOC_CATEGORY[category],
              })
            }
            onOpenImport={() =>
              openDocumentUpload({ defaultCategory: "medical_records" })
            }
            onViewAll={() => navigation.navigate("Documents", {})}
            categoryCounts={
              showFixtureData ? { medical: "1" } : recordsCategoryCounts
            }
          />
        </View>

        <CycleTeamSection
          navigation={navigation}
          members={cycleTeamDisplayMembers}
          cycleId={activeCycleId}
          clinicName={
            showFixtureData
              ? "NY Fertility Center"
              : (cycleClinic?.organization?.displayName ??
                cycleClinic?.legacyProvider ??
                activeCycle.cycle?.provider)
          }
          clinicContact={showFixtureData ? { phone: "+12125550198" } : {}}
        />

        {activeCycle.cycle?.status === "open" ? (
          <CycleStatusHandoffCard onEndCycle={() => setEndCycleVisible(true)} />
        ) : null}
      </ScrollView>

      <AddMedicationModal
        visible={addMedicationVisible || !!editMed}
        onClose={() => {
          setAddMedicationVisible(false);
          setAddMedicationStartDate(null);
          setEditMed(null);
        }}
        cycleId={activeCycle.cycle?.id ?? null}
        initialStartDate={addMedicationStartDate ?? undefined}
        menstruationStartDate={activeCycle.cycle?.menstruationStartDate}
        phaseId={activeCycle.currentPhase?.id ?? null}
        inventoryMode={addMedicationMode === "inventory"}
        onInventoryMedicationAdded={handleInventoryMedicationAdded}
        editMedication={editMed}
      />
      <PharmacyInventoryEditorDialog
        visible={inventoryEditorVisible}
        medications={inventoryMedications}
        onClose={() => setInventoryEditorVisible(false)}
        onSave={handleInventoryEditorSave}
      />
      <AddAppointmentModal
        visible={addAppointmentVisible || editAppointment != null}
        onClose={() => {
          setAddAppointmentVisible(false);
          setAddAppointmentPrefillDate(null);
          setEditAppointment(null);
          // useCreateAppointment.onSuccess already invalidates these
          // keys, but explicit re-invalidate guarantees the calendar's
          // newly-added useAppointments observer refetches the moment
          // the modal closes (UAT issue #33 follow-up — appointment
          // wasn't showing on the Care page after save).
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.patient(tenantId ?? "", patientId ?? ""),
          });
          queryClient.invalidateQueries({
            queryKey: calendarKeys.patient(tenantId ?? "", patientId ?? ""),
          });
        }}
        prefill={
          addAppointmentPrefillDate
            ? { date: addAppointmentPrefillDate }
            : undefined
        }
        editAppointment={editAppointment}
      />
      <AddSpecimenModal
        visible={addSpecimenVisible}
        onClose={() => setAddSpecimenVisible(false)}
        cycleId={activeCycle.cycle?.id ?? null}
      />
      <CryoSpecimenDetailModal
        specimen={
          cryoSpecimenDetails.find(
            (specimen) => specimen.id === selectedSpecimenId,
          ) ?? null
        }
        allSpecimens={cryoSpecimenDetails}
        visible={selectedSpecimenId != null}
        onClose={() => setSelectedSpecimenId(null)}
        onPlanTreatment={(specimen) => {
          if (
            specimen.type !== "sperm" &&
            specimen.type !== "embryo" &&
            specimen.type !== "oocyte"
          ) {
            return;
          }
          const specimenIds =
            specimen.type === "oocyte" && specimen.oocyteRecordKind === "cohort"
              ? (specimen.cohortMemberSpecimenIds ?? [])
              : [specimen.id];
          if (specimenIds.length === 0) return;
          setSelectedSpecimenId(null);
          openStartTreatment({
            frozenPrefill: {
              type: specimen.type === "sperm" ? "semen" : specimen.type,
              specimenIds,
            },
            source: "CareReviewConceptScreen",
            onCreated: () => navigation.navigate("CycleDetail"),
          });
        }}
        getCycleNavigation={(cycleId) => {
          const navigate = resolveCycleNavigation(cycleId);
          if (!navigate) return undefined;
          return () => {
            setSelectedSpecimenId(null);
            navigate();
          };
        }}
      />
      <EndCycleModal
        visible={endCycleVisible}
        onClose={() => setEndCycleVisible(false)}
        preservationCycleType={activeCycle.cycle?.cycleType}
        // The modal has to send back the outcome fields it does not own, or the
        // PATCH replaces them with nulls — including the beta value, which lives
        // in `notes`.
        existingOutcome={activeCycle.cycle?.outcome ?? null}
        hasPregnancyOpportunity={hasConfirmedPregnancyProcedure(
          activeCycle.phases,
        )}
        onBeforeSave={async () => {
          // `usePastCycleDetail` reads cached phase data for a closed cycle, so a
          // cycle closed without a snapshot loses its phase history. The modal
          // already resolves the cycle and outcome ids itself through
          // `useActiveCycle`, so only the snapshot was missing here. Best effort:
          // it must not stop the patient ending their cycle.
          const id = activeCycle.cycle?.id;
          if (!id) return;
          await createEndCycleSnapshot.mutateAsync(id).catch(() => {});
        }}
        onSaved={() => {
          // Without this the screen kept offering "end cycle" for a cycle it had
          // just closed: the handoff card is gated on
          // `activeCycle.cycle?.status === "open"`, and that never re-read.
          //
          // `useUpdateCycle` invalidates `active-cycle`, which should be enough,
          // so the underlying reason this screen did not pick it up is not
          // established. What is established is that the two mounts which
          // refetch explicitly do update, and this one — the only mount without
          // an `onSaved` — did not. Forcing the refetch makes it deterministic
          // rather than dependent on that.
          void queryClient.refetchQueries({
            queryKey: ["active-cycle"],
            type: "all",
          });
          void queryClient.refetchQueries({
            queryKey: ["cycles"],
            type: "all",
          });
        }}
        onOpenArticle={(articleId: string) => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Learn", {
            screen: "ArticleDetail",
            params: { articleId, returnTab: "Care" },
          });
        }}
      />
      <MedicationFactsheetSheet
        item={reviewMedicationSheet?.item ?? null}
        fallbackName={reviewMedicationSheet?.fallbackName}
        userContext={{
          ...reviewMedicationSheet?.userContext,
          source:
            editLookupMedications.find(
              (p) => p.id === reviewMedicationSheet?.medicationId,
            )?.source ?? null,
        }}
        onClose={() => setReviewMedicationSheet(null)}
        medicationId={reviewMedicationSheet?.medicationId}
        onLogDosePress={
          reviewMedicationSheet?.onLogDose
            ? () => {
                const openDoseLogger = reviewMedicationSheet.onLogDose;
                setReviewMedicationSheet(null);
                openDoseLogger?.();
              }
            : undefined
        }
        onEditPress={
          reviewMedicationSheet?.medicationId
            ? () => {
                const id = reviewMedicationSheet.medicationId;
                const name = reviewMedicationSheet.fallbackName;
                setReviewMedicationSheet(null);
                if (id) handleEditMedicationById(id, name);
              }
            : undefined
        }
      />
      <AppointmentDetailSheet
        visible={reviewAppointmentSheet != null}
        appointment={reviewAppointmentSheet}
        onClose={() => setReviewAppointmentSheet(null)}
        onEdit={(appointment) => {
          if (!appointment.date) return;
          setReviewAppointmentSheet(null);
          setEditAppointment({ ...appointment, date: appointment.date });
        }}
        onOpenEducation={handleOpenAppointmentEducation}
      />
      <ProcedureDetailSheet
        item={reviewProcedureSheet?.item ?? null}
        appointment={
          reviewProcedureSheet &&
          !reviewProcedureEducationOnly &&
          reviewProcedureSheet.appointment.appointmentId
            ? {
                type: "appointment",
                id: reviewProcedureSheet.appointment.id,
                appointmentId: reviewProcedureSheet.appointment.appointmentId,
                title: reviewProcedureSheet.appointment.title,
                subtitle: reviewProcedureSheet.appointment.subtitle,
                time: reviewProcedureSheet.appointment.time,
                date: reviewProcedureSheet.appointment.date,
                notes: reviewProcedureSheet.appointment.notes ?? null,
                ownership: (reviewProcedureSheet.appointment.ownership ??
                  undefined) as "patient" | "provider" | undefined,
                ownerId: reviewProcedureSheet.appointment.ownerId ?? undefined,
                icon: CalendarClock,
                factsheetId: reviewProcedureSheet.item.id,
              }
            : undefined
        }
        onClose={() => {
          setReviewProcedureSheet(null);
          setReviewProcedureEducationOnly(false);
        }}
      />
      <TestDetailSheet
        item={reviewTestSheet?.item ?? null}
        onClose={() => setReviewTestSheet(null)}
        returnTab="Care"
      />
      <ConditionDetailModal
        condition={selectedCondition}
        onClose={() => setSelectedCondition(null)}
        onAddRecords={addConditionRecords}
      />
    </GlassScreenLayout>
  );
}
